import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  Palette as PaletteIcon,
  Redo as RedoIcon,
  RestartAlt as RestartAltIcon,
  Restore as RestoreIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axiosPublic from '../../api/axiosPublic';
import axiosPrivate from '../../api/axiosConfig';
import ImageSelectorDialog from '../webadmin/ImageSelectorDialog';
import { resolveWebsitePageSlug } from '../webadmin/DataInitializer';
import { getRootSubdomain } from '../utils/subdomain';
import { isUserAuthenticated } from '../../utils/authSession';
import { WEB_LAYOUT, isDirectVideoUrl } from './ModernUiKit';
import CareerApplyDialog from './careers/CareerApplyDialog';
import CareerOpeningsSection from './careers/CareerOpeningsSection';
import SocialLinksSection from './sections/premium/SocialLinksSection';
import SubscribeSection from './sections/premium/SubscribeSection';
import StudentResultsGridSection from './sections/premium/StudentResultsGridSection';
import IconGridSection from './sections/premium/IconGridSection';
import SliderSection from './sections/premium/SliderSection';
import OverlayBannerSection from './sections/premium/OverlayBannerSection';
import ProfileCardsSection from './sections/premium/ProfileCardsSection';
import NewsHighlightsSection from './sections/premium/NewsHighlightsSection';
import StatsStripSection from './sections/premium/StatsStripSection';
import SidebarContentSection from './sections/premium/SidebarContentSection';
import ExperienceStorySection from './sections/premium/ExperienceStorySection';
import CurvedGallerySection from './sections/premium/CurvedGallerySection';
import PrincipalMessageSection from './sections/premium/PrincipalMessageSection';
import ProgramTabsSection from './sections/premium/ProgramTabsSection';
import InquiryFormSection from './sections/premium/InquiryFormSection';
import MapVisitSection from './sections/premium/MapVisitSection';
import FooterShowcaseSection from './sections/premium/FooterShowcaseSection';
import SplitMediaStorySection from './sections/premium/SplitMediaStorySection';
import WebsiteSectionsDialog from './WebsiteSectionsDialog';
import RichTextEditor from './customPageRenderer/RichTextEditor';
import SectionEditorDialog from './customPageRenderer/SectionEditorDialog';
import ThemeCustomizationDialog from './customPageRenderer/ThemeCustomizationDialog';
import {
  buildSectionLibrary,
  getSectionTypeOptions,
  searchSectionLibrary,
} from './sections/premium/sectionLibraryHelpers';
import { getStoredPublicSchoolProfile } from './publicSchoolProfile';

const normalizeSlug = (value = '') =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const titleFromSlug = (slug = '') =>
  slug
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const EDIT_HISTORY_LIMIT = 60;

const clonePageSnapshot = (value) => {
  if (value == null) {
    return value;
  }
  try {
    if (typeof structuredClone === 'function') {
      return structuredClone(value);
    }
    return JSON.parse(JSON.stringify(value));
  } catch (_error) {
    return value;
  }
};

const serializePageSnapshot = (value) => {
  try {
    return JSON.stringify(value ?? null);
  } catch (_error) {
    return '';
  }
};

const createEmptyCareerApplyForm = () => ({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  yearsExperience: '',
  portfolioUrl: '',
  message: '',
});

const RESULTS_TEMPLATE_VERSION = 'results-v5';
const CAREERS_TEMPLATE_VERSION = 'careers-v4';
const HOME_TEMPLATE_VERSION = 'home-v9';
const MAP_TEMPLATE_VERSION = 'map-v2';

const DEMO_THEME = {
  primary: '#059669',
  primaryDark: '#047857',
  accent: '#F59E0B',
  accentDeep: '#D97706',
  accentSoft: '#FEF3C7',
  surface: '#FFFFFF',
  surfaceAlt: '#F0FDF4',
  ink: '#111827',
  inkSoft: '#4B5563',
  white: '#FFFFFF',
  border: 'rgba(17, 24, 39, 0.12)',
};

const PAGE_THEME_PRESETS = [
  {
    id: 'kirana-emerald',
    label: 'Kirana Emerald',
    description: 'Fresh emerald green with warm amber accents.',
    primary: '#059669',
    accent: '#F59E0B',
    surface: '#F0FDF4',
  },
  {
    id: 'school-red',
    label: 'School Red',
    description: 'Premium school red with warm gold accents.',
    primary: '#A20D14',
    accent: '#D98A2B',
    surface: '#FFF8F7',
  },
  {
    id: 'classic-navy',
    label: 'Classic Navy',
    description: 'Traditional academic blue and gold balance.',
    primary: '#15316B',
    accent: '#C79A3B',
    surface: '#F7F9FD',
  },
  {
    id: 'forest-green',
    label: 'Forest Green',
    description: 'Calm, nature-led school identity.',
    primary: '#1F5B46',
    accent: '#D5A441',
    surface: '#F7FBF8',
  },
  {
    id: 'charcoal-minimal',
    label: 'Charcoal Minimal',
    description: 'Simple neutral base with subtle editorial contrast.',
    primary: '#2B2B31',
    accent: '#B97932',
    surface: '#FAFAFA',
  },
];

const shouldUpgradeHomePageContent = (content) => {
  if (!content || typeof content !== 'object') {
    return false;
  }
  if (content.templateVersion === HOME_TEMPLATE_VERSION) {
    return false;
  }

  const normalizedTitle = String(content.pageTitle || '').trim().toLowerCase();
  const sections = Array.isArray(content.sections) ? content.sections : [];
  const sectionTypes = sections
    .map((section) => String(section?.type || '').trim().toLowerCase())
    .filter(Boolean);
  const isHomePage = normalizedTitle === 'home';

  const templateVersion = String(content.templateVersion || '').trim().toLowerCase();
  const needsVersionUpgrade = isHomePage && templateVersion !== HOME_TEMPLATE_VERSION;

  const isLegacyHomePresetShape =
    isHomePage &&
    sectionTypes.length <= 8 &&
    ['text', 'icon_grid', 'slider', 'cta', 'stages', 'table', 'quote'].every((expectedType) =>
      sectionTypes.includes(expectedType)
    );

  const isCompactLegacyHome =
    isHomePage &&
    sectionTypes.length <= 5 &&
    ['text', 'icon_grid', 'slider', 'cta'].every((expectedType) => sectionTypes.includes(expectedType));

  return needsVersionUpgrade || isLegacyHomePresetShape || isCompactLegacyHome;
};

const shouldUpgradeResultsPageContent = (content) => {
  if (!content || typeof content !== 'object') {
    return false;
  }
  if (content.templateVersion === RESULTS_TEMPLATE_VERSION) {
    return false;
  }
  const normalizedTitle = String(content.pageTitle || '').trim().toLowerCase();
  const sections = Array.isArray(content.sections) ? content.sections : [];
  const sectionTypes = sections
    .map((section) => String(section?.type || '').trim().toLowerCase())
    .filter(Boolean);
  const isOlderShape =
    normalizedTitle === 'results' &&
    sectionTypes.length <= 3 &&
    ['student_results_grid', 'results', 'cta'].every((expectedType) => sectionTypes.includes(expectedType));
  const missingCommandCenter = !sectionTypes.includes('icon_grid') || !sectionTypes.includes('tabs');
  const templateVersion = String(content.templateVersion || '').trim().toLowerCase();
  const hasResultsTemplateVersion = templateVersion.startsWith('results-v');
  const needsVersionUpgrade = hasResultsTemplateVersion && templateVersion !== RESULTS_TEMPLATE_VERSION;
  return needsVersionUpgrade || isOlderShape || missingCommandCenter;
};

const shouldUpgradeCareersPageContent = (content) => {
  if (!content || typeof content !== 'object') {
    return false;
  }
  if (content.templateVersion === CAREERS_TEMPLATE_VERSION) {
    return false;
  }

  const normalizedTitle = String(content.pageTitle || '').trim().toLowerCase();
  const sections = Array.isArray(content.sections) ? content.sections : [];
  const sectionTypes = sections
    .map((section) => String(section?.type || '').trim().toLowerCase())
    .filter(Boolean);
  const hasCoreCareersSections = sectionTypes.includes('careers_openings');
  const isCareersPage = normalizedTitle === 'careers' || sectionTypes.includes('careers_openings');

  const templateVersion = String(content.templateVersion || '').trim().toLowerCase();
  const hasCareersTemplateVersion = templateVersion.startsWith('careers-v');
  const needsVersionUpgrade = hasCareersTemplateVersion && templateVersion !== CAREERS_TEMPLATE_VERSION;
  const missingCoreSections = isCareersPage && !hasCoreCareersSections;
  const legacyNoVersion = isCareersPage && !templateVersion;
  return needsVersionUpgrade || missingCoreSections || legacyNoVersion;
};

const shouldUpgradeMapPageContent = (content) => {
  if (!content || typeof content !== 'object') {
    return false;
  }
  if (content.templateVersion === MAP_TEMPLATE_VERSION) {
    return false;
  }
  const normalizedTitle = String(content.pageTitle || '').trim().toLowerCase();
  const sections = Array.isArray(content.sections) ? content.sections : [];
  const sectionTypes = sections
    .map((section) => String(section?.type || '').trim().toLowerCase())
    .filter(Boolean);
  const isMapPage = normalizedTitle === 'map' || normalizedTitle === 'campus map' || sectionTypes.includes('map');
  const templateVersion = String(content.templateVersion || '').trim().toLowerCase();
  const hasMapTemplateVersion = templateVersion.startsWith('map-v');
  const needsVersionUpgrade = hasMapTemplateVersion && templateVersion !== MAP_TEMPLATE_VERSION;
  const missingCoreSections = isMapPage && !sectionTypes.includes('map');
  const legacyNoVersion = isMapPage && !templateVersion;
  return needsVersionUpgrade || missingCoreSections || legacyNoVersion;
};

const BASE_SECTION_LIBRARY = [
  {
    id: 'hero',
    label: 'Hero Banner',
    description: 'Top hero section with title, subtitle and media.',
    defaults: {
      title: 'Admissions Open for the New Academic Year',
      body: 'Add a short welcome message, key classes offered, and the main action for parents.',
      image:
        'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1800&q=80',
      videoUrl: '',
      heroHeight: 520,
      heroContentWidth: 100,
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      tabs: [],
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      animationStyle: 'none',
      animationDuration: 700,
      animationDelay: 0,
    },
  },
  {
    id: 'text',
    label: 'Text Block',
    description: 'Title and paragraph section.',
    defaults: {
      title: 'About the School',
      body: 'Write a short introduction about the school, its approach, and its learning environment.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
    },
  },
  {
    id: 'image',
    label: 'Image Spotlight',
    description: 'Text with supporting image.',
    defaults: {
      title: 'Campus Overview',
      body: 'Use one image with a short note about the campus, classrooms, labs, library, or activity spaces.',
      image:
        'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1200&q=80',
      buttonLabel: '',
      buttonPath: '',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
    },
  },
  {
    id: 'list',
    label: 'List / Steps',
    description: 'Use numbered points or a checklist.',
    defaults: {
      title: 'Admissions Steps',
      body: '1. Submit an enquiry\n2. Visit the campus\n3. Complete the application and interaction',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
    },
  },
  {
    id: 'stages',
    label: 'Learning Stages',
    description: 'Show grade stages and progression.',
    defaults: {
      title: 'Learning Stages',
      body:
        '1. Pre-Primary: early learning through activity and play\n2. Primary: strong foundation in language, maths, and EVS\n3. Middle School: subject depth, projects, and labs\n4. Senior School: board preparation, electives, and guidance',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 280,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
    },
  },
  {
    id: 'results',
    label: 'Results Table',
    description: 'Display outcomes, records, or summary highlights.',
    defaults: {
      title: 'Board Results',
      body: 'Share recent board exam outcomes and academic highlights in a clear format.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 280,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      tableColumns: ['Year', 'Pass %', 'Top Score', 'Distinctions'],
      tableRows: [
        ['2025', '99%', '98.4%', '76'],
        ['2024', '98%', '97.8%', '68'],
        ['2023', '97%', '97.2%', '61'],
      ],
    },
  },
  {
    id: 'student_results_grid',
    label: 'Student Results Grid',
    description: 'Grid cards with student image, percentage, and marks.',
    defaults: {
      title: 'Student Achievements',
      body: 'Highlight student results, toppers, and subject achievements.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 220,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      resultsGridColumns: 3,
      resultsMetricMode: 'both',
      resultCards: [
        {
          name: 'Aarav Sharma',
          className: 'Grade 10',
          percentage: '98.2%',
          marks: '491/500',
          image:
            'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=500&q=80',
          highlight: 'School Topper',
        },
        {
          name: 'Ananya Reddy',
          className: 'Grade 10',
          percentage: '97.6%',
          marks: '488/500',
          image:
            'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=500&q=80',
          highlight: 'Science Distinction',
        },
        {
          name: 'Vihaan Patel',
          className: 'Grade 9',
          percentage: '96.9%',
          marks: '484/500',
          image:
            'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=500&q=80',
          highlight: 'Mathematics Excellence',
        },
      ],
    },
  },
  {
    id: 'faculty',
    label: 'Faculty',
    description: 'Faculty profiles with photo, role, and bio.',
    defaults: {
      title: 'Our Faculty',
      body: 'Introduce teachers with simple profiles, subjects, and teaching strengths.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 220,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      facultyColumns: 3,
      facultyCards: [
        {
          name: 'Ms. Priya Sharma',
          role: 'Mathematics Faculty',
          image:
            'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=500&q=80',
          bio: 'Concept-first learning with regular practice and doubt-clearing.',
        },
        {
          name: 'Mr. Rahul Verma',
          role: 'Science Faculty',
          image:
            'https://images.unsplash.com/photo-1541534401786-2077eed87a72?auto=format&fit=crop&w=500&q=80',
          bio: 'Hands-on experiments and application-driven classroom teaching.',
        },
        {
          name: 'Ms. Kavya Iyer',
          role: 'English Faculty',
          image:
            'https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=500&q=80',
          bio: 'Communication-rich lessons focused on reading, writing, and expression.',
        },
      ],
    },
  },
  {
    id: 'founders',
    label: 'Founders',
    description: 'Founder and leadership profiles for school story.',
    defaults: {
      title: 'Founders and Leadership',
      body: 'Use this section for a short note on the school vision and leadership team.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 220,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      founderColumns: 2,
      founderCards: [
        {
          name: 'Dr. Arvind Rao',
          role: 'Founder',
          image:
            'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=500&q=80',
          bio: 'Committed to accessible, future-ready education rooted in strong values.',
        },
        {
          name: 'Ms. Nisha Rao',
          role: 'Co-Founder',
          image:
            'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=500&q=80',
          bio: 'Focused on student wellbeing, teacher excellence, and community partnership.',
        },
      ],
    },
  },
  {
    id: 'profile_showcase',
    label: 'Profile Showcase',
    description: 'Use for profiles, testimonials, messages, or featured people.',
    defaults: {
      title: 'Alumni Voices',
      body: 'Share brief alumni messages, higher education paths, or career journeys.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 220,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      profileColumns: 4,
      profileCardRadius: 22,
      profileCardBgColor: '#FFFFFF',
      profileCardBorderColor: 'rgba(15,23,42,0.12)',
      profileCards: [
        {
          name: 'Kao Kamizono',
          role: 'Class of 2020',
          image:
            'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=900&q=80',
          bio: 'The school’s international environment, mentoring, and global perspective shaped a strong path into higher education and consulting.',
        },
        {
          name: 'Anshul Natekar',
          role: 'Class of 2023',
          image:
            'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=900&q=80',
          bio: 'Counselling support, clarity on goals, and consistent adult guidance made decision-making feel supported instead of overwhelming.',
        },
        {
          name: 'Arzoo Satar Sait',
          role: 'Class of 2022',
          image:
            'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=900&q=80',
          bio: 'Leadership, creativity, and community stayed connected long after graduation, which is exactly what strong alumni storytelling should communicate.',
        },
      ],
    },
  },
  {
    id: 'stats_strip',
    label: 'Stats',
    description: 'Show key numbers, metrics, or highlights.',
    defaults: {
      title: 'Why Families Choose Us',
      body: 'Show a few simple school highlights that parents usually look for first.',
      image:
        'https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=2200&q=80',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 220,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      statsColumns: 3,
      statsOverlayColor: 'rgba(15, 23, 42, 0.58)',
      statsItems: [
        { icon: '📚', value: '30+', label: 'Activities and Clubs' },
        { icon: '🧑‍🏫', value: '1:18', label: 'Teacher Student Ratio' },
        { icon: '🏆', value: '100%', label: 'Board Exam Pass Rate' },
      ],
    },
  },
  {
    id: 'sidebar_content',
    label: 'Side Navigation',
    description: 'Structured content page with side navigation.',
    defaults: {
      title: 'Admissions Guide',
      body: '<p>Use this section for admission details, eligibility, documents, fee notes, or parent guidance.</p>',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 220,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      sidebarTitle: 'Admissions',
      sidebarItems: [
        { label: 'Admissions Overview', path: '/admissions' },
        { label: 'Admissions Process', path: '/admissions' },
        { label: 'Documents Required', path: '/admissions' },
        { label: 'Contact', path: '/contact' },
      ],
      sidebarCtaTitle: 'Plan Your Next Step',
      sidebarCtaSubtitle: 'Invite families to enquire, visit, or speak with the admissions team.',
      sidebarButtons: [
        { label: 'Enquiry Form', path: '/admissions' },
        { label: 'Contact Us', path: '/contact' },
      ],
    },
  },
  {
    id: 'careers_openings',
    label: 'Openings List',
    description: 'Position or listing cards in vertical or grid layout.',
    defaults: {
      title: 'Careers',
      body: 'List current vacancies and basic role details for teaching and school operations.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 220,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      careersLayout: 'grid',
      careersColumns: 3,
      careerCards: [
        {
          title: 'Primary Teacher',
          department: 'Academics',
          location: 'Main Campus',
          employmentType: 'Full Time',
          applyPath: '/contact',
          image:
            'https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1200&q=80',
          summary: 'Lead foundational classrooms with activity-driven and student-centered teaching.',
        },
        {
          title: 'Mathematics Teacher',
          department: 'Academics',
          location: 'Main Campus',
          employmentType: 'Full Time',
          applyPath: '/contact',
          image:
            'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1200&q=80',
          summary: 'Deliver concept clarity, assessment planning, and exam readiness.',
        },
        {
          title: 'School Counselor',
          department: 'Student Support',
          location: 'Main Campus',
          employmentType: 'Full Time',
          applyPath: '/contact',
          image:
            'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1200&q=80',
          summary: 'Support student wellbeing, social-emotional development, and parent coordination.',
        },
      ],
    },
  },
  {
    id: 'social_links',
    label: 'Social Links',
    description: 'Social media buttons with label and link.',
    defaults: {
      title: 'Connect With Us',
      body: 'Add official school social links for announcements, events, and updates.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 220,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      socialLinks: [
        { label: 'Facebook', url: 'https://facebook.com', icon: 'f' },
        { label: 'Instagram', url: 'https://instagram.com', icon: 'ig' },
        { label: 'YouTube', url: 'https://youtube.com', icon: 'yt' },
        { label: 'LinkedIn', url: 'https://linkedin.com', icon: 'in' },
      ],
    },
  },
  {
    id: 'overlay_banner',
    label: 'Overlay Banner',
    description: 'Full-width media banner with text layered on top.',
    defaults: {
      title: 'Learning spaces designed for confidence and curiosity',
      body: 'Use this full-width section for campus story moments, admissions callouts, signature programmes, or important school messages.',
      image:
        'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1800&q=80',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      mediaHeight: 320,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      animationStyle: 'none',
      animationDuration: 700,
      animationDelay: 0,
    },
  },
  {
    id: 'news_highlights',
    label: 'News & Events',
    description: 'Cards for school news, circulars, notices, and event highlights.',
    defaults: {
      title: 'News and Updates',
      body: 'Share recent school news, events, admissions notices, and student achievements.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 220,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      profileColumns: 3,
      profileCardRadius: 24,
      profileCardBgColor: '#FFFFFF',
      profileCardBorderColor: 'rgba(15,23,42,0.1)',
      profileCards: [
        {
          name: 'Annual Day Celebrated',
          role: 'School Event',
          image:
            'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80',
          bio: 'Students presented music, dance, and stage performances during the annual day programme.',
        },
        {
          name: 'Admissions Open',
          role: 'Admissions',
          image:
            'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=900&q=80',
          bio: 'Admissions are open for the new academic year. Families can contact the school office for details.',
        },
        {
          name: 'Inter-School Sports Meet',
          role: 'Achievement',
          image:
            'https://images.unsplash.com/photo-1517649763962-0c623066013b?auto=format&fit=crop&w=900&q=80',
          bio: 'Students took part in inter-school competitions and received prizes across multiple events.',
        },
      ],
    },
  },
  {
    id: 'subscribe',
    label: 'Subscribe Form',
    description: 'Email subscribe form for updates.',
    defaults: {
      title: 'Get Updates',
      body: 'Use this section for admission alerts, circulars, newsletters, and school reminders.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 220,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      subscribePlaceholder: 'Enter your email',
      subscribeButtonLabel: 'Subscribe',
      subscribeSuccessMessage: 'Thanks for subscribing. We will keep you updated.',
    },
  },
  {
    id: 'quote',
    label: 'Message',
    description: 'Highlight a featured note, message, or short statement.',
    defaults: {
      title: 'Principal’s Message',
      body: '"We aim to provide a safe, disciplined, and encouraging environment where every child can grow."',
      image:
        'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=1200&q=80',
      buttonLabel: 'Dr. Meera Sharma',
      buttonPath: 'Principal',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
    },
  },
  {
    id: 'tabs',
    label: 'HTML Tabs',
    description: 'Tabbed content section with text or basic HTML in each tab.',
    defaults: {
      title: 'Key Information',
      body: '<p>Use this section to explain information stage by stage in a clean, parent-friendly format.</p>',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      videoUrl: '',
      tabs: [
        {
          title: 'Overview',
          content:
            '<p>Use this tab for a short overview of the stage, programme, or topic.</p><ul><li>Keep the language simple and parent-friendly</li><li>Add 3 to 4 key focus areas</li><li>Use short paragraphs instead of long blocks</li></ul>',
        },
        {
          title: 'Details',
          content:
            '<p>Use this tab for classroom approach, activities, and support systems.</p><ul><li>Explain how teaching works</li><li>Highlight practical learning and student support</li><li>Show what makes the stage different</li></ul>',
        },
        {
          title: 'Outcomes',
          content:
            '<p>Use this tab for expected learning growth, confidence, and preparation for the next stage.</p><ul><li>Academic growth</li><li>Skills and habits</li><li>Readiness for the next level</li></ul>',
        },
      ],
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
    },
  },
  {
    id: 'experience_story',
    label: 'Split Content',
    description: 'Single image on one side with content on the other.',
    defaults: {
      title: 'Section Overview',
      body:
        '<p>Use this section for a simple image and content block anywhere on the website.</p>',
      image:
        'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1200&q=80',
      buttonLabel: '',
      buttonPath: '',
      sectionWidth: 100,
      imagePosition: 'left',
      imageObjectPosition: 'center center',
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      videoUrl: '',
      tabs: [],
      iconGridColumns: 1,
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
    },
  },
  {
    id: 'video',
    label: 'Video Section',
    description: 'Section with embedded video and editable text.',
    defaults: {
      title: 'School Video',
      body: 'Use this section for a campus tour, principal message, or school event video.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      tabs: [],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
    },
  },
  {
    id: 'divider',
    label: 'Divider',
    description: 'Design divider line with optional label and custom colors.',
    defaults: {
      title: 'Divider',
      body: '',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: 'School Update',
    },
  },
  {
    id: 'button',
    label: 'Simple Button',
    description: 'Standalone action button block.',
    defaults: {
      title: '',
      body: '',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
    },
  },
  {
    id: 'image_only',
    label: 'Image',
    description: 'Simple image component.',
    defaults: {
      title: '',
      body: '',
      image:
        'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1200&q=80',
      buttonLabel: '',
      buttonPath: '',
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 8,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
    },
  },
  {
    id: 'image_grid',
    label: 'Image Grid',
    description: 'Responsive image gallery grid.',
    defaults: {
      title: 'Campus Gallery',
      body: 'Add photos from classrooms, labs, events, sports, and campus spaces.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 220,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      galleryColumns: 3,
      galleryImages: [
        'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1200&q=80',
        'https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=1200&q=80',
        'https://images.unsplash.com/photo-1588072432836-e10032774350?auto=format&fit=crop&w=1200&q=80',
        'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1200&q=80',
        'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?auto=format&fit=crop&w=1200&q=80',
        'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=1200&q=80',
      ],
    },
  },
  {
    id: 'slider',
    label: 'Image Slider',
    description: 'Image carousel with autoplay controls.',
    defaults: {
      title: 'Campus Highlights',
      body: 'Use the slider for classrooms, events, achievements, and school activities.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 300,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      galleryImages: [
        'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1400&q=80',
        'https://images.unsplash.com/photo-1588072432836-e10032774350?auto=format&fit=crop&w=1400&q=80',
        'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1400&q=80',
      ],
      sliderAutoPlay: true,
      sliderInterval: 3500,
    },
  },
  {
    id: 'icon_grid',
    label: 'Feature Grid',
    description: 'Highlight features, strengths, services, or key points.',
    defaults: {
      title: 'Why Families Choose Us',
      body: 'Use short points to explain the main strengths of the school.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 220,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      iconGridColumns: 3,
      iconCardVariant: 'card',
      iconCardRadius: 16,
      iconCards: [
        {
          icon: 'ACA',
          title: 'Strong Academics',
          content: 'Clear teaching, regular assessment, and subject support across grades.',
        },
        {
          icon: 'CCA',
          title: 'Co-Curricular Activities',
          content: 'Sports, arts, clubs, and events support all-round student development.',
        },
        {
          icon: 'CARE',
          title: 'Student Care',
          content: 'Teachers and staff provide guidance, discipline, and a supportive environment.',
        },
      ],
    },
  },
  {
    id: 'image_read_more',
    label: 'Image + Read More',
    description: 'Image with expandable content block.',
    defaults: {
      title: 'Learning Environment',
      body:
        'The school provides structured classroom teaching, regular practice, and guidance for steady progress. Students also take part in activities, events, and opportunities beyond the classroom.',
      image:
        'https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1200&q=80',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 280,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      readMoreLimit: 220,
      readMoreLabel: 'Read More',
      readLessLabel: 'Read Less',
    },
  },
  {
    id: 'map',
    label: 'Map',
    description: 'Map embed with address details.',
    defaults: {
      title: 'Visit the Campus',
      body: 'Share the campus location, address, and visit details for parents.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: DEMO_THEME.primary,
      buttonTextColor: DEMO_THEME.white,
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: DEMO_THEME.border,
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 300,
      tabs: [],
      videoUrl: '',
      dividerColor: DEMO_THEME.accent,
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      mapEmbedUrl:
        'https://www.google.com/maps?q=Skhool%20Demo%20School%20Bengaluru&output=embed',
      mapAddress: 'Skhool Demo School, Whitefield, Bengaluru, Karnataka 560066',
    },
  },
  {
    id: 'contact_form',
    label: 'Contact Form',
    description: 'Contact us form block.',
    defaults: {
      title: 'Contact Us',
      body: 'Use this form for parent enquiries, support, or campus visit requests.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 280,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      formSubmitLabel: 'Send Message',
      formSuccessMessage: 'Thank you. Your message has been submitted.',
    },
  },
  {
    id: 'admissions_form',
    label: 'Enquiry Form',
    description: 'Lead or enquiry form block.',
    defaults: {
      title: 'Admissions Enquiry',
      body: 'Use this form to collect student details for admission enquiries.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 280,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      formSubmitLabel: 'Submit Enquiry',
      formSuccessMessage: 'Admissions enquiry submitted. Our team will contact you soon.',
    },
  },
  {
    id: 'footer',
    label: 'Footer',
    description: 'Website footer with links and contact details.',
    defaults: {
      title: 'School Footer',
      body: 'Use the footer for contact details, address, and important links.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 280,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
      footerAddress: 'School Campus, City, State, PIN',
      footerPhone: '+91 70000 00000',
      footerEmail: 'hello@school.edu',
      footerQuickLinks: ['/', '/about', '/academics', '/admissions', '/contact'],
      footerCopyright: 'Copyright 2026 Your School. All rights reserved.',
    },
  },
  {
    id: 'custom_combo',
    label: 'Custom Combo',
    description: 'Image + content + table + action in one flexible block.',
    defaults: {
      title: 'School Information',
      body: 'Use this section to combine a short note, one image, key facts, and a simple action.',
      image:
        'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1200&q=80',
      buttonLabel: 'Know More',
      buttonPath: '/contact',
      buttonEnabled: true,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      mediaHeight: 260,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: ['Heading', 'Value'],
      tableRows: [
        ['Board', 'CBSE / ICSE / State'],
        ['Classes', 'Nursery to Grade 12'],
      ],
      comboShowImage: true,
      comboShowTable: true,
      comboShowButton: true,
    },
  },
  {
    id: 'dropdown',
    label: 'Dropdown',
    description: 'Simple dropdown with optional action button.',
    defaults: {
      title: 'Quick Links',
      body: 'Use this dropdown to guide parents to important school pages.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: ['Admissions', 'Academics', 'Fee Details', 'Contact'],
      dropdownPlaceholder: 'Select an option',
      tableColumns: [],
      tableRows: [],
    },
  },
  {
    id: 'table',
    label: 'Information Table',
    description: 'Use for fee details, schedules, structured data, or disclosures.',
    defaults: {
      title: 'Key Details',
      body: '',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonVariant: 'outlined',
      buttonBgColor: '#059669',
      buttonTextColor: '#059669',
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageFrameShape: 'rectangle',
      imageCornerRadius: 0,
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
      dropdownOptions: [],
      dropdownPlaceholder: 'Select an option',
      tableColumns: ['Class', 'Strength', 'Class Teacher'],
      tableRows: [
        ['Grade 1', '30', 'Ms. Anita'],
        ['Grade 2', '32', 'Ms. Kavya'],
        ['Grade 3', '31', 'Mr. Rahul'],
      ],
    },
  },
  {
    id: 'cta',
    label: 'Call To Action',
    description: 'Strong callout with action prompt.',
    defaults: {
      title: 'Plan a Visit',
      body: 'Invite families to contact the school or schedule a campus visit.',
      image: '',
      buttonLabel: '',
      buttonPath: '',
      buttonEnabled: false,
      sectionWidth: 100,
      imagePosition: 'right',
      imageObjectPosition: 'center center',
      imageBorderColor: 'rgba(15, 23, 42, 0.2)',
      imageBorderWidth: 0,
      imageColumnSpan: 5,
      tabs: [],
      videoUrl: '',
      dividerColor: '#059669',
      dividerThickness: 3,
      dividerStyle: 'solid',
      dividerLabel: '',
    },
  },
];

const EMPTY_SECTION_ARRAY_KEYS = [
  'tabs',
  'tableColumns',
  'tableRows',
  'dropdownOptions',
  'galleryImages',
  'iconCards',
  'resultCards',
  'facultyCards',
  'founderCards',
  'profileCards',
  'statsItems',
  'sidebarItems',
  'sidebarButtons',
  'careerCards',
  'socialLinks',
  'footerQuickLinks',
];

const EMPTY_SECTION_STRING_KEYS = [
  'title',
  'body',
  'image',
  'videoUrl',
  'buttonLabel',
  'buttonPath',
  'dividerLabel',
  'mapEmbedUrl',
  'mapAddress',
  'formSubmitLabel',
  'formSuccessMessage',
  'subscribePlaceholder',
  'subscribeButtonLabel',
  'subscribeSuccessMessage',
  'sidebarTitle',
  'sidebarCtaTitle',
  'sidebarCtaSubtitle',
  'dropdownPlaceholder',
  'readMoreLabel',
  'readLessLabel',
  'footerAddress',
  'footerPhone',
  'footerEmail',
  'footerCopyright',
];

const createEmptySectionDefaults = (defaults = {}) => {
  const nextDefaults = { ...defaults };

  EMPTY_SECTION_STRING_KEYS.forEach((key) => {
    if (key in nextDefaults) {
      nextDefaults[key] = '';
    }
  });

  EMPTY_SECTION_ARRAY_KEYS.forEach((key) => {
    if (key in nextDefaults) {
      nextDefaults[key] = [];
    }
  });

  if ('buttonEnabled' in nextDefaults) {
    nextDefaults.buttonEnabled = false;
  }

  if ('sliderAutoPlay' in nextDefaults) {
    nextDefaults.sliderAutoPlay = false;
  }

  return nextDefaults;
};

const SECTION_LIBRARY = buildSectionLibrary(
  BASE_SECTION_LIBRARY.map((item) => ({
    ...item,
    defaults: createEmptySectionDefaults(item.defaults),
  }))
);

const getSectionTemplate = (type = 'text') =>
  SECTION_LIBRARY.find((item) => item.id === type) || SECTION_LIBRARY[0];

const STATIC_PAGE_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Academics', path: '/academics' },
  { label: 'Admissions', path: '/admissions' },
  { label: 'Facilities', path: '/facilities' },
  { label: 'News & Events', path: '/news' },
  { label: 'Contact', path: '/contact' },
];

const createSectionByType = (type = 'text') => {
  const template = getSectionTemplate(type);
  return {
    id: `section-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    type: template.id,
    ...template.defaults,
  };
};

const applyThemePresetToPage = (page, preset) => {
  if (!page || !preset) {
    return page;
  }

  const nextSections = (page.sections || []).map((section) => {
    if (!section || section.type === 'hero' || section.type === 'divider') {
      return section;
    }

    const nextSection = {
      ...section,
      sectionTitleColor: preset.primary,
      dividerColor: preset.accent,
    };

    if (section.type === 'cta') {
      nextSection.sectionBgColor = preset.primary;
      nextSection.buttonBgColor = '#FFFFFF';
      nextSection.buttonTextColor = preset.primary;
      nextSection.sectionTitleColor = '#FFFFFF';
    } else {
      nextSection.buttonBgColor = preset.primary;
      nextSection.buttonTextColor =
        section.buttonVariant === 'contained' ? '#FFFFFF' : preset.primary;
      if (!section.sectionBgColor || section.sectionBgColor === SECTION_BG_DEFAULT) {
        nextSection.sectionBgColor = preset.surface;
      }
    }

    return nextSection;
  });

  return {
    ...page,
    sections: nextSections,
  };
};

const clampNumber = (value, min, max, fallback) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return fallback;
  }
  return Math.min(max, Math.max(min, numeric));
};

const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

const normalizeHexColor = (value, fallback) => {
  const nextValue = String(value || '').trim();
  if (HEX_COLOR_PATTERN.test(nextValue)) {
    return nextValue;
  }
  return fallback;
};

const HERO_HEIGHT_MIN = 520;
const HERO_HEIGHT_MAX = 520;
const HERO_HEIGHT_DEFAULT = 520;
const HERO_CONTENT_WIDTH_MIN = 40;
const HERO_CONTENT_WIDTH_MAX = 100;
const HERO_CONTENT_WIDTH_DEFAULT = 86;
const SECTION_WIDTH_MIN = 20;
const SECTION_WIDTH_MAX = 100;
const SECTION_WIDTH_DEFAULT = 100;
const MEDIA_HEIGHT_MIN = 120;
const MEDIA_HEIGHT_MAX = 520;
const MEDIA_HEIGHT_DEFAULT = 280;
const SECTION_SPACING_MIN = 0;
const SECTION_SPACING_MAX = 8;
const SECTION_SPACING_DEFAULT = 0;
const COMPONENT_MARGIN_MIN = 0;
const COMPONENT_MARGIN_MAX = 120;
const COMPONENT_MARGIN_DEFAULT = 8;
const SECTION_BG_DEFAULT = '#FFFFFF';
const SECTION_BORDER_DEFAULT = 'none';
const SECTION_TITLE_COLOR_DEFAULT = DEMO_THEME.primary;
const SECTION_TITLE_BG_DEFAULT = '#FFFFFF';
const SECTION_TITLE_FONT_MIN = 14;
const SECTION_TITLE_FONT_MAX = 52;
const SECTION_TITLE_FONT_DEFAULT = 28;
const PROFILE_IMAGE_SIZE_MIN = 40;
const PROFILE_IMAGE_SIZE_MAX = 180;
const PROFILE_IMAGE_SIZE_DEFAULT = 52;
const DEFAULT_ICON_CARDS = [
  {
    icon: '🎓',
    title: 'Qualified Teachers',
    content: 'Subject teachers guide students with regular support and follow-up.',
  },
  {
    icon: '🧪',
    title: 'Practical Learning',
    content: 'Labs, projects, and activities help students learn by doing.',
  },
  {
    icon: '🏅',
    title: 'All-Round Development',
    content: 'Sports, arts, clubs, and events support confidence and teamwork.',
  },
];
const DEFAULT_RESULT_CARDS = [
  {
    name: 'Aarav Sharma',
    className: 'Grade 10',
    percentage: '98.2%',
    marks: '491/500',
    image:
      'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=500&q=80',
    highlight: 'School Topper',
  },
  {
    name: 'Ananya Reddy',
    className: 'Grade 10',
    percentage: '97.6%',
    marks: '488/500',
    image:
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=500&q=80',
    highlight: 'Science Distinction',
  },
  {
    name: 'Vihaan Patel',
    className: 'Grade 9',
    percentage: '96.9%',
    marks: '484/500',
    image:
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=500&q=80',
    highlight: 'Mathematics Excellence',
  },
];
const DEFAULT_FACULTY_CARDS = [
  {
    name: 'Ms. Priya Sharma',
    role: 'Mathematics Faculty',
    image:
      'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=500&q=80',
    bio: 'Concept-first learning with regular practice and doubt-clearing.',
  },
  {
    name: 'Mr. Rahul Verma',
    role: 'Science Faculty',
    image:
      'https://images.unsplash.com/photo-1541534401786-2077eed87a72?auto=format&fit=crop&w=500&q=80',
    bio: 'Hands-on experiments and application-driven classroom teaching.',
  },
  {
    name: 'Ms. Kavya Iyer',
    role: 'English Faculty',
    image:
      'https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=500&q=80',
    bio: 'Communication-rich lessons focused on reading, writing, and expression.',
  },
];
const DEFAULT_FOUNDER_CARDS = [
  {
    name: 'Dr. Arvind Rao',
    role: 'Founder',
    image:
      'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=500&q=80',
    bio: 'Committed to quality education, discipline, and strong values in school life.',
  },
  {
    name: 'Ms. Nisha Rao',
    role: 'Co-Founder',
    image:
      'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=500&q=80',
    bio: 'Focused on student wellbeing, teacher development, and parent partnership.',
  },
];
const DEFAULT_CAREER_CARDS = [
  {
    title: 'Primary Teacher',
    department: 'Academics',
    location: 'Main Campus',
    employmentType: 'Full Time',
    applyPath: '/contact',
    image:
      'https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1200&q=80',
    summary: 'Lead foundational classrooms with activity-driven and student-centered teaching.',
  },
  {
    title: 'Mathematics Teacher',
    department: 'Academics',
    location: 'Main Campus',
    employmentType: 'Full Time',
    applyPath: '/contact',
    image:
      'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1200&q=80',
    summary: 'Deliver concept clarity, assessment planning, and exam readiness for middle and senior grades.',
  },
  {
    title: 'School Counselor',
    department: 'Student Support',
    location: 'Main Campus',
    employmentType: 'Full Time',
    applyPath: '/contact',
    image:
      'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1200&q=80',
    summary: 'Support student wellbeing, social-emotional development, and parent coordination.',
  },
];
const DEFAULT_SOCIAL_LINKS = [
  { label: 'Facebook', url: 'https://facebook.com', icon: 'f' },
  { label: 'Instagram', url: 'https://instagram.com', icon: 'ig' },
  { label: 'YouTube', url: 'https://youtube.com', icon: 'yt' },
  { label: 'LinkedIn', url: 'https://linkedin.com', icon: 'in' },
];

const getSectionSurfaceSx = (section = {}) => {
  const type = section.type;
  if (type === 'divider' || type === 'hero') {
    return {
      p: { xs: 0.5, md: 0.7 },
      borderRadius: 0,
      border: 'none',
      background: 'transparent',
      boxShadow: 'none',
    };
  }

  const sectionBorderEnabled = false;
  const sectionBgColor = normalizeHexColor(section.sectionBgColor, SECTION_BG_DEFAULT);
  const sectionFrameShape = ['rectangle', 'rounded'].includes(section.sectionFrameShape)
    ? section.sectionFrameShape
    : 'rectangle';
  const sectionCornerRadius = clampNumber(section.sectionCornerRadius, 0, 40, 12);
  return {
    p: { xs: 1.7, md: 2.3 },
    borderRadius:
      sectionFrameShape === 'rounded'
        ? `${sectionCornerRadius}px`
        : 0,
    border: sectionBorderEnabled ? SECTION_BORDER_DEFAULT : 'none',
    background: sectionBgColor,
    boxShadow: 'none',
  };
};

const normalizeTabItems = (tabs) => {
  if (!Array.isArray(tabs) || tabs.length === 0) {
    return [
      { title: 'Overview', content: '<p>Update this tab content.</p>' },
      { title: 'Details', content: '<p>Add details here.</p>' },
    ];
  }

  return tabs.map((item, index) => ({
    title: item?.title || `Tab ${index + 1}`,
    content: item?.content || '<p>Add content here.</p>',
  }));
};

const normalizeStringList = (value, fallback = []) => {
  if (Array.isArray(value)) {
    const list = value.map((item) => String(item || '').trim()).filter(Boolean);
    return list.length ? list : fallback;
  }
  if (typeof value === 'string') {
    const list = value
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean);
    return list.length ? list : fallback;
  }
  return fallback;
};

const normalizeDropdownOptions = (value) =>
  normalizeStringList(value, ['Option 1', 'Option 2', 'Option 3']);

const normalizeTableColumns = (value) => normalizeStringList(value, ['Column 1', 'Column 2']);

const normalizeTableRows = (value, columnCount = 2) => {
  const normalizedColumnCount = Math.max(1, Number(columnCount) || 2);
  const normalizeRow = (row) => {
    const cells = Array.isArray(row)
      ? row.map((item) => String(item || '').trim())
      : String(row || '')
          .split('|')
          .map((item) => item.trim());
    const sliced = cells.slice(0, normalizedColumnCount);
    while (sliced.length < normalizedColumnCount) {
      sliced.push('');
    }
    return sliced;
  };

  if (Array.isArray(value) && value.length) {
    return value.map((row) => normalizeRow(row));
  }

  if (typeof value === 'string' && value.trim()) {
    return value
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => normalizeRow(line));
  }

  return [Array.from({ length: normalizedColumnCount }).map(() => '')];
};

const parseTableColumnsInput = (value = '') =>
  normalizeStringList(
    String(value || '')
      .split(/[\n|,]/)
      .map((item) => item.trim())
      .filter(Boolean),
    ['Column 1', 'Column 2']
  );

const parseTableRowsInput = (value = '', columnCount = 2) =>
  normalizeTableRows(String(value || ''), columnCount);

const serializeTableRows = (rows = []) =>
  (Array.isArray(rows) ? rows : [])
    .map((row) => (Array.isArray(row) ? row : [row]).map((cell) => String(cell || '').trim()).join(' | '))
    .join('\n');

const normalizeIconCards = (value, fallback = DEFAULT_ICON_CARDS) => {
  const normalizeCard = (card, index) => {
    if (!card) {
      return null;
    }

    if (typeof card === 'string') {
      const [iconPart = '', titlePart = '', ...contentParts] = card.split('|').map((item) => item.trim());
      return {
        icon: iconPart || '•',
        title: titlePart || `Card ${index + 1}`,
        content: contentParts.join(' | ') || '',
      };
    }

    const icon = String(card.icon || '').trim() || '•';
    const title = String(card.title || '').trim() || `Card ${index + 1}`;
    const content = String(card.content || '').trim();
    return { icon, title, content };
  };

  if (Array.isArray(value)) {
    const cards = value.map((card, index) => normalizeCard(card, index)).filter(Boolean);
    return cards.length ? cards : fallback;
  }

  if (typeof value === 'string') {
    const cards = value
      .split('\n')
      .map((line, index) => normalizeCard(line, index))
      .filter(Boolean);
    return cards.length ? cards : fallback;
  }

  return fallback;
};

const parseIconCardsInput = (value = '') => normalizeIconCards(String(value || ''), []);

const serializeIconCards = (cards = []) =>
  normalizeIconCards(cards, [])
    .map((card) => `${card.icon} | ${card.title} | ${card.content}`)
    .join('\n');

const normalizeStatsItems = (value, fallback = []) => {
  const normalizeItem = (item, index) => {
    if (!item) {
      return null;
    }

    if (typeof item === 'string') {
      const [iconPart = '', valuePart = '', ...labelParts] = item.split('|').map((part) => part.trim());
      return {
        icon: iconPart || '•',
        value: valuePart || '0',
        label: labelParts.join(' | ') || `Stat ${index + 1}`,
      };
    }

    return {
      icon: String(item.icon || '').trim() || '•',
      value: String(item.value || '').trim() || '0',
      label: String(item.label || '').trim() || `Stat ${index + 1}`,
    };
  };

  if (Array.isArray(value)) {
    const items = value.map((item, index) => normalizeItem(item, index)).filter(Boolean);
    return items.length ? items : fallback;
  }

  if (typeof value === 'string') {
    const items = value
      .split('\n')
      .map((line, index) => normalizeItem(line, index))
      .filter(Boolean);
    return items.length ? items : fallback;
  }

  return fallback;
};

const parseStatsItemsInput = (value = '') => normalizeStatsItems(String(value || ''), []);

const serializeStatsItems = (items = []) =>
  normalizeStatsItems(items, [])
    .map((item) => `${item.icon} | ${item.value} | ${item.label}`)
    .join('\n');

const normalizeSidebarItems = (value, fallback = []) => {
  const normalizeItem = (item) => {
    if (!item) {
      return null;
    }
    if (typeof item === 'string') {
      const [labelPart = '', pathPart = ''] = item.split('|').map((part) => part.trim());
      return { label: labelPart, path: pathPart };
    }
    return { label: String(item.label || '').trim(), path: String(item.path || '').trim() };
  };

  if (Array.isArray(value)) {
    const items = value.map((item) => normalizeItem(item)).filter((item) => item?.label && item?.path);
    return items.length ? items : fallback;
  }
  if (typeof value === 'string') {
    const items = value
      .split('\n')
      .map((line) => normalizeItem(line))
      .filter((item) => item?.label && item?.path);
    return items.length ? items : fallback;
  }
  return fallback;
};

const parseSidebarItemsInput = (value = '') => normalizeSidebarItems(String(value || ''), []);

const serializeSidebarItems = (items = []) =>
  normalizeSidebarItems(items, [])
    .map((item) => `${item.label} | ${item.path}`)
    .join('\n');

const normalizeResultCards = (value, fallback = DEFAULT_RESULT_CARDS) => {
  const normalizeCard = (card, index) => {
    if (!card) {
      return null;
    }

    if (typeof card === 'string') {
      const [namePart = '', classPart = '', percentagePart = '', marksPart = '', imagePart = '', ...highlightParts] =
        card.split('|').map((item) => item.trim());
      return {
        name: namePart || `Student ${index + 1}`,
        className: classPart || '',
        percentage: percentagePart || '',
        marks: marksPart || '',
        image: imagePart || '',
        highlight: highlightParts.join(' | ') || '',
      };
    }

    return {
      name: String(card.name || '').trim() || `Student ${index + 1}`,
      className: String(card.className || '').trim(),
      percentage: String(card.percentage || '').trim(),
      marks: String(card.marks || '').trim(),
      image: String(card.image || '').trim(),
      highlight: String(card.highlight || '').trim(),
    };
  };

  if (Array.isArray(value)) {
    const cards = value.map((card, index) => normalizeCard(card, index)).filter(Boolean);
    return cards.length ? cards : fallback;
  }

  if (typeof value === 'string') {
    const cards = value
      .split('\n')
      .map((line, index) => normalizeCard(line, index))
      .filter(Boolean);
    return cards.length ? cards : fallback;
  }

  return fallback;
};

const parseResultCardsInput = (value = '') => normalizeResultCards(String(value || ''), []);

const serializeResultCards = (cards = []) =>
  normalizeResultCards(cards, [])
    .map((card) => `${card.name} | ${card.className} | ${card.percentage} | ${card.marks} | ${card.image} | ${card.highlight}`)
    .join('\n');

const normalizeProfileCards = (value, fallback = []) => {
  const normalizeCard = (card, index) => {
    if (!card) {
      return null;
    }

    if (typeof card === 'string') {
      const [namePart = '', rolePart = '', imagePart = '', ...bioParts] = card.split('|').map((item) => item.trim());
      return {
        name: namePart || `Profile ${index + 1}`,
        role: rolePart || '',
        image: imagePart || '',
        bio: bioParts.join(' | ') || '',
      };
    }

    return {
      name: String(card.name || '').trim() || `Profile ${index + 1}`,
      role: String(card.role || '').trim(),
      image: String(card.image || '').trim(),
      bio: String(card.bio || '').trim(),
    };
  };

  if (Array.isArray(value)) {
    const cards = value.map((card, index) => normalizeCard(card, index)).filter(Boolean);
    return cards.length ? cards : fallback;
  }

  if (typeof value === 'string') {
    const cards = value
      .split('\n')
      .map((line, index) => normalizeCard(line, index))
      .filter(Boolean);
    return cards.length ? cards : fallback;
  }

  return fallback;
};

const parseFacultyCardsInput = (value = '') => normalizeProfileCards(String(value || ''), []);
const parseFounderCardsInput = (value = '') => normalizeProfileCards(String(value || ''), []);
const parseProfileCardsInput = (value = '') => normalizeProfileCards(String(value || ''), []);

const serializeProfileCards = (cards = []) =>
  normalizeProfileCards(cards, [])
    .map((card) => `${card.name} | ${card.role} | ${card.image} | ${card.bio}`)
    .join('\n');

const normalizeCareerCards = (value, fallback = DEFAULT_CAREER_CARDS) => {
  const normalizeCard = (card, index) => {
    if (!card) {
      return null;
    }

    if (typeof card === 'string') {
      const parts = card.split('|').map((item) => item.trim());
      const [titlePart = '', deptPart = '', locationPart = '', typePart = '', applyPathPart = ''] = parts;
      const imagePart = parts.length >= 7 ? parts[5] : '';
      const summaryParts = parts.length >= 7 ? parts.slice(6) : parts.slice(5);
      return {
        title: titlePart || `Role ${index + 1}`,
        department: deptPart || '',
        location: locationPart || '',
        employmentType: typePart || '',
        applyPath: applyPathPart || '',
        image: imagePart || '',
        summary: summaryParts.join(' | ') || '',
      };
    }

    return {
      title: String(card.title || '').trim() || `Role ${index + 1}`,
      department: String(card.department || '').trim(),
      location: String(card.location || '').trim(),
      employmentType: String(card.employmentType || '').trim(),
      applyPath: String(card.applyPath || '').trim(),
      image: String(card.image || '').trim(),
      summary: String(card.summary || '').trim(),
    };
  };

  if (Array.isArray(value)) {
    const cards = value.map((card, index) => normalizeCard(card, index)).filter(Boolean);
    return cards.length ? cards : fallback;
  }

  if (typeof value === 'string') {
    const cards = value
      .split('\n')
      .map((line, index) => normalizeCard(line, index))
      .filter(Boolean);
    return cards.length ? cards : fallback;
  }

  return fallback;
};

const parseCareerCardsInput = (value = '') => normalizeCareerCards(String(value || ''), []);

const serializeCareerCards = (cards = []) =>
  normalizeCareerCards(cards, [])
    .map(
      (card) =>
        `${card.title} | ${card.department} | ${card.location} | ${card.employmentType} | ${card.applyPath} | ${card.image || ''} | ${card.summary}`
    )
    .join('\n');

const normalizeSocialLinks = (value, fallback = DEFAULT_SOCIAL_LINKS) => {
  const normalizeLink = (item, index) => {
    if (!item) {
      return null;
    }

    if (typeof item === 'string') {
      const [labelPart = '', urlPart = '', ...iconParts] = item.split('|').map((part) => part.trim());
      return {
        label: labelPart || `Social ${index + 1}`,
        url: urlPart || '',
        icon: iconParts.join(' | ') || labelPart.slice(0, 2).toUpperCase() || 'S',
      };
    }

    const label = String(item.label || '').trim() || `Social ${index + 1}`;
    const url = String(item.url || '').trim();
    const icon = String(item.icon || '').trim() || label.slice(0, 2).toUpperCase();
    return { label, url, icon };
  };

  if (Array.isArray(value)) {
    const links = value.map((item, index) => normalizeLink(item, index)).filter(Boolean);
    return links.length ? links : fallback;
  }

  if (typeof value === 'string') {
    const links = value
      .split('\n')
      .map((line, index) => normalizeLink(line, index))
      .filter(Boolean);
    return links.length ? links : fallback;
  }

  return fallback;
};

const parseSocialLinksInput = (value = '') => normalizeSocialLinks(String(value || ''), []);

const serializeSocialLinks = (links = []) =>
  normalizeSocialLinks(links, [])
    .map((link) => `${link.label} | ${link.url} | ${link.icon}`)
    .join('\n');

const looksLikeHtml = (value = '') =>
  typeof value === 'string' && /<\/?[a-z][\s\S]*>/i.test(value.trim());

const sanitizeUserHtml = (value = '') => {
  if (typeof value !== 'string') {
    return '';
  }

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return value;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(value, 'text/html');
    const disallowed = ['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta'];
    disallowed.forEach((tag) => {
      doc.querySelectorAll(tag).forEach((node) => node.remove());
    });

    doc.body.querySelectorAll('*').forEach((node) => {
      Array.from(node.attributes).forEach((attribute) => {
        const name = attribute.name.toLowerCase();
        const attrValue = attribute.value || '';
        if (name.startsWith('on')) {
          node.removeAttribute(attribute.name);
        }
        if ((name === 'href' || name === 'src') && /^\s*javascript:/i.test(attrValue)) {
          node.removeAttribute(attribute.name);
        }
      });
    });

    return doc.body.innerHTML;
  } catch (_error) {
    return value;
  }
};

const normalizeSection = (section = {}) => {
  const template = getSectionTemplate(section.type || 'text');
  const defaultImageFrameShape =
    template.defaults.imageFrameShape === 'rectangle'
      ? 'rounded'
      : template.defaults.imageFrameShape || 'rounded';
  const frameShape = section.imageFrameShape || defaultImageFrameShape;
  const buttonVariant = section.buttonVariant || template.defaults.buttonVariant || 'outlined';
  const tableColumns = normalizeTableColumns(section.tableColumns ?? template.defaults.tableColumns);
  const animationStyle = section.animationStyle || template.defaults.animationStyle || 'fade-up';
  const hasButtonConfig = Boolean(String(section.buttonLabel || '').trim() || String(section.buttonPath || '').trim());
  const inferredButtonEnabled =
    section.buttonEnabled ??
    template.defaults.buttonEnabled ??
    (hasButtonConfig && ['cta', 'button', 'dropdown'].includes(section.type || template.id));
  const isHeroOrDivider = ['hero', 'divider'].includes(section.type || template.id);
  const componentMarginFallback = isHeroOrDivider ? 0 : COMPONENT_MARGIN_DEFAULT;
  const defaultSectionFrameShape =
    template.defaults.sectionFrameShape === 'rectangle'
      ? 'rounded'
      : template.defaults.sectionFrameShape || 'rounded';
  const sectionFrameShape = section.sectionFrameShape || defaultSectionFrameShape;
  return {
    id: section.id || `section-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    type: section.type || template.id,
    title: section.title ?? template.defaults.title,
    body: section.body ?? template.defaults.body,
    image: section.image ?? template.defaults.image,
    buttonLabel: section.buttonLabel ?? template.defaults.buttonLabel ?? '',
    buttonPath: section.buttonPath ?? template.defaults.buttonPath ?? '',
    buttonEnabled: Boolean(inferredButtonEnabled),
    buttonVariant: ['contained', 'outlined', 'text'].includes(buttonVariant) ? buttonVariant : 'outlined',
    buttonBgColor: section.buttonBgColor || template.defaults.buttonBgColor || DEMO_THEME.accent,
    buttonTextColor: section.buttonTextColor || template.defaults.buttonTextColor || DEMO_THEME.accent,
    sectionBgColor: section.sectionBgColor || template.defaults.sectionBgColor || SECTION_BG_DEFAULT,
    sectionTitleColor: normalizeHexColor(
      section.sectionTitleColor || template.defaults.sectionTitleColor || SECTION_TITLE_COLOR_DEFAULT,
      SECTION_TITLE_COLOR_DEFAULT
    ),
    sectionTitleBgColor: normalizeHexColor(
      section.sectionTitleBgColor || template.defaults.sectionTitleBgColor || SECTION_TITLE_BG_DEFAULT,
      SECTION_TITLE_BG_DEFAULT
    ),
    sectionTitleBgEnabled: section.sectionTitleBgEnabled ?? template.defaults.sectionTitleBgEnabled ?? false,
    sectionTitleAlign: ['left', 'center', 'right'].includes(section.sectionTitleAlign)
      ? section.sectionTitleAlign
      : template.defaults.sectionTitleAlign || 'left',
    sectionTitleFontSize: clampNumber(
      section.sectionTitleFontSize ?? template.defaults.sectionTitleFontSize,
      SECTION_TITLE_FONT_MIN,
      SECTION_TITLE_FONT_MAX,
      SECTION_TITLE_FONT_DEFAULT
    ),
    sectionBorderEnabled: section.sectionBorderEnabled ?? template.defaults.sectionBorderEnabled ?? false,
    sectionFrameShape: ['rectangle', 'rounded'].includes(sectionFrameShape) ? sectionFrameShape : 'rectangle',
    sectionCornerRadius: clampNumber(
      section.sectionCornerRadius ?? template.defaults.sectionCornerRadius,
      0,
      40,
      12
    ),
    sectionMarginTop: clampNumber(
      section.sectionMarginTop ?? template.defaults.sectionMarginTop,
      COMPONENT_MARGIN_MIN,
      COMPONENT_MARGIN_MAX,
      componentMarginFallback
    ),
    sectionMarginBottom: clampNumber(
      section.sectionMarginBottom ?? template.defaults.sectionMarginBottom,
      COMPONENT_MARGIN_MIN,
      COMPONENT_MARGIN_MAX,
      componentMarginFallback
    ),
    sectionWidth: clampNumber(section.sectionWidth, SECTION_WIDTH_MIN, SECTION_WIDTH_MAX, SECTION_WIDTH_DEFAULT),
    heroHeight: clampNumber(
      section.heroHeight ?? template.defaults.heroHeight,
      HERO_HEIGHT_MIN,
      HERO_HEIGHT_MAX,
      HERO_HEIGHT_DEFAULT
    ),
    heroContentWidth: clampNumber(
      section.heroContentWidth ?? template.defaults.heroContentWidth,
      HERO_CONTENT_WIDTH_MIN,
      HERO_CONTENT_WIDTH_MAX,
      HERO_CONTENT_WIDTH_DEFAULT
    ),
    mediaHeight: clampNumber(
      section.mediaHeight ?? template.defaults.mediaHeight,
      MEDIA_HEIGHT_MIN,
      MEDIA_HEIGHT_MAX,
      MEDIA_HEIGHT_DEFAULT
    ),
    imagePosition: section.imagePosition === 'left' ? 'left' : 'right',
    imageObjectPosition: section.imageObjectPosition || 'center center',
    imageFrameShape: ['rectangle', 'rounded'].includes(frameShape) ? frameShape : 'rectangle',
    imageCornerRadius: clampNumber(
      section.imageCornerRadius ?? template.defaults.imageCornerRadius,
      0,
      40,
      14
    ),
    profileImageShape: ['circle', 'rectangle', 'square', 'rounded'].includes(section.profileImageShape)
      ? section.profileImageShape
      : (template.defaults.profileImageShape === 'rectangle'
        ? 'rounded'
        : template.defaults.profileImageShape || 'rounded'),
    profileImageAlign: ['left', 'center'].includes(section.profileImageAlign)
      ? section.profileImageAlign
      : template.defaults.profileImageAlign || 'left',
    profileImageObjectPosition:
      section.profileImageObjectPosition ||
      template.defaults.profileImageObjectPosition ||
      'center center',
    profileImageSize: clampNumber(
      section.profileImageSize ?? template.defaults.profileImageSize,
      PROFILE_IMAGE_SIZE_MIN,
      PROFILE_IMAGE_SIZE_MAX,
      PROFILE_IMAGE_SIZE_DEFAULT
    ),
    imageBorderColor: section.imageBorderColor || 'rgba(15, 23, 42, 0.2)',
    imageBorderWidth: clampNumber(section.imageBorderWidth, 0, 12, 0),
    imageColumnSpan: clampNumber(section.imageColumnSpan, 2, 10, 5),
    tabs: normalizeTabItems(section.tabs),
    videoUrl: section.videoUrl || '',
    galleryImages: normalizeStringList(section.galleryImages ?? template.defaults.galleryImages, []),
    galleryColumns: clampNumber(section.galleryColumns ?? template.defaults.galleryColumns, 2, 6, 3),
    sliderAutoPlay: section.sliderAutoPlay ?? template.defaults.sliderAutoPlay ?? true,
    sliderInterval: clampNumber(section.sliderInterval ?? template.defaults.sliderInterval, 1200, 10000, 3500),
    iconGridColumns: clampNumber(section.iconGridColumns ?? template.defaults.iconGridColumns, 1, 4, 3),
    iconCards: normalizeIconCards(section.iconCards ?? template.defaults.iconCards, template.defaults.iconCards || []),
    iconCardVariant: section.iconCardVariant === 'pill' ? 'pill' : template.defaults.iconCardVariant || 'card',
    iconCardRadius: clampNumber(section.iconCardRadius ?? template.defaults.iconCardRadius, 0, 64, 16),
    resultsGridColumns: clampNumber(section.resultsGridColumns ?? template.defaults.resultsGridColumns, 1, 4, 3),
    resultsMetricMode: ['percentage', 'marks', 'both'].includes(section.resultsMetricMode)
      ? section.resultsMetricMode
      : template.defaults.resultsMetricMode || 'percentage',
    resultCards: normalizeResultCards(
      section.resultCards ?? template.defaults.resultCards,
      template.defaults.resultCards || []
    ),
    facultyColumns: clampNumber(section.facultyColumns ?? template.defaults.facultyColumns, 1, 4, 3),
    founderColumns: clampNumber(section.founderColumns ?? template.defaults.founderColumns, 1, 4, 2),
    facultyCards: normalizeProfileCards(
      section.facultyCards ?? template.defaults.facultyCards,
      template.defaults.facultyCards || DEFAULT_FACULTY_CARDS
    ),
    founderCards: normalizeProfileCards(
      section.founderCards ?? template.defaults.founderCards,
      template.defaults.founderCards || DEFAULT_FOUNDER_CARDS
    ),
    profileColumns: clampNumber(section.profileColumns ?? template.defaults.profileColumns, 1, 4, 4),
    profileCards: normalizeProfileCards(
      section.profileCards ?? template.defaults.profileCards,
      template.defaults.profileCards || []
    ),
    profileCardRadius: clampNumber(section.profileCardRadius ?? template.defaults.profileCardRadius, 0, 64, 22),
    profileCardBgColor: section.profileCardBgColor || template.defaults.profileCardBgColor || '#FFFFFF',
    profileCardBorderColor:
      section.profileCardBorderColor || template.defaults.profileCardBorderColor || 'rgba(15,23,42,0.12)',
    careersLayout: ['grid', 'vertical'].includes(section.careersLayout)
      ? section.careersLayout
      : template.defaults.careersLayout || 'grid',
    careersColumns: clampNumber(section.careersColumns ?? template.defaults.careersColumns, 1, 4, 3),
    careerCards: normalizeCareerCards(
      section.careerCards ?? template.defaults.careerCards,
      template.defaults.careerCards || DEFAULT_CAREER_CARDS
    ),
    socialLinks: normalizeSocialLinks(
      section.socialLinks ?? template.defaults.socialLinks,
      template.defaults.socialLinks || DEFAULT_SOCIAL_LINKS
    ),
    subscribePlaceholder:
      section.subscribePlaceholder || template.defaults.subscribePlaceholder || '',
    subscribeButtonLabel:
      section.subscribeButtonLabel || template.defaults.subscribeButtonLabel || '',
    subscribeSuccessMessage:
      section.subscribeSuccessMessage || template.defaults.subscribeSuccessMessage || '',
    readMoreLimit: clampNumber(section.readMoreLimit ?? template.defaults.readMoreLimit, 80, 1200, 220),
    readMoreLabel: section.readMoreLabel || template.defaults.readMoreLabel || '',
    readLessLabel: section.readLessLabel || template.defaults.readLessLabel || '',
    statsColumns: clampNumber(section.statsColumns ?? template.defaults.statsColumns, 1, 4, 3),
    statsOverlayColor: section.statsOverlayColor || template.defaults.statsOverlayColor || 'rgba(15, 23, 42, 0.58)',
    statsItems: normalizeStatsItems(section.statsItems ?? template.defaults.statsItems, template.defaults.statsItems || []),
    sidebarTitle: section.sidebarTitle || template.defaults.sidebarTitle || '',
    sidebarCtaTitle: section.sidebarCtaTitle || template.defaults.sidebarCtaTitle || '',
    sidebarCtaSubtitle: section.sidebarCtaSubtitle || template.defaults.sidebarCtaSubtitle || '',
    sidebarItems: normalizeSidebarItems(
      section.sidebarItems ?? template.defaults.sidebarItems,
      template.defaults.sidebarItems || []
    ),
    sidebarButtons: normalizeSidebarItems(
      section.sidebarButtons ?? template.defaults.sidebarButtons,
      template.defaults.sidebarButtons || []
    ),
    mapEmbedUrl: section.mapEmbedUrl || template.defaults.mapEmbedUrl || '',
    mapAddress: section.mapAddress || template.defaults.mapAddress || '',
    formSubmitLabel: section.formSubmitLabel || template.defaults.formSubmitLabel || '',
    formSuccessMessage:
      section.formSuccessMessage || template.defaults.formSuccessMessage || '',
    footerAddress: section.footerAddress || template.defaults.footerAddress || '',
    footerPhone: section.footerPhone || template.defaults.footerPhone || '',
    footerEmail: section.footerEmail || template.defaults.footerEmail || '',
    footerQuickLinks: normalizeStringList(section.footerQuickLinks ?? template.defaults.footerQuickLinks, []),
    footerCopyright: section.footerCopyright || template.defaults.footerCopyright || '',
    comboShowImage: section.comboShowImage ?? template.defaults.comboShowImage ?? true,
    comboShowTable: section.comboShowTable ?? template.defaults.comboShowTable ?? true,
    comboShowButton: section.comboShowButton ?? template.defaults.comboShowButton ?? true,
    dropdownOptions: normalizeDropdownOptions(section.dropdownOptions ?? template.defaults.dropdownOptions),
    dropdownPlaceholder:
      section.dropdownPlaceholder || template.defaults.dropdownPlaceholder || '',
    tableColumns,
    tableRows: normalizeTableRows(section.tableRows ?? template.defaults.tableRows, tableColumns.length),
    animationStyle: ['none', 'fade-up', 'zoom-in'].includes(animationStyle) ? animationStyle : 'fade-up',
    animationDuration: clampNumber(
      section.animationDuration ?? template.defaults.animationDuration,
      250,
      2200,
      700
    ),
    animationDelay: clampNumber(
      section.animationDelay ?? template.defaults.animationDelay,
      0,
      1200,
      0
    ),
    dividerColor: section.dividerColor || DEMO_THEME.accent,
    dividerThickness: clampNumber(section.dividerThickness, 1, 10, 3),
    dividerStyle: ['solid', 'dashed', 'dotted', 'double'].includes(section.dividerStyle)
      ? section.dividerStyle
      : 'solid',
    dividerLabel: section.dividerLabel || '',
  };
};

const DEFAULT_CUSTOM_PAGE_SLUGS = new Set([
  'home',
  'about',
  'academics',
  'admissions',
  'facilities',
  'contact',
  'news',
]);

const REMOVED_PUBLIC_PAGE_SLUGS = new Set([
  'test',
  'schedules',
  'announcements',
  'careers',
  'results',
  'admissions-overview',
  'mission-and-vision',
]);
const BLOCKED_INTERNAL_LINK_PATHS = new Set([
  '/test',
  '/page/test',
  '/schedules',
  '/page/schedules',
  '/announcements',
  '/careers',
  '/results',
  '/page/admissions-overview',
  '/page/mission-and-vision',
]);

const normalizePresetSections = (sections = []) =>
  sections.map((section, index) =>
    normalizeSection({
      ...section,
      animationStyle: section?.animationStyle || 'fade-up',
      animationDuration: section?.animationDuration ?? 700,
      animationDelay: section?.animationDelay ?? index * 90,
    })
  );

const DEFAULT_WEBSITE_SCHOOL_NAME = 'Greenfield International School';

const getDefaultWebsiteSchoolProfile = () => {
  const profile = getStoredPublicSchoolProfile();
  return profile && typeof profile === 'object' ? profile : {};
};

const getDefaultWebsiteSchoolName = () => {
  const profileName = String(getDefaultWebsiteSchoolProfile().name || '').trim();
  if (profileName) {
    return profileName;
  }

  if (typeof window === 'undefined') {
    return DEFAULT_WEBSITE_SCHOOL_NAME;
  }

  const storedSchoolName = String(localStorage.getItem('schoolName') || '').trim();
  return storedSchoolName || DEFAULT_WEBSITE_SCHOOL_NAME;
};

const SCHOOL_SITE_IMAGES = {
  homeHero:
    'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=2200&q=80',
  aboutHero:
    'https://images.unsplash.com/photo-1513258496099-48168024aec0?auto=format&fit=crop&w=2200&q=80',
  academicsHero:
    'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=2200&q=80',
  admissionsHero:
    'https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=2200&q=80',
  facilitiesHero:
    'https://images.unsplash.com/photo-1577896851231-70ef18881754?auto=format&fit=crop&w=2200&q=80',
  newsHero:
    'https://images.unsplash.com/photo-1497486751825-1233686d5d80?auto=format&fit=crop&w=2200&q=80',
  contactHero:
    'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=2200&q=80',
  campusOverview:
    'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1600&q=80',
  campusWalkway:
    'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=1600&q=80',
  principalPortrait:
    'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=900&q=80',
  counsellorPortrait:
    'https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=900&q=80',
  leaderOne:
    'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=900&q=80',
  leaderTwo:
    'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=900&q=80',
  leaderThree:
    'https://images.unsplash.com/photo-1544717301-9cdcb1f5940f?auto=format&fit=crop&w=900&q=80',
  topperOne:
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=900&q=80',
  topperTwo:
    'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=900&q=80',
  topperThree:
    'https://images.unsplash.com/photo-1506794778202-cad84cf45f1?auto=format&fit=crop&w=900&q=80',
  lab:
    'https://images.unsplash.com/photo-1588072432836-e10032774350?auto=format&fit=crop&w=1600&q=80',
  library:
    'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?auto=format&fit=crop&w=1600&q=80',
  sports:
    'https://images.unsplash.com/photo-1517649763962-0c623066013b?auto=format&fit=crop&w=1600&q=80',
  arts:
    'https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&w=1600&q=80',
  assembly:
    'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?auto=format&fit=crop&w=1600&q=80',
  classroom:
    'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1600&q=80',
  eventOne:
    'https://images.unsplash.com/photo-1511632765486-a01980e01a18?auto=format&fit=crop&w=1600&q=80',
  eventTwo:
    'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1600&q=80',
  eventThree:
    'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?auto=format&fit=crop&w=1600&q=80',
  campusVisit:
    'https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=1600&q=80',
};

const joinParagraphs = (...paragraphs) =>
  paragraphs
    .filter(Boolean)
    .map((paragraph) => `<p>${paragraph}</p>`)
    .join('');

const createHeroSection = ({
  title,
  body,
  image,
  buttonLabel,
  buttonPath,
  heroHeight = 520,
  heroContentWidth = 58,
}) => ({
  type: 'hero',
  title,
  body,
  image,
  heroHeight,
  heroContentWidth,
  imageObjectPosition: 'center center',
  buttonEnabled: true,
  buttonLabel,
  buttonPath,
});

const createPagePreset = ({ pageTitle, subtitle, heroImage = '', sections = [] }) => ({
  pageTitle,
  subtitle,
  heroImage,
  heroVideo: '',
  showHero: true,
  heroHeight: 520,
  heroContentWidth: 58,
  sectionSpacing: SECTION_SPACING_DEFAULT,
  sections: normalizePresetSections(sections),
});

const ensureHeroSectionInSections = (pageLike = {}) => {
  const sourceSections = Array.isArray(pageLike.sections) ? pageLike.sections : [];
  const hasHeroSection = sourceSections.some((section) => section?.type === 'hero');

  if (hasHeroSection) {
    return sourceSections;
  }

  if (pageLike.showHero === false) {
    return sourceSections;
  }

  const hasLegacyHeroContent = Boolean(
    pageLike.heroImage ||
    pageLike.heroVideo ||
    pageLike.pageTitle ||
    pageLike.subtitle
  );

  if (!hasLegacyHeroContent) {
    return sourceSections;
  }

  return [
    createHeroSection({
      title: pageLike.pageTitle || '',
      body: pageLike.subtitle || '',
      image: pageLike.heroImage || '',
      buttonLabel: '',
      buttonPath: '',
      heroHeight: clampNumber(pageLike.heroHeight, HERO_HEIGHT_MIN, HERO_HEIGHT_MAX, HERO_HEIGHT_DEFAULT),
      heroContentWidth: clampNumber(
        pageLike.heroContentWidth,
        HERO_CONTENT_WIDTH_MIN,
        HERO_CONTENT_WIDTH_MAX,
        HERO_CONTENT_WIDTH_DEFAULT
      ),
    }),
    ...sourceSections,
  ];
};

const createDefaultPage = (slug) => {
  const schoolProfile = getDefaultWebsiteSchoolProfile();
  const schoolName = getDefaultWebsiteSchoolName();
  const principalName = String(schoolProfile.principalName || 'Principal').trim() || 'Principal';
  const schoolDescription =
    String(schoolProfile.description || '').trim() ||
    'A welcoming school community built on strong academics, meaningful relationships, and balanced student growth.';
  const schoolPhone = String(schoolProfile.phone || '+91 98765 43210').trim();
  const schoolEmail = String(schoolProfile.email || 'admissions@greenfieldschool.edu.in').trim();
  const schoolAddress = String(schoolProfile.address || '12 Learning Avenue, Whitefield, Bengaluru, Karnataka 560066').trim();
  const schoolHours = String(schoolProfile.openingHours || 'Monday to Friday, 8:00 AM to 4:00 PM').trim();
  const schoolBoard = String(schoolProfile.affiliationBoard || schoolProfile.recognisedBy || '').trim();
  const contactMapQuery = schoolAddress || `${schoolName} campus`;
  const contactMapEmbedUrl = `https://www.google.com/maps?q=${encodeURIComponent(contactMapQuery)}&z=14&output=embed`;
  const contactDirectionsUrl = `https://maps.google.com/?q=${encodeURIComponent(contactMapQuery)}`;

  if (slug === 'home') {
    return createPagePreset({
      pageTitle: schoolName,
      subtitle: schoolDescription,
      heroImage: SCHOOL_SITE_IMAGES.homeHero,
      sections: [
        createHeroSection({
          title: schoolName,
          body: joinParagraphs(
            'A vibrant day school with strong academics, caring teachers, colourful campus life, and clear pathways from foundational years to senior school.',
            'Use this demo website as a starting point for a real school website with modern layouts, strong imagery, and easy navigation.'
          ),
          image: SCHOOL_SITE_IMAGES.homeHero,
          buttonLabel: 'Apply for Admission',
          buttonPath: '/admissions',
        }),
        {
          type: 'stats_strip',
          title: 'Quick Facts',
          body: 'A simple snapshot of what families often look for first.',
          statsColumns: 4,
          statsItems: [
            { label: 'Student-Teacher Ratio', value: '1:18' },
            { label: 'Clubs & Activities', value: '30+' },
            { label: 'Campus Size', value: '15 Acres' },
            { label: 'Board Success', value: '98%' },
          ],
        },
        {
          type: 'image',
          title: 'A campus designed for focused learning and joyful discovery',
          body: joinParagraphs(
            'Bright classrooms, science labs, arts spaces, and open play zones help students move through the day with energy and confidence.',
            'The layout is intentionally clean and easy for parents to scan while still giving the school enough space to tell its story.'
          ),
          image: SCHOOL_SITE_IMAGES.campusOverview,
          imagePosition: 'left',
        },
        {
          type: 'icon_grid',
          title: 'What students experience every day',
          body: 'The best school websites quickly show what learning feels like on campus.',
          iconGridColumns: 4,
          iconCards: [
            { icon: 'ST', title: 'STEM Learning', content: 'Labs, coding exposure, and hands-on classroom projects across grades.' },
            { icon: 'AR', title: 'Arts & Expression', content: 'Visual arts, music, theatre, and showcase opportunities through the year.' },
            { icon: 'SP', title: 'Sports Programme', content: 'Daily activity, team sport, fitness routines, and competitive participation.' },
            { icon: 'CW', title: 'Care & Wellbeing', content: 'Pastoral care, mentoring, and a school environment that feels safe and supported.' },
          ],
        },
        {
          type: 'tabs',
          title: 'Academic Pathways',
          body: joinParagraphs(
            'A clear stage-wise curriculum helps families understand how teaching, support, and expectations evolve from the early years to senior school.',
            'This layout works best when each tab explains classroom experience, subject focus, and student growth in a practical way.'
          ),
          tabs: [
            {
              title: 'Foundational Years',
              content:
                '<p>In the foundational years, learning is organised around joyful routines, language readiness, early numeracy, movement, music, and social growth. Children build confidence through guided play, stories, hands-on activities, and warm classroom relationships.</p><ul><li>Phonics readiness, vocabulary building, and listening skills</li><li>Number sense through games, manipulatives, and daily practice</li><li>Circle time, movement, music, and habit formation</li></ul><p>Teachers focus on routine, curiosity, and confidence so that children feel safe, engaged, and ready for primary learning.</p>',
            },
            {
              title: 'Primary School',
              content:
                '<p>Primary school strengthens the core building blocks of English, mathematics, environmental studies, reading habits, and project work. Students begin to communicate ideas clearly, read with greater confidence, and work more independently in class.</p><ul><li>Concept-based learning in languages, mathematics, and EVS</li><li>Reading programmes, writing practice, and activity-led classrooms</li><li>Art, sports, assemblies, and class projects as part of regular school life</li></ul><p>The emphasis is on strong foundations, steady progress, and positive classroom participation.</p>',
            },
            {
              title: 'Middle School',
              content:
                '<p>Middle school helps students move into deeper subject learning while continuing to build confidence, discipline, and communication skills. Lessons include practical work, subject specialists, and more opportunities for teamwork and presentation.</p><ul><li>Science labs, research tasks, and practical application</li><li>Independent study habits and stronger written responses</li><li>Clubs, competitions, public speaking, and collaborative projects</li></ul><p>This stage prepares learners for greater responsibility while keeping support and mentoring visible.</p>',
            },
            {
              title: 'Senior School',
              content:
                '<p>Senior school focuses on subject depth, board readiness, revision planning, and career awareness. Students are supported through structured teaching, periodic review, exam strategy, and individual academic guidance.</p><ul><li>Board-oriented planning with clear assessment milestones</li><li>Subject guidance, revision schedules, and targeted support</li><li>Career conversations, stream awareness, and future planning</li></ul><p>The goal is to help students perform with clarity while staying balanced, focused, and well supported.</p>',
            },
          ],
        },
        {
          type: 'quote',
          title: 'A message from the Principal',
          body: joinParagraphs(
            schoolDescription,
            'We want every child to feel known, challenged, and encouraged through strong academics, confidence, curiosity, and character.'
          ),
          image: SCHOOL_SITE_IMAGES.principalPortrait,
          buttonLabel: principalName,
          buttonPath: 'Principal',
        },
        {
          type: 'student_results_grid',
          title: 'Achievement Highlights',
          body: 'Academic performance, competitions, and student milestones can all live comfortably in one section.',
          resultsGridColumns: 3,
          resultsMetricMode: 'both',
          resultCards: [
            { name: 'Aarav Sharma', className: 'Grade 10', percentage: '98.4%', marks: '492/500', image: SCHOOL_SITE_IMAGES.topperOne },
            { name: 'Ananya Reddy', className: 'Grade 12', percentage: '97.8%', marks: '489/500', image: SCHOOL_SITE_IMAGES.topperTwo },
            { name: 'Vihaan Patel', className: 'Grade 10', percentage: '97.2%', marks: '486/500', image: SCHOOL_SITE_IMAGES.topperThree },
          ],
        },
        {
          type: 'news_highlights',
          title: 'Latest News & Events',
          body: 'Recent updates help the homepage feel active and current.',
          profileColumns: 3,
          profileCards: [
            { name: 'Annual Day 2026', role: 'Campus Event', bio: 'An evening of performances, student showcases, and parent participation across grades.', image: SCHOOL_SITE_IMAGES.eventOne },
            { name: 'STEM Innovation Week', role: 'Academic Event', bio: 'Students presented robotics, design thinking projects, and science models.', image: SCHOOL_SITE_IMAGES.lab },
            { name: 'Admissions Open House', role: 'Admissions', bio: 'Families can visit classrooms, meet teachers, and speak with the admissions team.', image: SCHOOL_SITE_IMAGES.campusVisit },
          ],
        },
        {
          type: 'image_grid',
          title: 'Campus Life',
          body: joinParagraphs('Use this section to show the texture of school life through events, classes, sports, and shared spaces.'),
          galleryColumns: 4,
          galleryImages: [
            SCHOOL_SITE_IMAGES.classroom,
            SCHOOL_SITE_IMAGES.assembly,
            SCHOOL_SITE_IMAGES.sports,
            SCHOOL_SITE_IMAGES.arts,
            SCHOOL_SITE_IMAGES.library,
          ],
        },
      ],
    });
  }

  if (slug === 'about') {
    return createPagePreset({
      pageTitle: `About ${schoolName}`,
      subtitle: 'School overview, leadership, values, and campus culture.',
      heroImage: SCHOOL_SITE_IMAGES.aboutHero,
      sections: [
        createHeroSection({
          title: `About ${schoolName}`,
          body: joinParagraphs(
            'A welcoming school community built on strong academics, meaningful relationships, and balanced student growth.',
            'This page gives families a simple, trustworthy introduction to the school.'
          ),
          image: SCHOOL_SITE_IMAGES.aboutHero,
          buttonLabel: 'Book a Campus Visit',
          buttonPath: '/contact',
        }),
        {
          type: 'text',
          title: 'Who we are',
          body: joinParagraphs(
            `${schoolName} is designed as a modern school environment where students can learn with structure, explore with confidence, and grow with support.`,
            schoolDescription
          ),
        },
        {
          type: 'image',
          title: 'A calm learning environment with modern spaces',
          body: joinParagraphs(
            'Thoughtful campus planning, bright interiors, and accessible learning spaces help students settle into the rhythm of the school day.',
            'Parents often want to understand the environment quickly, so this split layout keeps the message clean and visual.'
          ),
          image: SCHOOL_SITE_IMAGES.campusWalkway,
        },
        {
          type: 'stats_strip',
          title: 'At a Glance',
          body: 'A short proof section works well on about pages.',
          statsColumns: 4,
          statsItems: [
            { label: 'Students', value: '1800+' },
            { label: 'Teachers', value: '120+' },
            { label: 'Years of Service', value: '18' },
            { label: 'Clubs & Sports', value: '30+' },
          ],
        },
        {
          type: 'icon_grid',
          title: 'Values in Practice',
          body: 'Schools often use one strong section to explain how values show up in daily life.',
          iconGridColumns: 4,
          iconCards: [
            { icon: 'RE', title: 'Respect', content: 'Students learn to listen, collaborate, and care for their shared environment.' },
            { icon: 'IN', title: 'Integrity', content: 'Clear routines and honest communication build trust across school life.' },
            { icon: 'CU', title: 'Curiosity', content: 'Questions, projects, reading, and practical learning are part of everyday teaching.' },
            { icon: 'CO', title: 'Community', content: 'Families, teachers, and students stay closely connected through the year.' },
          ],
        },
        {
          type: 'profile_showcase',
          title: 'Leadership Team',
          body: 'Keep leadership introductions warm, short, and credible.',
          profileColumns: 3,
          profileCards: [
            { name: principalName, role: 'Principal', image: SCHOOL_SITE_IMAGES.principalPortrait, bio: schoolDescription },
            { name: 'Arjun Mehta', role: 'Head of Academics', image: SCHOOL_SITE_IMAGES.leaderOne, bio: 'Supports curriculum planning, teacher development, and strong classroom practice across sections.' },
            { name: 'Nisha Rao', role: 'Head of Student Life', image: SCHOOL_SITE_IMAGES.leaderTwo, bio: 'Builds community programmes, student leadership, co-curriculars, and parent engagement.' },
          ],
        },
        {
          type: 'image_grid',
          title: 'Life on Campus',
          body: joinParagraphs('A compact gallery helps families understand the energy and atmosphere of the school.'),
          galleryColumns: 4,
          galleryImages: [
            SCHOOL_SITE_IMAGES.campusOverview,
            SCHOOL_SITE_IMAGES.classroom,
            SCHOOL_SITE_IMAGES.assembly,
            SCHOOL_SITE_IMAGES.library,
            SCHOOL_SITE_IMAGES.arts,
          ],
        },
      ],
    });
  }

  if (slug === 'academics') {
    return createPagePreset({
      pageTitle: 'Academics',
      subtitle: 'Curriculum, learning stages, academic support, and results.',
      heroImage: SCHOOL_SITE_IMAGES.academicsHero,
      sections: [
        createHeroSection({
          title: 'Academics',
          body: joinParagraphs(
            'A clear academic structure helps families understand how learners are supported from foundational years to board preparation.',
            'This page combines programmes, support systems, and outcomes in one school-friendly flow.'
          ),
          image: SCHOOL_SITE_IMAGES.academicsHero,
          buttonLabel: 'Explore Admissions',
          buttonPath: '/admissions',
        }),
        {
          type: 'tabs',
          title: 'Programmes and Curriculum',
          body: joinParagraphs(
            'Each academic stage has a distinct classroom rhythm, subject focus, and level of student support.',
            'This section helps parents quickly compare how learning grows from the early years to senior school.'
          ),
          tabs: [
            {
              title: 'Early Years',
              content:
                '<p>Early years classrooms are designed around guided discovery, language exposure, movement, music, and social-emotional growth. Teachers use stories, songs, hands-on materials, and routine-based learning to build confidence.</p><ul><li>Play-led learning with structured daily routines</li><li>Phonics readiness, number sense, and expressive language</li><li>Movement, art, music, and circle-time participation</li></ul><p>The focus is on joyful learning and strong school readiness.</p>',
            },
            {
              title: 'Primary School',
              content:
                '<p>Primary school builds strong habits in reading, writing, mathematics, environmental studies, and project work. Students begin to organise their learning better and communicate ideas with more confidence.</p><ul><li>Core subject foundations with activity-based teaching</li><li>Reading, writing, projects, and concept clarity</li><li>Assemblies, clubs, arts, and classroom collaboration</li></ul><p>This stage combines academic growth with positive classroom habits and confidence building.</p>',
            },
            {
              title: 'Middle School',
              content:
                '<p>Middle school gives students deeper subject exposure, practical work, and stronger expectations around participation, organisation, and independent thinking. Students start working more closely with subject teachers and task-based learning.</p><ul><li>Science experiments, research tasks, and presentations</li><li>Subject depth with growing student responsibility</li><li>Co-curricular participation alongside regular academics</li></ul><p>It is a bridge stage that prepares learners for senior academic expectations.</p>',
            },
            {
              title: 'Senior School',
              content:
                '<p>Senior school supports students through board preparation, subject strategy, revision plans, and future readiness. Teaching is more focused, mentoring is more intentional, and progress tracking becomes sharper.</p><ul><li>Board-focused planning and revision support</li><li>Academic mentoring, doubt support, and test practice</li><li>Career awareness and guidance for next-step decisions</li></ul><p>The aim is to build performance, maturity, and clarity for life after school.</p>',
            },
          ],
        },
        {
          type: 'image',
          title: 'How teaching works in every classroom',
          body: joinParagraphs(
            'Lessons are planned around clear learning outcomes, practice, feedback, and student participation.',
            'Schools can use this section to explain classroom rhythm, assessment, and project-based learning in simple language.'
          ),
          image: SCHOOL_SITE_IMAGES.classroom,
          imagePosition: 'left',
        },
        {
          type: 'icon_grid',
          title: 'Academic Support Systems',
          body: 'Support systems are often what reassure parents most on an academics page.',
          iconGridColumns: 4,
          iconCards: [
            { icon: 'AS', title: 'Assessment Clarity', content: 'Regular review, feedback loops, and clear performance tracking across classes.' },
            { icon: 'MT', title: 'Mentor Support', content: 'Teachers and coordinators guide students through targets, habits, and routines.' },
            { icon: 'DL', title: 'Labs & Practice', content: 'Science labs, technology tools, library support, and subject enrichment.' },
            { icon: 'CG', title: 'Career Guidance', content: 'Senior students receive exposure to stream choices, universities, and planning.' },
          ],
        },
        {
          type: 'results',
          title: 'Board Results Snapshot',
          body: 'A compact table is enough for outcomes and exam performance.',
          tableColumns: ['Year', 'Pass %', 'Top Score', 'Distinctions'],
          tableRows: [
            ['2026', '98%', '98.4%', '82'],
            ['2025', '97%', '97.8%', '74'],
            ['2024', '97%', '97.1%', '69'],
          ],
        },
        {
          type: 'student_results_grid',
          title: 'Student Achievement Highlights',
          body: 'Highlighting a few student outcomes gives the page a human face.',
          resultsGridColumns: 3,
          resultsMetricMode: 'both',
          resultCards: [
            { name: 'Aarav Sharma', className: 'Grade 10', percentage: '98.4%', marks: '492/500', image: SCHOOL_SITE_IMAGES.topperOne },
            { name: 'Ananya Reddy', className: 'Grade 12', percentage: '97.8%', marks: '489/500', image: SCHOOL_SITE_IMAGES.topperTwo },
            { name: 'Sara Joseph', className: 'Grade 10', percentage: '97.1%', marks: '485/500', image: SCHOOL_SITE_IMAGES.topperThree },
          ],
        },
        {
          type: 'quote',
          title: 'Guidance that stays personal',
          body: joinParagraphs(
            'Strong academic systems work best when students also feel supported. Pastoral care, counselling, and mentoring remain visible through the school year.'
          ),
          image: SCHOOL_SITE_IMAGES.counsellorPortrait,
          buttonLabel: 'Student Support Team',
          buttonPath: 'Academic Guidance',
        },
      ],
    });
  }

  if (slug === 'admissions') {
    return createPagePreset({
      pageTitle: 'Admissions',
      subtitle: 'Admissions process, enquiry form, and campus visit details.',
      heroImage: SCHOOL_SITE_IMAGES.admissionsHero,
      sections: [
        createHeroSection({
          title: 'Admissions',
          body: joinParagraphs(
            'A good admissions page should feel calm, clear, and easy to act on.',
            'This demo combines process details, enquiry options, and campus visit information in a practical format.'
          ),
          image: SCHOOL_SITE_IMAGES.admissionsHero,
          buttonLabel: 'Start Enquiry',
          buttonPath: '/admissions',
        }),
        {
          type: 'text',
          title: 'Admissions made simple',
          body: joinParagraphs(
            'Families can begin with an enquiry, speak to the admissions team, schedule a visit, and move through application steps with clarity.',
            'Use short sections here rather than long policy-heavy text.'
          ),
        },
        {
          type: 'sidebar_content',
          title: 'Admissions Guide',
          sidebarTitle: 'Admissions Menu',
          body: joinParagraphs(
            'Enquiries are welcome throughout the year, subject to available seats and grade-level intake timelines.',
            'Schools can use this area for eligibility, documents, interaction details, fee notes, and visit guidance.'
          ),
          sidebarItems: [
            { label: 'Admissions', path: '/admissions' },
            { label: 'Academics', path: '/academics' },
            { label: 'Facilities', path: '/facilities' },
            { label: 'Contact', path: '/contact' },
          ],
          sidebarCtaTitle: 'Ready to begin?',
          sidebarCtaSubtitle: 'Start with an enquiry or plan a campus visit.',
          sidebarButtons: [
            { label: 'Enquire Now', path: '/admissions' },
            { label: 'Contact Office', path: '/contact' },
          ],
        },
        {
          type: 'admissions_form',
          title: 'Start Your Enquiry',
          body: joinParagraphs(
            'This section works well for online enquiries, call-back requests, or campus tour booking.',
            'Keep the copy short and let the form do the work.'
          ),
          formSubmitLabel: 'Submit Enquiry',
          formSuccessMessage: 'Thanks for reaching out. Our admissions team will contact you shortly.',
        },
        {
          type: 'map',
          title: 'Plan a Campus Visit',
          body: joinParagraphs('A map and address block gives parents an easy next step after they read the admissions process.'),
          mapAddress: schoolAddress,
          mapEmbedUrl: contactMapEmbedUrl,
          buttonEnabled: true,
          buttonLabel: 'Open in Maps',
          buttonPath: contactDirectionsUrl,
        },
        {
          type: 'icon_grid',
          title: 'What parents can expect',
          body: 'A final reassurance section works well before the footer.',
          iconGridColumns: 4,
          iconCards: [
            { icon: 'VT', title: 'Campus Visit', content: 'Meet the team, see classrooms, and understand the daily school environment.' },
            { icon: 'CL', title: 'Clear Process', content: 'Simple steps, timely follow-up, and guidance at each stage of admission.' },
            { icon: 'SC', title: 'Supportive Counselling', content: 'Get answers on programme fit, transport, student wellbeing, and campus systems.' },
            { icon: 'ON', title: 'Online Enquiry', content: 'Submit details quickly and let the admissions office connect back with you.' },
          ],
        },
      ],
    });
  }

  if (slug === 'facilities') {
    return createPagePreset({
      pageTitle: 'Facilities',
      subtitle: 'Campus spaces, labs, sports, arts, safety, and student services.',
      heroImage: SCHOOL_SITE_IMAGES.facilitiesHero,
      sections: [
        createHeroSection({
          title: 'Facilities',
          body: joinParagraphs(
            'Parents often decide quickly based on how a school presents its spaces for learning, play, arts, and daily care.',
            'This page gives those spaces room to breathe through cards, galleries, and clean image-led sections.'
          ),
          image: SCHOOL_SITE_IMAGES.facilitiesHero,
          buttonLabel: 'Book a Campus Visit',
          buttonPath: '/contact',
        }),
        {
          type: 'icon_grid',
          title: 'Spaces that support every learner',
          body: 'Use cards for the facilities parents ask about most.',
          iconGridColumns: 3,
          iconCards: [
            { icon: 'LB', title: 'Science & Computer Labs', content: 'Practical learning spaces with equipment for guided experiments and digital learning.' },
            { icon: 'LI', title: 'Library & Reading Zones', content: 'Quiet reading spaces, age-appropriate collections, and supervised study areas.' },
            { icon: 'SP', title: 'Sports Areas', content: 'Outdoor courts, activity zones, fitness spaces, and structured physical education routines.' },
            { icon: 'AR', title: 'Arts & Performance', content: 'Studios and performance spaces that support visual arts, music, and stage work.' },
            { icon: 'TR', title: 'Transport & Arrival', content: 'Planned drop-off systems, bus support, and supervised movement through the day.' },
            { icon: 'CW', title: 'Health & Wellbeing', content: 'Infirmary support, pastoral care, counselling, and safe day-to-day campus systems.' },
          ],
        },
        {
          type: 'image_grid',
          title: 'Inside the Campus',
          body: joinParagraphs('A strong gallery helps this page feel real and visual without needing long explanations.'),
          galleryColumns: 4,
          galleryImages: [
            SCHOOL_SITE_IMAGES.lab,
            SCHOOL_SITE_IMAGES.library,
            SCHOOL_SITE_IMAGES.sports,
            SCHOOL_SITE_IMAGES.arts,
            SCHOOL_SITE_IMAGES.classroom,
          ],
        },
        {
          type: 'slider',
          title: 'Campus Highlights',
          body: joinParagraphs('A slider works well for assemblies, events, classroom moments, and campus views.'),
          galleryImages: [
            SCHOOL_SITE_IMAGES.assembly,
            SCHOOL_SITE_IMAGES.classroom,
            SCHOOL_SITE_IMAGES.sports,
            SCHOOL_SITE_IMAGES.eventTwo,
          ],
        },
        {
          type: 'image',
          title: 'Safety, transport, and student care',
          body: joinParagraphs(
            'Families value clear information on supervision, transport routines, visitor management, and student wellbeing.',
            'This section gives schools space to explain those systems in a calm, reassuring way.'
          ),
          image: SCHOOL_SITE_IMAGES.campusVisit,
          imagePosition: 'left',
        },
        {
          type: 'text',
          title: 'Co-curricular life',
          body: joinParagraphs(
            'Facilities are not just buildings. They shape how students rehearse, compete, collaborate, read, create, and recharge through the school day.'
          ),
        },
      ],
    });
  }

  if (slug === 'news') {
    return createPagePreset({
      pageTitle: 'News & Events',
      subtitle: 'School news, recent events, campus moments, and community updates.',
      heroImage: SCHOOL_SITE_IMAGES.newsHero,
      sections: [
        createHeroSection({
          title: 'News & Events',
          body: joinParagraphs(
            'A school website feels more alive when parents can see recent events, celebrations, and important updates in one place.'
          ),
          image: SCHOOL_SITE_IMAGES.newsHero,
          buttonLabel: 'Contact the School',
          buttonPath: '/contact',
        }),
        {
          type: 'news_highlights',
          title: 'Recent News and Events',
          body: 'Short cards work well for annual days, competitions, open houses, and school circular highlights.',
          profileColumns: 3,
          profileCards: [
            { name: 'Annual Day Celebrations', role: 'Campus Event', bio: 'Students presented dance, drama, music, and multilingual stage performances.', image: SCHOOL_SITE_IMAGES.eventOne },
            { name: 'Inter-School Sports Meet', role: 'Sports', bio: 'Team participation, medal wins, and strong student spirit across multiple categories.', image: SCHOOL_SITE_IMAGES.sports },
            { name: 'Parent Orientation Week', role: 'Community', bio: 'New families met coordinators, explored spaces, and learned about school systems.', image: SCHOOL_SITE_IMAGES.campusVisit },
            { name: 'Robotics Showcase', role: 'Academics', bio: 'Students demonstrated prototypes, coding projects, and design problem-solving.', image: SCHOOL_SITE_IMAGES.lab },
          ],
        },
        {
          type: 'slider',
          title: 'Event Gallery',
          body: joinParagraphs('This section is useful for photo-led event storytelling.'),
          galleryImages: [
            SCHOOL_SITE_IMAGES.eventOne,
            SCHOOL_SITE_IMAGES.eventTwo,
            SCHOOL_SITE_IMAGES.eventThree,
            SCHOOL_SITE_IMAGES.assembly,
          ],
        },
        {
          type: 'text',
          title: 'School Calendar and Notices',
          body: joinParagraphs(
            'Use this page for upcoming events, holidays, circulars, and reminders that parents need to find quickly.',
            'Keeping the wording brief makes the section far easier to maintain.'
          ),
        },
        {
          type: 'image_grid',
          title: 'Recent Moments',
          body: joinParagraphs('A compact photo wall gives the page colour and movement.'),
          galleryColumns: 4,
          galleryImages: [
            SCHOOL_SITE_IMAGES.eventOne,
            SCHOOL_SITE_IMAGES.eventTwo,
            SCHOOL_SITE_IMAGES.eventThree,
            SCHOOL_SITE_IMAGES.assembly,
            SCHOOL_SITE_IMAGES.classroom,
          ],
        },
      ],
    });
  }

  if (slug === 'contact') {
    return createPagePreset({
      pageTitle: 'Contact',
      subtitle: 'Admissions office, visit details, contact form, and school channels.',
      heroImage: SCHOOL_SITE_IMAGES.contactHero,
      sections: [
        createHeroSection({
          title: 'Contact Us',
          body: joinParagraphs(
            'Keep the final contact page direct, welcoming, and easy to act on.',
            'Parents should be able to call, email, enquire, or plan a campus visit without searching around.'
          ),
          image: SCHOOL_SITE_IMAGES.contactHero,
          buttonLabel: 'Send an Enquiry',
          buttonPath: '/contact',
        }),
        {
          type: 'text',
          title: 'Admissions and Front Office',
          body: joinParagraphs(
            `Phone: ${schoolPhone}`,
            `Email: ${schoolEmail}`,
            `Office Hours: ${schoolHours}`,
            schoolBoard ? `Board / Recognition: ${schoolBoard}` : ''
          ),
        },
        {
          type: 'map',
          title: 'Visit Us',
          body: joinParagraphs('Add a map, address, and one clear action for directions.'),
          mapAddress: schoolAddress,
          mapEmbedUrl: contactMapEmbedUrl,
          buttonEnabled: true,
          buttonLabel: 'Get Directions',
          buttonPath: contactDirectionsUrl,
        },
        {
          type: 'contact_form',
          title: 'Send a Message',
          body: joinParagraphs(
            'This contact form can handle general queries, transport questions, documentation requests, or parent support.'
          ),
          formSubmitLabel: 'Send Message',
          formSuccessMessage: 'Thank you. Our team will get back to you soon.',
        },
        {
          type: 'social_links',
          title: 'Stay Connected',
          body: joinParagraphs('Schools often use one simple section for official social and digital channels.'),
          socialLinks: [
            { label: 'Facebook', icon: 'FB', url: 'https://www.facebook.com/' },
            { label: 'Instagram', icon: 'IG', url: 'https://www.instagram.com/' },
            { label: 'YouTube', icon: 'YT', url: 'https://www.youtube.com/' },
            { label: 'LinkedIn', icon: 'IN', url: 'https://www.linkedin.com/' },
          ],
        },
      ],
    });
  }

  return {
    pageTitle: '',
    subtitle: '',
    heroImage: '',
    heroVideo: '',
    showHero: false,
    heroHeight: HERO_HEIGHT_DEFAULT,
    heroContentWidth: HERO_CONTENT_WIDTH_DEFAULT,
    sectionSpacing: SECTION_SPACING_DEFAULT,
    sections: normalizePresetSections([]),
  };
};

const CustomPageRenderer = ({ forcedSlug = '' }) => {
  const { slug: slugParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const tenantKey = useMemo(() => getRootSubdomain(), []);
  const storedSchoolId = useMemo(() => localStorage.getItem('schoolId'), []);

  const slug = useMemo(() => normalizeSlug(forcedSlug || slugParam || ''), [forcedSlug, slugParam]);
  const pageSlug = useMemo(() => resolveWebsitePageSlug(slug), [slug]);
  const [isAdmin, setIsAdmin] = useState(isUserAuthenticated());

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageData, setPageData] = useState(null);
  const [error, setError] = useState('');
  const [tenantSchoolId, setTenantSchoolId] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editModeNoticeVisible, setEditModeNoticeVisible] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragReadyIndex, setDragReadyIndex] = useState(null);
  const [elementDragState, setElementDragState] = useState({ index: -1, key: '', active: false, x: 0, y: 0 });
  const [heroCardDragState, setHeroCardDragState] = useState({ index: -1, active: false, x: 0, y: 0 });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [componentDialogOpen, setComponentDialogOpen] = useState(false);
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const [toolsMinimized, setToolsMinimized] = useState(false);
  const [componentSearch, setComponentSearch] = useState('');
  const [availablePageLinks, setAvailablePageLinks] = useState(STATIC_PAGE_LINKS);
  const [activeTabsBySection, setActiveTabsBySection] = useState({});
  const [activeDropdownBySection, setActiveDropdownBySection] = useState({});
  const [expandedReadMoreBySection, setExpandedReadMoreBySection] = useState({});
  const [activeSliderBySection, setActiveSliderBySection] = useState({});
  const [careerApplyOpen, setCareerApplyOpen] = useState(false);
  const [careerApplySubmitting, setCareerApplySubmitting] = useState(false);
  const [selectedCareerRole, setSelectedCareerRole] = useState(null);
  const [careerApplyForm, setCareerApplyForm] = useState(createEmptyCareerApplyForm());

  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageTarget, setImageTarget] = useState(null);
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [sectionEditor, setSectionEditor] = useState({ open: false, index: -1, draft: null });
  const [inlineEditor, setInlineEditor] = useState({ index: -1, field: '', value: '', mode: 'text' });
  const [lastCommittedPageData, setLastCommittedPageData] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [portalReady, setPortalReady] = useState(false);
  const dragPressTimerRef = useRef(null);
  const elementPressTimerRef = useRef(null);
  const elementDragRef = useRef({
    pointerId: null,
    index: -1,
    key: '',
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    currentX: 0,
    currentY: 0,
  });
  const heroCardPressTimerRef = useRef(null);
  const heroCardDragRef = useRef({
    pointerId: null,
    index: -1,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    currentX: 0,
    currentY: 0,
  });

  const effectiveSchoolId = useMemo(
    () => String(tenantSchoolId || storedSchoolId || '').trim(),
    [storedSchoolId, tenantSchoolId]
  );

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const openCareerApplyDialog = useCallback((careerCard) => {
    setSelectedCareerRole(careerCard || null);
    setCareerApplyForm(createEmptyCareerApplyForm());
    setCareerApplyOpen(true);
  }, []);

  const closeCareerApplyDialog = useCallback(() => {
    if (careerApplySubmitting) {
      return;
    }
    setCareerApplyOpen(false);
    setSelectedCareerRole(null);
    setCareerApplyForm(createEmptyCareerApplyForm());
  }, [careerApplySubmitting]);

  const handleCareerApplyFieldChange = useCallback((field, value) => {
    setCareerApplyForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleCareerApplySubmit = useCallback(async () => {
    const firstName = String(careerApplyForm.firstName || '').trim();
    const lastName = String(careerApplyForm.lastName || '').trim();
    const email = String(careerApplyForm.email || '').trim();
    const phone = String(careerApplyForm.phone || '').trim();
    const yearsExperience = String(careerApplyForm.yearsExperience || '').trim();
    const portfolioUrl = String(careerApplyForm.portfolioUrl || '').trim();
    const message = String(careerApplyForm.message || '').trim();

    if (!tenantKey) {
      showSnackbar('Unable to submit right now. Tenant context is missing.', 'error');
      return;
    }
    if (!firstName || !lastName || !email || !phone) {
      showSnackbar('Enter first name, last name, email, and phone.', 'warning');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showSnackbar('Enter a valid email address.', 'warning');
      return;
    }

    const selectedRoleTitle = String(selectedCareerRole?.title || 'Career Application').trim();
    const selectedRoleDepartment = String(selectedCareerRole?.department || '').trim();
    const roleTag = selectedRoleDepartment
      ? `${selectedRoleTitle} - ${selectedRoleDepartment}`
      : selectedRoleTitle;

    const payload = {
      parentName: 'Career Applicant',
      firstName,
      lastName,
      email,
      phone,
      gradeApplying: selectedRoleTitle,
      areasOfInterest: `Career Application | ${roleTag}`,
      otherArea: message || 'Submitted from Careers page',
      knownLanguages: yearsExperience ? `Experience: ${yearsExperience}` : '',
      address1: portfolioUrl,
      city: '',
      region: '',
      postalCode: '',
      country: '',
      accommodationNeeded: 'No',
      sports: 'No',
    };

    setCareerApplySubmitting(true);
    try {
      await axiosPublic.post(`/api/${tenantKey}/pages/enrollment/save`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      showSnackbar('Application submitted successfully. Our team will contact you soon.', 'success');
      setCareerApplyOpen(false);
      setSelectedCareerRole(null);
      setCareerApplyForm(createEmptyCareerApplyForm());
    } catch (submitError) {
      console.error('Career application submit error:', submitError);
      showSnackbar('Failed to submit application. Please try again.', 'error');
    } finally {
      setCareerApplySubmitting(false);
    }
  }, [careerApplyForm, selectedCareerRole, showSnackbar, tenantKey]);

  const applyPageMutation = useCallback((updater, options = {}) => {
    const { trackHistory = true } = options;
    setPageData((previousValue) => {
      const nextValue =
        typeof updater === 'function'
          ? updater(clonePageSnapshot(previousValue))
          : clonePageSnapshot(updater);

      if (!nextValue) {
        return previousValue;
      }

      const previousSerialized = serializePageSnapshot(previousValue);
      const nextSerialized = serializePageSnapshot(nextValue);
      if (previousSerialized === nextSerialized) {
        return previousValue;
      }

      if (trackHistory && previousValue) {
        setUndoStack((prevHistory) => [
          ...prevHistory.slice(-(EDIT_HISTORY_LIMIT - 1)),
          clonePageSnapshot(previousValue),
        ]);
        setRedoStack([]);
      }

      return clonePageSnapshot(nextValue);
    });
  }, []);

  const resetEditSession = useCallback((nextPageData) => {
    const snapshot = clonePageSnapshot(nextPageData);
    setPageData(snapshot);
    setLastCommittedPageData(clonePageSnapshot(snapshot));
    setUndoStack([]);
    setRedoStack([]);
  }, []);

  const hasUnsavedChanges = useMemo(() => {
    if (!pageData || !lastCommittedPageData) {
      return false;
    }
    return serializePageSnapshot(pageData) !== serializePageSnapshot(lastCommittedPageData);
  }, [lastCommittedPageData, pageData]);

  const componentLibrary = useMemo(() => {
    return searchSectionLibrary(SECTION_LIBRARY, componentSearch);
  }, [componentSearch]);

  const undoLastChange = useCallback(() => {
    if (!undoStack.length || !pageData) {
      return;
    }
    const previousSnapshot = undoStack[undoStack.length - 1];
    setUndoStack((prevHistory) => prevHistory.slice(0, -1));
    setRedoStack((prevHistory) => [
      ...prevHistory.slice(-(EDIT_HISTORY_LIMIT - 1)),
      clonePageSnapshot(pageData),
    ]);
    setPageData(clonePageSnapshot(previousSnapshot));
  }, [pageData, undoStack]);

  const redoLastChange = useCallback(() => {
    if (!redoStack.length || !pageData) {
      return;
    }
    const nextSnapshot = redoStack[redoStack.length - 1];
    setRedoStack((prevHistory) => prevHistory.slice(0, -1));
    setUndoStack((prevHistory) => [
      ...prevHistory.slice(-(EDIT_HISTORY_LIMIT - 1)),
      clonePageSnapshot(pageData),
    ]);
    setPageData(clonePageSnapshot(nextSnapshot));
  }, [pageData, redoStack]);

  const rollbackUnsavedChanges = useCallback(() => {
    if (!lastCommittedPageData) {
      return;
    }
    if (!hasUnsavedChanges) {
      showSnackbar('No unsaved changes to roll back.', 'info');
      return;
    }
    const ok = window.confirm('Roll back all unsaved changes on this page?');
    if (!ok) {
      return;
    }
    setPageData(clonePageSnapshot(lastCommittedPageData));
    setUndoStack([]);
    setRedoStack([]);
    showSnackbar('Unsaved changes rolled back.', 'info');
  }, [hasUnsavedChanges, lastCommittedPageData, showSnackbar]);

  const resetToDefaultDesign = useCallback(() => {
    const ok = window.confirm('Reset this page to the new default design template?');
    if (!ok) {
      return;
    }
    applyPageMutation(createDefaultPage(slug), { trackHistory: true });
    showSnackbar('New default design applied. Review and click Save Changes.', 'info');
  }, [applyPageMutation, showSnackbar, slug]);

  const applyThemePreset = useCallback(
    (preset) => {
      applyPageMutation((prev) => applyThemePresetToPage(prev, preset));
      setThemeDialogOpen(false);
      showSnackbar(`${preset.label} applied. Review and save when ready.`, 'success');
    },
    [applyPageMutation, showSnackbar]
  );

  const loadPage = useCallback(async () => {
    if (!tenantKey || !slug || !pageSlug) {
      setLoading(false);
      setError('Invalid page URL.');
      return;
    }

    if (REMOVED_PUBLIC_PAGE_SLUGS.has(slug)) {
      setPageData(null);
      setLastCommittedPageData(null);
      setLoading(false);
      setError('Page not found.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      let resp;
      try {
        resp = await axiosPublic.get(`/api/${tenantKey}/pages/${pageSlug}`);
      } catch (err) {
        throw err;
      }
      const defaultPage = createDefaultPage(slug);
      let parsed = defaultPage;
      if (resp?.data?.content) {
        try {
          parsed = JSON.parse(resp.data.content);
        } catch (_parseError) {
          parsed = defaultPage;
        }
      }
      const merged = {
        ...defaultPage,
        ...parsed,
        heroHeight: clampNumber(parsed?.heroHeight, HERO_HEIGHT_MIN, HERO_HEIGHT_MAX, defaultPage.heroHeight),
        heroContentWidth: clampNumber(
          parsed?.heroContentWidth,
          HERO_CONTENT_WIDTH_MIN,
          HERO_CONTENT_WIDTH_MAX,
          defaultPage.heroContentWidth
        ),
        sectionSpacing: clampNumber(
          parsed?.sectionSpacing,
          SECTION_SPACING_MIN,
          SECTION_SPACING_MAX,
          defaultPage.sectionSpacing
        ),
      };
      const sourceSections = ensureHeroSectionInSections(merged);
      const normalizedSections = sourceSections
        .map((section) => normalizeSection(section));
      resetEditSession({ ...merged, sections: normalizedSections });
    } catch (err) {
      if (DEFAULT_CUSTOM_PAGE_SLUGS.has(slug)) {
        resetEditSession(createDefaultPage(slug));
        setError('');
      } else if (err?.response?.status === 404) {
        setError('Page not found.');
      } else {
        setError('Failed to load page.');
      }
    } finally {
      setLoading(false);
    }
  }, [pageSlug, resetEditSession, slug, tenantKey]);

  const loadAvailableLinks = useCallback(async () => {
    if (!tenantKey) {
      setAvailablePageLinks(STATIC_PAGE_LINKS);
      return;
    }

    try {
      let response;
      try {
        response = await axiosPublic.get(`/api/${tenantKey}/pages/custom_pages_index`);
      } catch (err) {
        throw err;
      }
      const rawContent = response?.data?.content;
      let parsed = {};

      if (typeof rawContent === 'string') {
        parsed = JSON.parse(rawContent || '{}');
      } else if (rawContent && typeof rawContent === 'object') {
        parsed = rawContent;
      }

      const customPages = Array.isArray(parsed?.pages) ? parsed.pages : [];
      const mappedCustomPages = customPages
        .map((pageItem) => {
          const normalizedPageSlug = normalizeSlug(pageItem?.slug || '');
          if (REMOVED_PUBLIC_PAGE_SLUGS.has(normalizedPageSlug)) {
            return null;
          }
          const mappedPath = pageItem?.path || `/page/${normalizedPageSlug}`;
          const validPath = String(mappedPath || '').trim();
          if (!validPath) {
            return null;
          }
          const normalizedPath = validPath.startsWith('/') ? validPath : `/${validPath}`;
          if (BLOCKED_INTERNAL_LINK_PATHS.has(normalizedPath)) {
            return null;
          }
          return {
            label: pageItem?.menuLabel || pageItem?.title || titleFromSlug(pageItem?.slug || ''),
            path: normalizedPath,
          };
        })
        .filter(Boolean);

      const uniqueLinkMap = new Map();
      [...STATIC_PAGE_LINKS, ...mappedCustomPages].forEach((link) => {
        if (link?.path && !uniqueLinkMap.has(link.path)) {
          uniqueLinkMap.set(link.path, link);
        }
      });

      setAvailablePageLinks(Array.from(uniqueLinkMap.values()));
    } catch (_error) {
      setAvailablePageLinks(STATIC_PAGE_LINKS);
    }
  }, [tenantKey]);

  useEffect(() => {
    const checkAdmin = () => setIsAdmin(isUserAuthenticated());
    checkAdmin();
    window.addEventListener('storage', checkAdmin);
    return () => window.removeEventListener('storage', checkAdmin);
  }, []);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    loadAvailableLinks();
  }, [loadAvailableLinks]);

  useEffect(() => {
    if (!tenantKey) {
      setTenantSchoolId('');
      return undefined;
    }

    let cancelled = false;

    const resolveTenantSchoolId = async () => {
      try {
        const response = await axiosPublic.get(`/api/${tenantKey}/pages/sidebar`, {
          skipGlobalLoader: true,
          skipGlobalError: true,
        });
        const resolvedId = response?.data?.sidebar?.school?.schoolId;
        if (!cancelled) {
          setTenantSchoolId(resolvedId ? String(resolvedId) : '');
        }
      } catch (_error) {
        if (!cancelled) {
          setTenantSchoolId('');
        }
      }
    };

    resolveTenantSchoolId();
    return () => {
      cancelled = true;
    };
  }, [tenantKey]);

  useEffect(() => {
    if (!isAdmin) {
      setEditMode(false);
      return;
    }

    const query = new URLSearchParams(location.search);
    if (query.get('edit') === '1') {
      setEditMode(true);
    }
  }, [isAdmin, location.search]);

  useEffect(() => {
    if (!editMode) {
      setEditModeNoticeVisible(false);
      return undefined;
    }

    setEditModeNoticeVisible(true);
    const timer = window.setTimeout(() => {
      setEditModeNoticeVisible(false);
    }, 2400);

    return () => window.clearTimeout(timer);
  }, [editMode]);

  useEffect(() => {
    if (isAdmin && editMode) {
      return undefined;
    }

    const sliderSections = (pageData?.sections || []).filter((section) => section?.type === 'slider');
    if (!sliderSections.length) {
      return undefined;
    }

    const intervalIds = sliderSections
      .map((section) => {
        const sectionKey = section.id || '';
        if (!sectionKey || section.sliderAutoPlay === false) {
          return null;
        }
        const sliderImages = normalizeStringList(section.galleryImages, section.image ? [section.image] : []);
        if (sliderImages.length < 2) {
          return null;
        }
        const intervalMs = clampNumber(section.sliderInterval, 1200, 10000, 3500);
        return window.setInterval(() => {
          setActiveSliderBySection((prev) => ({
            ...prev,
            [sectionKey]: ((Number(prev[sectionKey]) || 0) + 1) % sliderImages.length,
          }));
        }, intervalMs);
      })
      .filter(Boolean);

    return () => {
      intervalIds.forEach((intervalId) => window.clearInterval(intervalId));
    };
  }, [editMode, isAdmin, pageData?.sections]);

  useEffect(() => {
    if (!isAdmin || !editMode) {
      return undefined;
    }

    const handleKeydown = (event) => {
      if (!(event.ctrlKey || event.metaKey)) {
        return;
      }

      const key = String(event.key || '').toLowerCase();
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        undoLastChange();
        return;
      }
      if (key === 'y' || (key === 'z' && event.shiftKey)) {
        event.preventDefault();
        redoLastChange();
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [editMode, isAdmin, redoLastChange, undoLastChange]);

  useEffect(() => {
    setPortalReady(typeof document !== 'undefined');
  }, []);

  const updateSectionField = useCallback((index, field, value) => {
    applyPageMutation((prev) => {
      const sections = [...(prev?.sections || [])];
      const nextSection = { ...(sections[index] || {}), [field]: value };
      sections[index] = nextSection;
      return { ...prev, sections };
    });
  }, [applyPageMutation]);

  const openSectionEditor = useCallback(
    (index) => {
      const existingSection = pageData?.sections?.[index];
      if (!existingSection) {
        return;
      }
      setSectionEditor({
        open: true,
        index,
        draft: normalizeSection(existingSection),
      });
    },
    [pageData?.sections]
  );

  const handleSectionDoubleClick = useCallback((event, index) => {
    if (!isAdmin || !editMode) {
      return;
    }

    const interactiveTarget = event.target?.closest?.(
      'button, a, input, textarea, select, [role="button"], [contenteditable="true"]'
    );
    if (interactiveTarget) {
      return;
    }

    openSectionEditor(index);
  }, [editMode, isAdmin, openSectionEditor]);

  const closeSectionEditor = useCallback(() => {
    setSectionEditor({ open: false, index: -1, draft: null });
  }, []);

  const openInlineEditor = useCallback((event, index, field, value, mode = 'text') => {
    if (!isAdmin || !editMode) {
      return;
    }
    event?.stopPropagation?.();
    event?.preventDefault?.();
    setInlineEditor({
      index,
      field,
      value: typeof value === 'string' ? value : '',
      mode,
    });
  }, [editMode, isAdmin]);

  const closeInlineEditor = useCallback(() => {
    setInlineEditor({ index: -1, field: '', value: '', mode: 'text' });
  }, []);

  const isInlineEditing = useCallback(
    (index, field) => inlineEditor.index === index && inlineEditor.field === field,
    [inlineEditor.field, inlineEditor.index]
  );

  const saveSectionEditor = useCallback(async () => {
    if (sectionEditor.index < 0 || !sectionEditor.draft) {
      closeSectionEditor();
      return;
    }

    if (!effectiveSchoolId) {
      showSnackbar('School ID is missing. Login again to save.', 'error');
      return;
    }

    if (!pageSlug || !pageData) {
      showSnackbar('Nothing to save.', 'error');
      return;
    }

    const nextSections = [...(pageData.sections || [])];
    if (!nextSections[sectionEditor.index]) {
      closeSectionEditor();
      return;
    }

    nextSections[sectionEditor.index] = normalizeSection({
      ...nextSections[sectionEditor.index],
      ...sectionEditor.draft,
    });

    const nextPageData = {
      ...pageData,
      sections: nextSections,
    };

    setSaving(true);
    try {
      const payloadContent = {
        ...nextPageData,
        sections: Array.isArray(nextPageData.sections) ? nextPageData.sections : [],
      };
      const payload = {
        pageSlug,
        componentKey: 'customPage',
        content: JSON.stringify(payloadContent),
        schoolId: effectiveSchoolId,
      };

      await axiosPrivate.post(`/api/${effectiveSchoolId}/pages/${pageSlug}`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      resetEditSession(payloadContent);
      closeSectionEditor();
      showSnackbar('Section saved successfully.');
    } catch (error) {
      console.error('Save section failed:', error);
      showSnackbar('Failed to save section.', 'error');
    } finally {
      setSaving(false);
    }
  }, [
    closeSectionEditor,
    effectiveSchoolId,
    pageData,
    pageSlug,
    resetEditSession,
    sectionEditor.draft,
    sectionEditor.index,
    showSnackbar,
  ]);

  const addSection = useCallback((sectionType = 'text') => {
    applyPageMutation((prev) => ({
      ...prev,
      sections: [...(prev?.sections || []), normalizeSection(createSectionByType(sectionType))],
    }));
  }, [applyPageMutation]);

  const removeSection = useCallback((index) => {
    const ok = window.confirm('Delete this section?');
    if (!ok) {
      return;
    }

    applyPageMutation((prev) => ({
      ...prev,
      sections: (prev?.sections || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  }, [applyPageMutation]);

  const moveSection = useCallback((index, direction) => {
    applyPageMutation((prev) => {
      const sections = [...(prev?.sections || [])];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;

      if (targetIndex < 0 || targetIndex >= sections.length) {
        return prev;
      }

      const [moved] = sections.splice(index, 1);
      sections.splice(targetIndex, 0, moved);
      return { ...prev, sections };
    });
  }, [applyPageMutation]);

  const handleDragStart = useCallback((index) => {
    setDragIndex(index);
  }, []);

  const clearDragPressTimer = useCallback(() => {
    if (dragPressTimerRef.current) {
      window.clearTimeout(dragPressTimerRef.current);
      dragPressTimerRef.current = null;
    }
  }, []);

  const clearElementPressTimer = useCallback(() => {
    if (elementPressTimerRef.current) {
      window.clearTimeout(elementPressTimerRef.current);
      elementPressTimerRef.current = null;
    }
  }, []);

  const resetElementDrag = useCallback(() => {
    clearElementPressTimer();
    elementDragRef.current = {
      pointerId: null,
      index: -1,
      key: '',
      startX: 0,
      startY: 0,
      originX: 0,
      originY: 0,
      currentX: 0,
      currentY: 0,
    };
    setElementDragState({ index: -1, key: '', active: false, x: 0, y: 0 });
  }, [clearElementPressTimer]);

  const updateSectionElementPosition = useCallback((index, elementKey, x, y) => {
    applyPageMutation((prev) => {
      const sections = [...(prev?.sections || [])];
      if (!sections[index]) {
        return prev;
      }
      const existing = sections[index];
      sections[index] = {
        ...existing,
        elementPositions: {
          ...(existing.elementPositions || {}),
          [elementKey]: { x, y },
        },
      };
      return { ...prev, sections };
    });
  }, [applyPageMutation]);

  const getElementPosition = useCallback((section, index, elementKey) => {
    if (elementDragState.active && elementDragState.index === index && elementDragState.key === elementKey) {
      return { x: elementDragState.x, y: elementDragState.y };
    }

    const raw = section?.elementPositions?.[elementKey];
    return {
      x: Number(raw?.x) || 0,
      y: Number(raw?.y) || 0,
    };
  }, [elementDragState.active, elementDragState.index, elementDragState.key, elementDragState.x, elementDragState.y]);

  const handleElementPointerDown = useCallback((event, index, elementKey, position) => {
    if (!isAdmin || !editMode) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    clearElementPressTimer();
    elementDragRef.current = {
      pointerId: event.pointerId,
      index,
      key: elementKey,
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
      currentX: position.x,
      currentY: position.y,
    };

    elementPressTimerRef.current = window.setTimeout(() => {
      setElementDragState({
        index,
        key: elementKey,
        active: true,
        x: position.x,
        y: position.y,
      });
      showSnackbar('Element drag enabled. Move it and release to place it.', 'info');
      elementPressTimerRef.current = null;
    }, 320);
  }, [clearElementPressTimer, editMode, isAdmin, showSnackbar]);

  const handleElementPointerMove = useCallback((event) => {
    const dragMeta = elementDragRef.current;
    if (!elementDragState.active || dragMeta.pointerId !== event.pointerId) {
      return;
    }

    event.preventDefault();
    const nextX = Math.round(dragMeta.originX + (event.clientX - dragMeta.startX));
    const nextY = Math.round(dragMeta.originY + (event.clientY - dragMeta.startY));
    elementDragRef.current = {
      ...dragMeta,
      currentX: nextX,
      currentY: nextY,
    };
    setElementDragState((prev) => (
      prev.active
        ? {
            ...prev,
            x: nextX,
            y: nextY,
          }
        : prev
    ));
  }, [elementDragState.active]);

  const handleElementPointerUp = useCallback((event) => {
    const dragMeta = elementDragRef.current;
    event.currentTarget.releasePointerCapture?.(event.pointerId);

    if (
      elementDragState.active &&
      dragMeta.pointerId === event.pointerId &&
      dragMeta.index >= 0 &&
      dragMeta.key
    ) {
      updateSectionElementPosition(dragMeta.index, dragMeta.key, dragMeta.currentX, dragMeta.currentY);
      showSnackbar('Element position updated.', 'success');
    }

    resetElementDrag();
  }, [elementDragState.active, resetElementDrag, showSnackbar, updateSectionElementPosition]);

  const clearHeroCardPressTimer = useCallback(() => {
    if (heroCardPressTimerRef.current) {
      window.clearTimeout(heroCardPressTimerRef.current);
      heroCardPressTimerRef.current = null;
    }
  }, []);

  const resetHeroCardDrag = useCallback(() => {
    clearHeroCardPressTimer();
    heroCardDragRef.current = {
      pointerId: null,
      index: -1,
      startX: 0,
      startY: 0,
      originX: 0,
      originY: 0,
      currentX: 0,
      currentY: 0,
    };
    setHeroCardDragState({ index: -1, active: false, x: 0, y: 0 });
  }, [clearHeroCardPressTimer]);

  const handleHeroCardPointerDown = useCallback(
    (event, index, offsetX, offsetY) => {
      if (!isAdmin || !editMode) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture?.(event.pointerId);

      clearHeroCardPressTimer();
      heroCardDragRef.current = {
        pointerId: event.pointerId,
        index,
        startX: event.clientX,
        startY: event.clientY,
        originX: offsetX,
        originY: offsetY,
        currentX: offsetX,
        currentY: offsetY,
      };

      heroCardPressTimerRef.current = window.setTimeout(() => {
        setHeroCardDragState({
          index,
          active: true,
          x: offsetX,
          y: offsetY,
        });
        showSnackbar('Hero card drag enabled. Move it and release to place it.', 'info');
        heroCardPressTimerRef.current = null;
      }, 320);
    },
    [clearHeroCardPressTimer, editMode, isAdmin, showSnackbar]
  );

  const handleHeroCardPointerMove = useCallback((event) => {
    const dragMeta = heroCardDragRef.current;
    if (!heroCardDragState.active || dragMeta.pointerId !== event.pointerId) {
      return;
    }

    event.preventDefault();
    const nextX = Math.round(dragMeta.originX + (event.clientX - dragMeta.startX));
    const nextY = Math.round(dragMeta.originY + (event.clientY - dragMeta.startY));

    heroCardDragRef.current = {
      ...dragMeta,
      currentX: nextX,
      currentY: nextY,
    };

    setHeroCardDragState((prev) => (
      prev.active
        ? {
            ...prev,
            x: nextX,
            y: nextY,
          }
        : prev
    ));
  }, [heroCardDragState.active]);

  const handleHeroCardPointerUp = useCallback(
    (event) => {
      const dragMeta = heroCardDragRef.current;
      event.currentTarget.releasePointerCapture?.(event.pointerId);

      if (heroCardDragState.active && dragMeta.pointerId === event.pointerId && dragMeta.index >= 0) {
        updateSectionField(dragMeta.index, 'heroCardOffsetX', dragMeta.currentX);
        updateSectionField(dragMeta.index, 'heroCardOffsetY', dragMeta.currentY);
        showSnackbar('School At A Glance position updated.', 'success');
      }

      resetHeroCardDrag();
    },
    [heroCardDragState.active, resetHeroCardDrag, showSnackbar, updateSectionField]
  );

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setDragReadyIndex(null);
    clearDragPressTimer();
  }, [clearDragPressTimer]);

  const enableDragForSection = useCallback((index) => {
    setDragReadyIndex(index);
    showSnackbar('Drag mode enabled for this section.', 'info');
  }, [showSnackbar]);

  const startLongPressDrag = useCallback((index) => {
    clearDragPressTimer();
    dragPressTimerRef.current = window.setTimeout(() => {
      setDragReadyIndex(index);
      showSnackbar('Drag ready. Move this section now.', 'info');
      dragPressTimerRef.current = null;
    }, 320);
  }, [clearDragPressTimer, showSnackbar]);

  useEffect(() => () => clearDragPressTimer(), [clearDragPressTimer]);
  useEffect(() => () => resetHeroCardDrag(), [resetHeroCardDrag]);

  const handleDrop = useCallback((dropIndex) => {
    applyPageMutation((prev) => {
      if (dragIndex === null || dragIndex === dropIndex) {
        return prev;
      }

      const nextSections = [...(prev?.sections || [])];
      const [moved] = nextSections.splice(dragIndex, 1);
      nextSections.splice(dropIndex, 0, moved);
      return { ...prev, sections: nextSections };
    });
    setDragIndex(null);
    setDragReadyIndex(null);
    clearDragPressTimer();
  }, [applyPageMutation, clearDragPressTimer, dragIndex]);

  const openImageDialogFor = useCallback(
    async (target) => {
      if (!effectiveSchoolId) {
        showSnackbar('School ID is missing. Login again to edit images.', 'error');
        return;
      }

      setImageTarget(target);
      setImageDialogOpen(true);
      setLoadingImages(true);
      try {
        const resp = await axiosPrivate.get('/api/images/all', { headers: { schoolId: effectiveSchoolId } });
        setImages(resp.data || []);
      } catch (error) {
        console.error('Fetch images failed:', error);
        showSnackbar('Failed to load image library.', 'error');
      } finally {
        setLoadingImages(false);
      }
    },
    [effectiveSchoolId, showSnackbar]
  );

  const applyImageForTarget = useCallback(
    (target, value) => {
      if (!target) {
        return;
      }

      if (target.type === 'section') {
        updateSectionField(target.index, 'image', value);
        return;
      }

      if (target.type === 'section-video') {
        updateSectionField(target.index, 'videoUrl', value);
        return;
      }

      if (target.type === 'section-editor-image') {
        setSectionEditor((prev) => ({
          ...prev,
          draft: {
            ...(prev.draft || {}),
            image: value,
          },
        }));
        return;
      }

      if (target.type === 'section-editor-video') {
        setSectionEditor((prev) => ({
          ...prev,
          draft: {
            ...(prev.draft || {}),
            videoUrl: value,
          },
        }));
      }
    },
    [updateSectionField]
  );

  const handleSelectImage = useCallback(
    (url) => {
      applyImageForTarget(imageTarget, url);
      setImageDialogOpen(false);
    },
    [applyImageForTarget, imageTarget]
  );

  const persistPageContent = useCallback(async (nextPageData, successMessage = 'Custom page saved successfully.') => {
    if (!effectiveSchoolId) {
      showSnackbar('School ID is missing. Login again to save.', 'error');
      return false;
    }

    if (!pageSlug || !nextPageData) {
      showSnackbar('Nothing to save.', 'error');
      return false;
    }

    setSaving(true);
    try {
      const payloadContent = {
        ...nextPageData,
        sections: Array.isArray(nextPageData.sections) ? nextPageData.sections : [],
      };
      const payload = {
        pageSlug,
        componentKey: 'customPage',
        content: JSON.stringify(payloadContent),
        schoolId: effectiveSchoolId,
      };
      await axiosPrivate.post(`/api/${effectiveSchoolId}/pages/${pageSlug}`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      resetEditSession(payloadContent);
      showSnackbar(successMessage);
      return true;
    } catch (error) {
      console.error('Save custom page failed:', error);
      showSnackbar('Failed to save custom page.', 'error');
      return false;
    } finally {
      setSaving(false);
    }
  }, [effectiveSchoolId, pageSlug, resetEditSession, showSnackbar]);

  const saveInlineEditor = useCallback(async () => {
    if (inlineEditor.index < 0 || !inlineEditor.field || !pageData) {
      closeInlineEditor();
      return;
    }

    const nextSections = [...(pageData.sections || [])];
    if (!nextSections[inlineEditor.index]) {
      closeInlineEditor();
      return;
    }

    nextSections[inlineEditor.index] = {
      ...nextSections[inlineEditor.index],
      [inlineEditor.field]: inlineEditor.value,
    };

    const nextPageData = {
      ...pageData,
      sections: nextSections,
    };

    const didSave = await persistPageContent(nextPageData, 'Changes applied and saved.');
    if (didSave) {
      closeInlineEditor();
    }
  }, [
    closeInlineEditor,
    inlineEditor.field,
    inlineEditor.index,
    inlineEditor.value,
    pageData,
    persistPageContent,
  ]);

  const savePage = useCallback(async () => {
    await persistPageContent(pageData);
  }, [pageData, persistPageContent]);

  const openActionPath = useCallback(
    (rawPath) => {
      const pathValue = String(rawPath || '').trim();
      if (!pathValue) {
        return;
      }

      if (/^https?:\/\//i.test(pathValue)) {
        window.open(pathValue, '_blank', 'noopener,noreferrer');
        return;
      }

      const normalizedPath = pathValue.startsWith('/') ? pathValue : `/${pathValue}`;
      navigate(normalizedPath);
    },
    [navigate]
  );

  const renderHtmlTextBlock = useCallback((value, sx = {}) => {
    const content = value || '';
    if (looksLikeHtml(content)) {
      return (
        <Box
          sx={{
            color: 'text.secondary',
            lineHeight: 1.7,
            '& p': { mt: 0, mb: 1 },
            '& ul, & ol': { pl: 2.2, my: 0.5 },
            '& a': { color: 'primary.main' },
            ...sx,
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeUserHtml(content) }}
        />
      );
    }

    return (
      <Typography sx={{ whiteSpace: 'pre-line', color: 'text.secondary', lineHeight: 1.7, ...sx }}>
        {content}
      </Typography>
    );
  }, []);

  const renderInlineEditableField = useCallback(
    ({ index, field, value, mode = 'text', placeholder = '', display, wrapperSx = {}, helperText }) => {
      const editing = isInlineEditing(index, field);

      if (editing) {
        return (
          <Box
            sx={{
              border: '1px solid rgba(15,23,42,0.14)',
              backgroundColor: '#FFFFFF',
              p: 1,
              ...wrapperSx,
            }}
            onDoubleClick={(event) => event.stopPropagation()}
          >
            <Stack spacing={0.8}>
              {mode === 'rich' ? (
                <RichTextEditor
                  value={inlineEditor.value}
                  onChange={(nextHtml) =>
                    setInlineEditor((prev) => ({
                      ...prev,
                      value: nextHtml,
                    }))
                  }
                  placeholder={placeholder}
                />
              ) : (
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={field === 'title' ? 2 : 4}
                  value={inlineEditor.value}
                  placeholder={placeholder}
                  onChange={(event) =>
                    setInlineEditor((prev) => ({
                      ...prev,
                      value: event.target.value,
                    }))
                  }
                />
              )}
              <Stack direction="row" spacing={0.8} alignItems="center">
                <Button size="small" variant="contained" onClick={saveInlineEditor} sx={{ borderRadius: 0 }}>
                  Apply
                </Button>
                <Button size="small" variant="outlined" onClick={closeInlineEditor} sx={{ borderRadius: 0 }}>
                  Cancel
                </Button>
                {helperText ? (
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 700 }}>
                    {helperText}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </Box>
        );
      }

      return (
        <Box
          onDoubleClick={(event) => openInlineEditor(event, index, field, value, mode)}
          sx={{
            cursor: isAdmin && editMode ? 'text' : 'inherit',
            ...wrapperSx,
          }}
        >
          {display}
        </Box>
      );
    },
    [
      closeInlineEditor,
      editMode,
      inlineEditor.value,
      isAdmin,
      isInlineEditing,
      openInlineEditor,
      saveInlineEditor,
    ]
  );

  const renderPositionableElement = useCallback(
    ({ section, index, elementKey, children, wrapperSx = {}, showHandle = true }) => {
      const position = getElementPosition(section, index, elementKey);
      const active = elementDragState.active && elementDragState.index === index && elementDragState.key === elementKey;

      return (
        <Box
          sx={{
            position: 'relative',
            transform: `translate(${position.x}px, ${position.y}px)`,
            transition: active ? 'none' : 'transform 220ms ease',
            willChange: 'transform',
            ...wrapperSx,
          }}
        >
          {isAdmin && editMode && showHandle ? (
            <Box
              sx={{
                position: 'absolute',
                top: -10,
                right: -10,
                zIndex: 3,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.45,
                px: 0.45,
                py: 0.3,
                border: active ? '1px solid rgba(252,211,77,0.7)' : '1px solid rgba(15,23,42,0.14)',
                backgroundColor: active ? 'rgba(252,211,77,0.12)' : 'rgba(255,255,255,0.96)',
                cursor: active ? 'grabbing' : 'grab',
                touchAction: 'none',
                userSelect: 'none',
              }}
              onPointerDown={(event) => handleElementPointerDown(event, index, elementKey, position)}
              onPointerMove={handleElementPointerMove}
              onPointerUp={handleElementPointerUp}
              onPointerCancel={handleElementPointerUp}
            >
              <DragIndicatorIcon
                sx={{
                  fontSize: 14,
                  color: active ? '#D97706' : '#334155',
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: active ? '#92400E' : '#334155',
                  whiteSpace: 'nowrap',
                }}
              >
                {active ? 'Move' : 'Hold'}
              </Typography>
            </Box>
          ) : null}
          {children}
        </Box>
      );
    },
    [
      editMode,
      elementDragState.active,
      elementDragState.index,
      elementDragState.key,
      getElementPosition,
      handleElementPointerDown,
      handleElementPointerMove,
      handleElementPointerUp,
      isAdmin,
    ]
  );

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!pageData) {
    return (
      <Container maxWidth="xl" sx={{ py: WEB_LAYOUT.sectionPy }}>
        <Alert severity="error">{error || 'Unable to load page.'}</Alert>
      </Container>
    );
  }

  const sectionSpacing = clampNumber(
    pageData.sectionSpacing,
    SECTION_SPACING_MIN,
    SECTION_SPACING_MAX,
    SECTION_SPACING_DEFAULT
  );
  const sectionDraft = sectionEditor.draft || null;
  const sectionDraftTabs = normalizeTabItems(sectionDraft?.tabs);
  const sectionEditorDialogConstants = {
    SECTION_TITLE_FONT_MIN,
    SECTION_TITLE_FONT_MAX,
    SECTION_TITLE_FONT_DEFAULT,
    SECTION_TITLE_COLOR_DEFAULT,
    SECTION_TITLE_BG_DEFAULT,
    PROFILE_IMAGE_SIZE_MIN,
    PROFILE_IMAGE_SIZE_MAX,
    PROFILE_IMAGE_SIZE_DEFAULT,
    MEDIA_HEIGHT_MIN,
    MEDIA_HEIGHT_MAX,
    MEDIA_HEIGHT_DEFAULT,
    HERO_HEIGHT_MIN,
    HERO_HEIGHT_MAX,
    HERO_HEIGHT_DEFAULT,
    HERO_CONTENT_WIDTH_MIN,
    HERO_CONTENT_WIDTH_MAX,
    HERO_CONTENT_WIDTH_DEFAULT,
    SECTION_WIDTH_MIN,
    SECTION_WIDTH_MAX,
    SECTION_WIDTH_DEFAULT,
    COMPONENT_MARGIN_MIN,
    COMPONENT_MARGIN_MAX,
    COMPONENT_MARGIN_DEFAULT,
    SECTION_BG_DEFAULT,
  };
  const pageBackgrounds = {
    home: '#FFFFFF',
    about: '#FFFFFF',
    academics: '#FFFFFF',
    admissions: '#FFFFFF',
    facilities: '#FFFFFF',
    contact: '#FFFFFF',
    results: '#FFFFFF',
  };
  const pageBackground = pageBackgrounds[slug] || '#FFFFFF';

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          pb: 3.5,
          background: pageBackground,
        }}
      >
        {error && (
          <Container maxWidth="xl" sx={{ pt: { xs: 0.9, md: 1.1 } }}>
            <Alert severity={error.includes('no saved content') ? 'info' : 'error'} sx={{ mb: 1.2 }}>
              {error}
            </Alert>
          </Container>
        )}

        {(pageData.sections || []).map((section, index) => {
        const sectionKey = section.id || `section-${index}`;
        const sectionWidth = clampNumber(
          section.sectionWidth,
          SECTION_WIDTH_MIN,
          SECTION_WIDTH_MAX,
          SECTION_WIDTH_DEFAULT
        );
        const sectionMarginTop = clampNumber(
          section.sectionMarginTop,
          COMPONENT_MARGIN_MIN,
          COMPONENT_MARGIN_MAX,
          section.type === 'hero' || section.type === 'divider' ? 0 : COMPONENT_MARGIN_DEFAULT
        );
        const sectionMarginBottom = clampNumber(
          section.sectionMarginBottom,
          COMPONENT_MARGIN_MIN,
          COMPONENT_MARGIN_MAX,
          section.type === 'hero' || section.type === 'divider' ? 0 : COMPONENT_MARGIN_DEFAULT
        );
        const sectionTabs = normalizeTabItems(section.tabs);
        const activeTabIndex = clampNumber(
          activeTabsBySection[sectionKey],
          0,
          Math.max(sectionTabs.length - 1, 0),
          0
        );
        const sectionVideo = section.videoUrl || '';
        const isSectionVideoDirect = isDirectVideoUrl(sectionVideo);
        const isHeroComponent = section.type === 'hero';
        const hasMedia = Boolean(section.image) || Boolean(sectionVideo);
        const imageSpan = hasMedia ? clampNumber(section.imageColumnSpan, 2, 10, 5) : 0;
        const textSpan = hasMedia ? 12 - imageSpan : 12;
        const imageOnLeft = section.imagePosition === 'left';
        const isMediaOnly = section.type === 'image_only';
        const usesDedicatedSectionLayout = [
          'image',
          'image_read_more',
          'tabs',
          'quote',
          'image_grid',
          'map',
          'contact_form',
          'admissions_form',
          'footer',
        ].includes(section.type);
        const buttonEnabled = Boolean(section.buttonEnabled);
        const buttonVariant = ['contained', 'outlined', 'text'].includes(section.buttonVariant)
          ? section.buttonVariant
          : 'outlined';
        const buttonBgColor = section.buttonBgColor || DEMO_THEME.accent;
        const buttonTextColor =
          section.buttonTextColor || (buttonVariant === 'contained' ? '#FFFFFF' : buttonBgColor);
        const mediaHeight = clampNumber(
          section.mediaHeight,
          MEDIA_HEIGHT_MIN,
          MEDIA_HEIGHT_MAX,
          MEDIA_HEIGHT_DEFAULT
        );
        const imageFrameShape = ['rectangle', 'rounded'].includes(section.imageFrameShape)
          ? section.imageFrameShape
          : 'rounded';
        const imageCornerRadius = clampNumber(section.imageCornerRadius, 0, 40, 14);
        const mediaBorderWidth = clampNumber(section.imageBorderWidth, 0, 12, 0);
        const animationStyle = ['none', 'fade-up', 'zoom-in'].includes(section.animationStyle)
          ? section.animationStyle
          : 'fade-up';
        const animationDuration = clampNumber(section.animationDuration, 300, 2200, 900);
        const animationDelay = clampNumber(section.animationDelay, 0, 1200, index * 90);
        const shouldAnimate = animationStyle !== 'none' && !(isAdmin && editMode);
        const dropdownOptions = normalizeDropdownOptions(section.dropdownOptions);
        const selectedDropdown = activeDropdownBySection[sectionKey] || '';
        const dropdownPlaceholder = section.dropdownPlaceholder || '';
        const tableColumns = normalizeTableColumns(section.tableColumns);
        const tableRows = normalizeTableRows(section.tableRows, tableColumns.length);
        const dividerThickness = clampNumber(section.dividerThickness, 1, 10, 3);
        const dividerColor = section.dividerColor || DEMO_THEME.accent;
        const dividerStyle = ['solid', 'dashed', 'dotted', 'double'].includes(section.dividerStyle)
          ? section.dividerStyle
          : 'solid';
        const listItems = String(section.body || '')
          .split('\n')
          .map((item) => item.trim())
          .filter(Boolean);
        const galleryImages = normalizeStringList(section.galleryImages, []);
        const galleryColumns = clampNumber(section.galleryColumns, 2, 6, 3);
        const sliderImages = normalizeStringList(section.galleryImages, section.image ? [section.image] : []);
        const sliderCurrentIndex = clampNumber(
          activeSliderBySection[sectionKey],
          0,
          Math.max(sliderImages.length - 1, 0),
          0
        );
        const iconCards = normalizeIconCards(section.iconCards, []);
        const iconGridColumns = clampNumber(section.iconGridColumns, 1, 4, 3);
        const resultCards = normalizeResultCards(section.resultCards, []);
        const resultsGridColumns = clampNumber(section.resultsGridColumns, 1, 4, 3);
        const resultsMetricMode = ['percentage', 'marks', 'both'].includes(section.resultsMetricMode)
          ? section.resultsMetricMode
          : 'percentage';
        const careersLayout = ['grid', 'vertical'].includes(section.careersLayout) ? section.careersLayout : 'grid';
        const careersColumns = clampNumber(section.careersColumns, 1, 4, 3);
        const careerCards = normalizeCareerCards(section.careerCards, []);
        const socialLinks = normalizeSocialLinks(section.socialLinks, []);
        const facultyCards = normalizeProfileCards(section.facultyCards, []);
        const founderCards = normalizeProfileCards(section.founderCards, []);
        const profileCards = normalizeProfileCards(section.profileCards, []);
        const facultyColumns = clampNumber(section.facultyColumns, 1, 4, 3);
        const founderColumns = clampNumber(section.founderColumns, 1, 4, 2);
        const profileColumns = clampNumber(section.profileColumns, 1, 4, 4);
        const statsItems = normalizeStatsItems(section.statsItems, []);
        const sidebarItems = normalizeSidebarItems(section.sidebarItems, []);
        const sidebarButtons = normalizeSidebarItems(section.sidebarButtons, []);
        const readMoreLimit = clampNumber(section.readMoreLimit, 80, 1200, 220);
        const readMoreLabel = section.readMoreLabel || '';
        const readLessLabel = section.readLessLabel || '';
        const subscribePlaceholder = section.subscribePlaceholder || '';
        const subscribeButtonLabel = section.subscribeButtonLabel || '';
        const subscribeSuccessMessage = section.subscribeSuccessMessage || '';
        const readMoreSource = String(section.body || '');
        const readMoreExpanded = Boolean(expandedReadMoreBySection[sectionKey]);
        const readMoreCanCollapse =
          section.type === 'image_read_more' &&
          !looksLikeHtml(readMoreSource) &&
          readMoreSource.length > readMoreLimit;
        const readMoreDisplay =
          readMoreCanCollapse && !readMoreExpanded
            ? `${readMoreSource.slice(0, readMoreLimit).trimEnd()}...`
            : readMoreSource;
        const footerLinks = normalizeStringList(section.footerQuickLinks, []);
        const comboShowImage = section.comboShowImage !== false;
        const comboShowTable = section.comboShowTable !== false;
        const comboShowButton = section.comboShowButton !== false;
        const sectionTitleColor = normalizeHexColor(section.sectionTitleColor, SECTION_TITLE_COLOR_DEFAULT);
        const sectionTitleBgColor = normalizeHexColor(section.sectionTitleBgColor, SECTION_TITLE_BG_DEFAULT);
        const sectionTitleBgEnabled = Boolean(section.sectionTitleBgEnabled);
        const sectionTitleAlign = ['left', 'center', 'right'].includes(section.sectionTitleAlign)
          ? section.sectionTitleAlign
          : 'left';
        const sectionTitleFontSize = clampNumber(
          section.sectionTitleFontSize,
          SECTION_TITLE_FONT_MIN,
          SECTION_TITLE_FONT_MAX,
          SECTION_TITLE_FONT_DEFAULT
        );
        const profileImageShape = ['circle', 'rectangle', 'square', 'rounded'].includes(section.profileImageShape)
          ? section.profileImageShape
          : 'rectangle';
        const profileImageAlign = ['left', 'center'].includes(section.profileImageAlign)
          ? section.profileImageAlign
          : 'left';
        const profileImageSize = clampNumber(
          section.profileImageSize,
          PROFILE_IMAGE_SIZE_MIN,
          PROFILE_IMAGE_SIZE_MAX,
          PROFILE_IMAGE_SIZE_DEFAULT
        );
        const profileImageObjectPosition = section.profileImageObjectPosition || 'center center';
        const profileImageRadius =
          profileImageShape === 'circle'
            ? '50%'
            : profileImageShape === 'rectangle' || profileImageShape === 'square'
              ? 0
              : profileImageShape === 'rounded'
                ? 14
                : '42% 58% 60% 40% / 44% 36% 64% 56%';
        const sectionSurfaceSx = getSectionSurfaceSx(section);
        const componentManagedTitleTypes = new Set([
          'image',
          'image_read_more',
          'tabs',
          'quote',
          'image_grid',
          'map',
          'contact_form',
          'admissions_form',
          'footer',
          'icon_grid',
          'stats_strip',
          'sidebar_content',
          'news_highlights',
          'profile_showcase',
          'student_results_grid',
          'social_links',
          'slider',
          'experience_story',
        ]);
        const shouldRenderSharedSectionTitle =
          Boolean(section.title) && !componentManagedTitleTypes.has(section.type);

        if (isHeroComponent) {
          const heroHeight = clampNumber(section.heroHeight, HERO_HEIGHT_MIN, HERO_HEIGHT_MAX, HERO_HEIGHT_DEFAULT);
          const heroContentWidth = clampNumber(
            section.heroContentWidth,
            HERO_CONTENT_WIDTH_MIN,
            HERO_CONTENT_WIDTH_MAX,
            HERO_CONTENT_WIDTH_DEFAULT
          );
          const heroMediaPosition = ['center center', 'center top', 'center bottom'].includes(section.imageObjectPosition)
            ? section.imageObjectPosition
            : 'center center';
          const heroVideo = section.videoUrl || '';
          const directHeroVideo = isDirectVideoUrl(heroVideo);
          const heroTitle = section.title || '';
          const heroSubtitle = section.body || '';
          const heroPrimaryActionLabel = section.buttonLabel || '';
          const heroPrimaryActionPath = section.buttonPath || '';
          const heroSecondaryActionPath = '/contact';
          const heroImage = section.image || '';
          const storedSchoolName =
            typeof window !== 'undefined' ? String(localStorage.getItem('schoolName') || '').trim() : '';
          const shouldHideSchoolNameTitle =
            Boolean(heroTitle) &&
            Boolean(storedSchoolName) &&
            heroTitle.trim().toLowerCase() === storedSchoolName.toLowerCase();
          const heroDisplayTitle = shouldHideSchoolNameTitle ? '' : heroTitle;

          return (
            <Box
              key={sectionKey}
              draggable={Boolean(isAdmin && editMode && dragReadyIndex === index)}
              onDragStart={() => handleDragStart(index)}
              onDragEnd={handleDragEnd}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => handleDrop(index)}
              onDoubleClick={(event) => handleSectionDoubleClick(event, index)}
              sx={{
                position: 'relative',
                background: 'transparent',
                mt: `${sectionMarginTop}px`,
                mb: `${sectionMarginBottom}px`,
                opacity: shouldAnimate ? 0 : 1,
                animation: shouldAnimate
                  ? `${
                      animationStyle === 'zoom-in' ? 'sectionZoomInReveal' : 'sectionFadeUpReveal'
                    } ${animationDuration}ms ease ${animationDelay}ms forwards`
                  : 'none',
                '@keyframes sectionFadeUpReveal': {
                  '0%': { opacity: 0, transform: 'translate3d(0, 12px, 0)' },
                  '100%': { opacity: 1, transform: 'translate3d(0, 0, 0)' },
                },
                '@keyframes sectionZoomInReveal': {
                  '0%': { opacity: 0, transform: 'scale(0.98)' },
                  '100%': { opacity: 1, transform: 'scale(1)' },
                },
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: `${heroHeight}px`,
                  minHeight: `${heroHeight}px`,
                  maxHeight: `${heroHeight}px`,
                  overflow: 'hidden',
                  background:
                    'linear-gradient(180deg, #FFF8F1 0%, #FFFFFF 54%, #F9FBFF 100%)',
                  borderRadius: { xs: 24, md: 32 },
                }}
              >
                {heroVideo ? (
                  directHeroVideo ? (
                    <Box
                      component="video"
                      src={heroVideo}
                      autoPlay
                      muted
                      loop
                      playsInline
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: heroMediaPosition,
                      }}
                    />
                  ) : (
                    <Box
                      component="iframe"
                      src={heroVideo}
                      title={heroTitle}
                      allow="autoplay; encrypted-media; picture-in-picture"
                      allowFullScreen
                      sx={{ width: '100%', height: '100%', border: 0, pointerEvents: 'none' }}
                    />
                  )
                ) : (
                  <Box
                    component="img"
                    src={heroImage}
                    alt={heroTitle}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: heroMediaPosition,
                      transform: 'scale(1.01)',
                    }}
                  />
                )}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(90deg, rgba(255,248,241,0.96) 0%, rgba(255,252,247,0.9) 36%, rgba(255,255,255,0.58) 62%, rgba(255,255,255,0.06) 100%)',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.02) 26%, rgba(15,23,42,0.08) 100%)',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: { xs: 26, md: 34 },
                    right: { xs: '-4%', md: '6%' },
                    width: { xs: 180, md: 240 },
                    height: { xs: 180, md: 240 },
                    borderRadius: '34px',
                    background: 'radial-gradient(circle, rgba(245,184,72,0.22) 0%, rgba(245,184,72,0.08) 40%, rgba(245,184,72,0) 72%)',
                    pointerEvents: 'none',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: { xs: 18, md: 28 },
                    left: { xs: '-8%', md: '0%' },
                    width: { xs: 140, md: 200 },
                    height: { xs: 140, md: 200 },
                    borderRadius: '28px',
                    background: 'radial-gradient(circle, rgba(59,130,246,0.16) 0%, rgba(59,130,246,0.08) 40%, rgba(59,130,246,0) 72%)',
                    pointerEvents: 'none',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: { xs: 22, md: 26 },
                    left: { xs: 22, md: 30 },
                    width: { xs: 54, md: 68 },
                    height: 4,
                    borderRadius: 6,
                    bgcolor: '#D97706',
                    pointerEvents: 'none',
                  }}
                />
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: '100%', py: { xs: 5.5, md: 6.25 } }}>
                    <Container maxWidth={false} disableGutters sx={{ px: { xs: 2, md: 5, xl: 7 } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: { xs: 2.6, md: 4.4 },
                          flexDirection: { xs: 'column', lg: 'row' },
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: '100%', lg: `${Math.min(heroContentWidth, 62)}%` },
                            maxWidth: 880,
                          }}
                        >
                          <Stack spacing={{ xs: 1.4, md: 1.9 }}>
                            <Typography
                              sx={{
                                color: '#B45309',
                                fontSize: { xs: '0.82rem', md: '0.88rem' },
                                fontWeight: 800,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                              }}
                            >
                              School Admissions
                            </Typography>
                            {heroDisplayTitle ? renderInlineEditableField({
                              index,
                              field: 'title',
                              value: section.title || '',
                              mode: 'text',
                              placeholder: 'Hero title',
                              helperText: 'Double-click text blocks to edit in place.',
                              display: (
                                renderPositionableElement({
                                  section,
                                  index,
                                  elementKey: 'hero-title',
                                  showHandle: true,
                                  children: (
                                    <Typography
                                      sx={{
                                        color: '#0F172A',
                                        fontSize: { xs: '1.75rem', md: '2.95rem' },
                                        lineHeight: { xs: 1.14, md: 1.02 },
                                        fontWeight: 850,
                                        letterSpacing: '-0.04em',
                                        maxWidth: 760,
                                      }}
                                    >
                                      {heroDisplayTitle}
                                    </Typography>
                                  ),
                                })
                              ),
                            }) : null}
                            {renderInlineEditableField({
                              index,
                              field: 'body',
                              value: section.body || '',
                              mode: 'rich',
                              placeholder: 'Hero subtitle',
                              display: heroSubtitle
                                ? renderPositionableElement({
                                    section,
                                    index,
                                    elementKey: 'hero-body',
                                    showHandle: true,
                                    children: renderHtmlTextBlock(heroSubtitle, {
                                      maxWidth: { xs: '100%', md: '70%' },
                                      color: '#475569',
                                      '& p': { mt: 0, mb: 0.46 },
                                      '& a': { color: '#B45309' },
                                      fontSize: { xs: '0.96rem', md: '1.02rem' },
                                      lineHeight: 1.78,
                                    }),
                                  })
                                : (
                                  <Typography sx={{ color: '#64748B', fontSize: { xs: '0.96rem', md: '1rem' } }}>
                                    Double-click to add hero description
                                  </Typography>
                                ),
                            })}
                            <Stack direction="row" spacing={1.1} useFlexGap flexWrap="wrap" sx={{ pt: 0.4 }}>
                              <Button
                                size="medium"
                                variant="contained"
                                sx={{
                                  background: 'linear-gradient(135deg, #A1181F 0%, #C53030 100%)',
                                  color: '#FFFFFF',
                                  fontWeight: 800,
                                  px: 2.4,
                                  py: 1.05,
                                  borderRadius: 6,
                                  boxShadow: '0 14px 28px rgba(161,24,31,0.28)',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #8F1217 0%, #A1181F 100%)',
                                    boxShadow: '0 18px 32px rgba(161,24,31,0.3)',
                                  },
                                }}
                                onClick={() => openActionPath(heroPrimaryActionPath)}
                              >
                                {heroPrimaryActionLabel}
                              </Button>
                              <Button
                                size="medium"
                                variant="outlined"
                                sx={{
                                  borderColor: 'rgba(15,23,42,0.18)',
                                  color: '#0F172A',
                                  fontWeight: 700,
                                  px: 2.2,
                                  py: 1.05,
                                  borderRadius: 6,
                                  backgroundColor: 'rgba(255,255,255,0.72)',
                                  '&:hover': {
                                    borderColor: 'rgba(15,23,42,0.28)',
                                    backgroundColor: '#FFFFFF',
                                  },
                                }}
                                onClick={() => openActionPath(heroSecondaryActionPath)}
                              >
                                Contact School
                              </Button>
                            </Stack>
                          </Stack>
                        </Box>
                        <Box
                          sx={{
                            width: { xs: '100%', lg: '34%' },
                            display: { xs: 'none', lg: 'flex' },
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <Box
                            sx={{
                              width: '100%',
                              maxWidth: 460,
                              position: 'relative',
                            }}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: '14px -10px -10px 16px',
                                borderRadius: 36,
                                background: 'linear-gradient(180deg, rgba(245,184,72,0.18) 0%, rgba(245,184,72,0.04) 100%)',
                              }}
                            />
                            <Stack
                              spacing={1.35}
                              sx={{
                                position: 'relative',
                                p: 1.2,
                                borderRadius: 30,
                                background: 'rgba(255,255,255,0.82)',
                                border: '1px solid rgba(15,23,42,0.08)',
                                backdropFilter: 'blur(10px)',
                                boxShadow: '0 18px 34px rgba(15,23,42,0.12)',
                              }}
                            >
                              <Box
                                component="img"
                                src={heroImage}
                                alt={heroTitle}
                                sx={{
                                  width: '100%',
                                  height: 300,
                                  objectFit: 'cover',
                                  objectPosition: heroMediaPosition,
                                  borderRadius: 6,
                                }}
                              />
                              <Grid container spacing={1.1}>
                                {[
                                  { label: 'Campus Visits', value: 'Open' },
                                  { label: 'Admissions', value: 'Active' },
                                  { label: 'Curriculum', value: 'School-led' },
                                ].map((item) => (
                                  <Grid item xs={4} key={item.label}>
                                    <Box
                                      sx={{
                                        p: 1.15,
                                        borderRadius: 6,
                                        backgroundColor: '#FFF8F1',
                                        border: '1px solid rgba(217,119,6,0.08)',
                                        textAlign: 'center',
                                      }}
                                    >
                                      <Typography sx={{ color: '#64748B', fontSize: '0.68rem', fontWeight: 700 }}>
                                        {item.label}
                                      </Typography>
                                      <Typography sx={{ color: '#0F172A', fontSize: '0.88rem', fontWeight: 800, mt: 0.35 }}>
                                        {item.value}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                            </Stack>
                          </Box>
                        </Box>
                      </Box>
                    </Container>
                  </Box>
                </Box>

                {isAdmin && editMode && (
                  <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        px: 0.7,
                        py: 0.4,
                        borderRadius: 1.6,
                        border: '1px solid rgba(255,255,255,0.22)',
                        background: 'rgba(8,17,32,0.64)',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      <Stack direction="row" spacing={0.3}>
                        <Button
                          size="small"
                          variant="text"
                          color="inherit"
                          onClick={() => openSectionEditor(index)}
                          sx={{ minWidth: 0, px: 0.8, color: '#FFFFFF' }}
                        >
                          <EditIcon fontSize="small" />
                        </Button>
                        <IconButton
                          size="small"
                          color="inherit"
                          onMouseDown={() => enableDragForSection(index)}
                          onTouchStart={() => startLongPressDrag(index)}
                          onTouchEnd={clearDragPressTimer}
                          onTouchCancel={clearDragPressTimer}
                          sx={{
                            borderRadius: 1.2,
                            border: dragReadyIndex === index ? '1px solid rgba(252,211,77,0.72)' : '1px solid rgba(255,255,255,0.16)',
                            color: dragReadyIndex === index ? '#FCD34D' : '#FFFFFF',
                          }}
                        >
                          <DragIndicatorIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="inherit" onClick={() => moveSection(index, 'up')} disabled={index === 0} sx={{ borderRadius: 1.2 }}>
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="inherit"
                          onClick={() => moveSection(index, 'down')}
                          disabled={index === (pageData.sections || []).length - 1}
                          sx={{ borderRadius: 1.2 }}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => removeSection(index)} sx={{ borderRadius: 1.2 }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Paper>
                  </Box>
                )}
              </Box>
            </Box>
          );
        }

        return (
          <Box
            key={sectionKey}
            draggable={Boolean(isAdmin && editMode && dragReadyIndex === index)}
            onDragStart={() => handleDragStart(index)}
            onDragEnd={handleDragEnd}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => handleDrop(index)}
            onDoubleClick={(event) => handleSectionDoubleClick(event, index)}
            sx={{
              background: 'transparent',
              mt: `${sectionMarginTop}px`,
              mb: `${sectionMarginBottom}px`,
              opacity: shouldAnimate ? 0 : 1,
              animation: shouldAnimate
                ? `${
                    animationStyle === 'zoom-in' ? 'sectionZoomInReveal' : 'sectionFadeUpReveal'
                  } ${animationDuration}ms ease ${animationDelay}ms forwards`
                : 'none',
              '@keyframes sectionFadeUpReveal': {
                '0%': { opacity: 0, transform: 'translate3d(0, 18px, 0)' },
                '100%': { opacity: 1, transform: 'translate3d(0, 0, 0)' },
              },
              '@keyframes sectionZoomInReveal': {
                '0%': { opacity: 0, transform: 'scale(0.97)' },
                '100%': { opacity: 1, transform: 'scale(1)' },
              },
            }}
          >
            <Container maxWidth="xl" sx={{ py: sectionSpacing, px: { xs: 0, md: 0 } }}>
              <Box
                sx={{
                  width: { xs: '100%', md: `${sectionWidth}%` },
                  mx: 'auto',
                  transition: 'width 0.24s ease',
                }}
              >
                {isAdmin && editMode && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.55 }}>
                    <Stack
                      direction="row"
                      spacing={0.35}
                      sx={{
                        px: 0.45,
                        py: 0.35,
                        borderRadius: 1.6,
                        border: '1px solid rgba(15,23,42,0.1)',
                        background: 'rgba(255,255,255,0.86)',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      <IconButton size="small" onClick={() => openSectionEditor(index)} sx={{ borderRadius: 1.2 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onMouseDown={() => enableDragForSection(index)}
                        onTouchStart={() => startLongPressDrag(index)}
                        onTouchEnd={clearDragPressTimer}
                        onTouchCancel={clearDragPressTimer}
                        sx={{
                          border: dragReadyIndex === index ? '1px solid rgba(15,23,42,0.4)' : '1px solid rgba(15,23,42,0.12)',
                          borderRadius: 1.2,
                          color: dragReadyIndex === index ? '#0F172A' : 'text.secondary',
                          backgroundColor: dragReadyIndex === index ? 'rgba(15,23,42,0.06)' : '#FFFFFF',
                        }}
                      >
                        <DragIndicatorIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => moveSection(index, 'up')} disabled={index === 0} sx={{ borderRadius: 1.2 }}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => moveSection(index, 'down')}
                        disabled={index === (pageData.sections || []).length - 1}
                        sx={{ borderRadius: 1.2 }}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => removeSection(index)} sx={{ borderRadius: 1.2 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                )}

                <Paper
                  elevation={0}
                  sx={{
                    ...sectionSurfaceSx,
                  }}
                >
                  {section.type === 'divider' ? (
                    <Box sx={{ py: { xs: 0.5, md: 0.8 } }}>
                      {section.dividerLabel ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                          <Box
                            sx={{
                              flex: 1,
                              borderTop: `${dividerThickness}px ${dividerStyle} ${dividerColor}`,
                            }}
                          />
                          <Typography
                            sx={{
                              px: 1,
                              color: dividerColor,
                              fontWeight: 700,
                              fontSize: { xs: '0.78rem', md: '0.84rem' },
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {section.dividerLabel}
                          </Typography>
                          <Box
                            sx={{
                              flex: 1,
                              borderTop: `${dividerThickness}px ${dividerStyle} ${dividerColor}`,
                            }}
                          />
                        </Box>
                      ) : (
                        <Box sx={{ borderTop: `${dividerThickness}px ${dividerStyle} ${dividerColor}` }} />
                      )}
                    </Box>
                  ) : usesDedicatedSectionLayout ? (
                    <Box>
                      {shouldRenderSharedSectionTitle ? (
                        <Box
                          sx={{
                            mb: 0.7,
                            display: 'flex',
                            justifyContent:
                              sectionTitleAlign === 'center'
                                ? 'center'
                                : sectionTitleAlign === 'right'
                                  ? 'flex-end'
                                  : 'flex-start',
                          }}
                        >
                          {renderInlineEditableField({
                            index,
                            field: 'title',
                            value: section.title || '',
                            mode: 'text',
                            placeholder: 'Section title',
                            display: (
                              renderPositionableElement({
                                section,
                                index,
                                elementKey: `section-title-${sectionKey}`,
                                showHandle: true,
                                children: (
                                  <Typography
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: {
                                        xs: `${Math.max(0.72, (sectionTitleFontSize - 5) / 16)}rem`,
                                        md: `${sectionTitleFontSize / 16}rem`,
                                      },
                                      color: sectionTitleColor,
                                      textAlign: sectionTitleAlign,
                                      px: sectionTitleBgEnabled ? 0.9 : 0,
                                      py: sectionTitleBgEnabled ? 0.4 : 0,
                                      borderRadius: sectionTitleBgEnabled ? 1 : 0,
                                      backgroundColor: sectionTitleBgEnabled ? sectionTitleBgColor : 'transparent',
                                      width: sectionTitleAlign === 'left' ? 'auto' : 'fit-content',
                                    }}
                                  >
                                    {section.title}
                                  </Typography>
                                ),
                              })
                            ),
                          })}
                        </Box>
                      ) : null}

                      {section.type === 'image' || section.type === 'image_read_more' ? (
                        <SplitMediaStorySection
                          section={section}
                          sectionKey={sectionKey}
                          sectionIndex={index}
                          mediaHeight={mediaHeight}
                          bodyContent={renderInlineEditableField({
                            index,
                            field: 'body',
                            value: section.body || '',
                            mode: 'rich',
                            placeholder: 'Section content',
                            display: (section.type === 'image_read_more' ? readMoreDisplay : section.body)
                              ? renderPositionableElement({
                                  section,
                                  index,
                                  elementKey: `${sectionKey}-story-body`,
                                  children: renderHtmlTextBlock(
                                    section.type === 'image_read_more' ? readMoreDisplay : section.body,
                                    {
                                      color: 'text.secondary',
                                      lineHeight: 1.8,
                                      fontSize: { xs: '0.86rem', md: '0.92rem' },
                                    }
                                  ),
                                })
                              : (
                                <Typography sx={{ color: 'text.secondary', fontSize: '0.84rem' }}>
                                  Double-click to add section content
                                </Typography>
                              ),
                          })}
                          canCollapse={section.type === 'image_read_more' ? readMoreCanCollapse : false}
                          isExpanded={section.type === 'image_read_more' ? readMoreExpanded : true}
                          readMoreLabel={readMoreLabel}
                          readLessLabel={readLessLabel}
                          onToggleExpand={() =>
                            setExpandedReadMoreBySection((prev) => ({
                              ...prev,
                              [sectionKey]: !Boolean(prev[sectionKey]),
                            }))
                          }
                          renderPositionableElement={renderPositionableElement}
                        />
                      ) : section.type === 'tabs' ? (
                        <ProgramTabsSection
                          section={section}
                          sectionKey={sectionKey}
                          sectionIndex={index}
                          sectionTabs={sectionTabs}
                          activeTabIndex={activeTabIndex}
                          setActiveTabsBySection={setActiveTabsBySection}
                          bodyContent={renderInlineEditableField({
                            index,
                            field: 'body',
                            value: section.body || '',
                            mode: 'rich',
                            placeholder: 'Section introduction',
                            display: section.body
                              ? renderPositionableElement({
                                  section,
                                  index,
                                  elementKey: `${sectionKey}-tabs-body`,
                                  children: renderHtmlTextBlock(section.body, {
                                    color: 'text.secondary',
                                    lineHeight: 1.75,
                                    fontSize: { xs: '0.84rem', md: '0.9rem' },
                                  }),
                                })
                              : (
                                <Typography sx={{ color: 'text.secondary', fontSize: '0.84rem' }}>
                                  Double-click to add section introduction
                                </Typography>
                              ),
                          })}
                          renderHtmlTextBlock={renderHtmlTextBlock}
                          renderPositionableElement={renderPositionableElement}
                        />
                      ) : section.type === 'quote' ? (
                        <PrincipalMessageSection
                          section={section}
                          sectionKey={sectionKey}
                          sectionIndex={index}
                          mediaHeight={mediaHeight}
                          bodyContent={renderInlineEditableField({
                            index,
                            field: 'body',
                            value: section.body || '',
                            mode: 'rich',
                            placeholder: 'Message content',
                            display: section.body
                              ? renderPositionableElement({
                                  section,
                                  index,
                                  elementKey: `${sectionKey}-quote-body`,
                                  children: renderHtmlTextBlock(section.body, {
                                    color: 'text.secondary',
                                    lineHeight: 1.9,
                                    fontSize: { xs: '0.9rem', md: '0.98rem' },
                                    '& p': { mt: 0, mb: 0.8 },
                                  }),
                                })
                              : (
                                <Typography sx={{ color: 'text.secondary', fontSize: '0.84rem' }}>
                                  Double-click to add principal message
                                </Typography>
                              ),
                          })}
                          renderPositionableElement={renderPositionableElement}
                        />
                      ) : section.type === 'image_grid' ? (
                        <CurvedGallerySection
                          section={section}
                          sectionKey={sectionKey}
                          sectionIndex={index}
                          galleryImages={galleryImages}
                          galleryColumns={galleryColumns}
                          mediaHeight={mediaHeight}
                          bodyContent={renderInlineEditableField({
                            index,
                            field: 'body',
                            value: section.body || '',
                            mode: 'rich',
                            placeholder: 'Gallery introduction',
                            display: section.body
                              ? renderPositionableElement({
                                  section,
                                  index,
                                  elementKey: `${sectionKey}-gallery-body`,
                                  children: renderHtmlTextBlock(section.body, {
                                    color: 'text.secondary',
                                    lineHeight: 1.75,
                                    fontSize: { xs: '0.84rem', md: '0.9rem' },
                                  }),
                                })
                              : (
                                <Typography sx={{ color: 'text.secondary', fontSize: '0.84rem' }}>
                                  Double-click to add gallery introduction
                                </Typography>
                              ),
                          })}
                          renderPositionableElement={renderPositionableElement}
                        />
                      ) : section.type === 'map' ? (
                        <MapVisitSection
                          section={section}
                          sectionKey={sectionKey}
                          sectionIndex={index}
                          mediaHeight={mediaHeight}
                          openActionPath={openActionPath}
                          bodyContent={renderInlineEditableField({
                            index,
                            field: 'body',
                            value: section.body || '',
                            mode: 'rich',
                            placeholder: 'Visit information',
                            display: section.body
                              ? renderPositionableElement({
                                  section,
                                  index,
                                  elementKey: `${sectionKey}-map-body`,
                                  children: renderHtmlTextBlock(section.body, {
                                    color: 'text.secondary',
                                    lineHeight: 1.78,
                                    fontSize: { xs: '0.84rem', md: '0.9rem' },
                                  }),
                                })
                              : (
                                <Typography sx={{ color: 'text.secondary', fontSize: '0.84rem' }}>
                                  Double-click to add visit information
                                </Typography>
                              ),
                          })}
                          renderPositionableElement={renderPositionableElement}
                        />
                      ) : section.type === 'contact_form' || section.type === 'admissions_form' ? (
                        <InquiryFormSection
                          section={section}
                          sectionKey={sectionKey}
                          sectionIndex={index}
                          showSnackbar={showSnackbar}
                          bodyContent={renderInlineEditableField({
                            index,
                            field: 'body',
                            value: section.body || '',
                            mode: 'rich',
                            placeholder: 'Form introduction',
                            display: section.body
                              ? renderPositionableElement({
                                  section,
                                  index,
                                  elementKey: `${sectionKey}-form-body`,
                                  children: renderHtmlTextBlock(section.body, {
                                    color: 'rgba(255,255,255,0.86)',
                                    lineHeight: 1.8,
                                    fontSize: { xs: '0.86rem', md: '0.92rem' },
                                  }),
                                })
                              : (
                                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.84rem' }}>
                                  Double-click to add form introduction
                                </Typography>
                              ),
                          })}
                          renderPositionableElement={renderPositionableElement}
                        />
                      ) : section.type === 'footer' ? (
                        <FooterShowcaseSection
                          section={section}
                          sectionKey={sectionKey}
                          footerLinks={footerLinks}
                          openActionPath={openActionPath}
                          bodyContent={renderInlineEditableField({
                            index,
                            field: 'body',
                            value: section.body || '',
                            mode: 'rich',
                            placeholder: 'Footer introduction',
                            display: section.body
                              ? renderPositionableElement({
                                  section,
                                  index,
                                  elementKey: `${sectionKey}-footer-body`,
                                  children: renderHtmlTextBlock(section.body, {
                                    color: 'rgba(255,255,255,0.8)',
                                    lineHeight: 1.75,
                                    fontSize: { xs: '0.84rem', md: '0.9rem' },
                                  }),
                                })
                              : (
                                <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.84rem' }}>
                                  Double-click to add footer introduction
                                </Typography>
                              ),
                          })}
                        />
                      ) : null}
                    </Box>
                  ) : (
                    <Grid container spacing={3} alignItems="center">
                      {!isMediaOnly && (
                        <Grid item xs={12} md={textSpan} sx={{ order: { xs: 1, md: imageOnLeft ? 2 : 1 } }}>
                          {shouldRenderSharedSectionTitle ? (
                            <Box
                              sx={{
                                mb: 0.7,
                                display: 'flex',
                                justifyContent:
                                  sectionTitleAlign === 'center'
                                    ? 'center'
                                    : sectionTitleAlign === 'right'
                                      ? 'flex-end'
                                      : 'flex-start',
                              }}
                            >
                              {renderInlineEditableField({
                                index,
                                field: 'title',
                                value: section.title || '',
                                mode: 'text',
                                placeholder: 'Section title',
                                display: (
                                  renderPositionableElement({
                                    section,
                                    index,
                                    elementKey: `section-title-${sectionKey}`,
                                    showHandle: true,
                                    children: (
                                      <Typography
                                        sx={{
                                          fontWeight: 700,
                                          fontSize: {
                                            xs: `${Math.max(0.72, (sectionTitleFontSize - 5) / 16)}rem`,
                                            md: `${sectionTitleFontSize / 16}rem`,
                                          },
                                          color: sectionTitleColor,
                                          textAlign: sectionTitleAlign,
                                          px: sectionTitleBgEnabled ? 0.9 : 0,
                                          py: sectionTitleBgEnabled ? 0.4 : 0,
                                          borderRadius: sectionTitleBgEnabled ? 1 : 0,
                                          backgroundColor: sectionTitleBgEnabled ? sectionTitleBgColor : 'transparent',
                                          width: sectionTitleAlign === 'left' ? 'auto' : 'fit-content',
                                        }}
                                      >
                                        {section.title}
                                      </Typography>
                                    ),
                                  })
                                ),
                              })}
                            </Box>
                          ) : null}

                          {(section.type === 'table' || section.type === 'results') ? (
                            <Paper
                              elevation={0}
                              sx={{ mt: 1, borderRadius: 1.6, border: '1px solid rgba(15, 23, 42, 0.14)' }}
                            >
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    {tableColumns.map((column) => (
                                      <TableCell
                                        key={`${sectionKey}-column-${column}`}
                                        sx={{
                                          fontWeight: 700,
                                          fontSize: '0.8rem',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {column}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {tableRows.map((row, rowIndex) => (
                                    <TableRow key={`${sectionKey}-row-${rowIndex}`}>
                                      {row.map((cell, cellIndex) => (
                                        <TableCell key={`${sectionKey}-row-${rowIndex}-cell-${cellIndex}`} sx={{ fontSize: '0.8rem' }}>
                                          {cell}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Paper>
                          ) : section.type === 'stages' && !(isAdmin && editMode) && !looksLikeHtml(section.body || '') ? (
                            <Stack spacing={0.8} sx={{ mt: 1 }}>
                              {listItems.map((item, itemIndex) => (
                                <Box
                                  key={`${sectionKey}-stage-${itemIndex}`}
                                  sx={{
                                    px: 1.1,
                                    py: 0.8,
                                    borderRadius: 1.2,
                                    background: 'linear-gradient(180deg, rgba(14,165,233,0.08) 0%, rgba(0,195,137,0.06) 100%)',
                                  }}
                                >
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'text.primary' }}>
                                    Stage {itemIndex + 1}
                                  </Typography>
                                  <Typography sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.8rem' }}>
                                    {item}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          ) : section.type === 'slider' ? (
                            <SliderSection
                              section={section}
                              sectionKey={sectionKey}
                              sectionIndex={index}
                              sliderImages={sliderImages}
                              sliderCurrentIndex={sliderCurrentIndex}
                              mediaHeight={mediaHeight}
                              setActiveSliderBySection={setActiveSliderBySection}
                              renderHtmlTextBlock={renderHtmlTextBlock}
                              renderPositionableElement={renderPositionableElement}
                            />
                          ) : section.type === 'overlay_banner' ? (
                            <OverlayBannerSection
                              section={section}
                              sectionIndex={index}
                              renderHtmlTextBlock={renderHtmlTextBlock}
                              renderPositionableElement={renderPositionableElement}
                            />
                          ) : section.type === 'experience_story' ? (
                            <ExperienceStorySection
                              section={section}
                              sectionKey={sectionKey}
                              sectionIndex={index}
                              sectionTabs={sectionTabs}
                              activeTabIndex={activeTabIndex}
                              setActiveTabsBySection={setActiveTabsBySection}
                              iconCards={iconCards}
                              renderHtmlTextBlock={renderHtmlTextBlock}
                              renderPositionableElement={renderPositionableElement}
                            />
                          ) : section.type === 'icon_grid' ? (
                            <IconGridSection
                              section={section}
                              sectionKey={sectionKey}
                              sectionIndex={index}
                              iconCards={iconCards}
                              iconGridColumns={iconGridColumns}
                              renderHtmlTextBlock={renderHtmlTextBlock}
                              renderPositionableElement={renderPositionableElement}
                            />
                          ) : section.type === 'stats_strip' ? (
                            <StatsStripSection
                              section={section}
                              sectionKey={sectionKey}
                              sectionIndex={index}
                              statsItems={statsItems}
                              renderPositionableElement={renderPositionableElement}
                            />
                          ) : section.type === 'sidebar_content' ? (
                            <SidebarContentSection
                              section={section}
                              sectionKey={sectionKey}
                              sectionIndex={index}
                              sidebarItems={sidebarItems}
                              sidebarButtons={sidebarButtons}
                              renderHtmlTextBlock={renderHtmlTextBlock}
                              renderPositionableElement={renderPositionableElement}
                            />
                          ) : section.type === 'news_highlights' ? (
                            <NewsHighlightsSection
                              section={section}
                              sectionKey={sectionKey}
                              sectionIndex={index}
                              profiles={profileCards}
                              columns={profileColumns}
                              profileImageObjectPosition={profileImageObjectPosition}
                              renderHtmlTextBlock={renderHtmlTextBlock}
                              renderPositionableElement={renderPositionableElement}
                            />
                          ) : section.type === 'profile_showcase' ? (
                            <ProfileCardsSection
                              section={section}
                              sectionKey={sectionKey}
                              sectionIndex={index}
                              profiles={profileCards}
                              columns={profileColumns}
                              profileKind="Profile"
                              emptyMessage="Add profiles in editor to show cards."
                              variant="showcase"
                              profileImageAlign={profileImageAlign}
                              profileImageSize={profileImageSize}
                              profileImageRadius={profileImageRadius}
                              profileImageObjectPosition={profileImageObjectPosition}
                              renderHtmlTextBlock={renderHtmlTextBlock}
                              renderPositionableElement={renderPositionableElement}
                            />
                          ) : section.type === 'careers_openings' ? (
                            <CareerOpeningsSection
                              section={section}
                              sectionKey={sectionKey}
                              careerCards={careerCards}
                              careersLayout={careersLayout}
                              careersColumns={careersColumns}
                              slug={slug}
                              isAdmin={isAdmin}
                              editMode={editMode}
                              openActionPath={openActionPath}
                              onApply={openCareerApplyDialog}
                              renderHtmlTextBlock={renderHtmlTextBlock}
                            />
                          ) : section.type === 'social_links' ? (
                            <SocialLinksSection
                              section={section}
                              sectionKey={sectionKey}
                              sectionIndex={index}
                              socialLinks={socialLinks}
                              isAdmin={isAdmin}
                              editMode={editMode}
                              openActionPath={openActionPath}
                              renderHtmlTextBlock={renderHtmlTextBlock}
                              renderPositionableElement={renderPositionableElement}
                            />
                          ) : section.type === 'subscribe' ? (
                            <SubscribeSection
                              section={section}
                              sectionIndex={index}
                              subscribePlaceholder={subscribePlaceholder}
                              subscribeButtonLabel={subscribeButtonLabel}
                              subscribeSuccessMessage={subscribeSuccessMessage}
                              showSnackbar={showSnackbar}
                              renderHtmlTextBlock={renderHtmlTextBlock}
                              renderPositionableElement={renderPositionableElement}
                            />
                          ) : section.type === 'student_results_grid' ? (
                            <StudentResultsGridSection
                              section={section}
                              sectionKey={sectionKey}
                              sectionIndex={index}
                              resultCards={resultCards}
                              resultsGridColumns={resultsGridColumns}
                              resultsMetricMode={resultsMetricMode}
                              profileImageAlign={profileImageAlign}
                              profileImageSize={profileImageSize}
                              profileImageRadius={profileImageRadius}
                              profileImageObjectPosition={profileImageObjectPosition}
                              renderHtmlTextBlock={renderHtmlTextBlock}
                              renderPositionableElement={renderPositionableElement}
                            />
                          ) : section.type === 'faculty' ? (
                            <ProfileCardsSection
                              section={section}
                              sectionKey={sectionKey}
                              sectionIndex={index}
                              profiles={facultyCards}
                              columns={facultyColumns}
                              profileKind="Faculty"
                              emptyMessage="Add faculty rows in editor to show profiles."
                              profileImageAlign={profileImageAlign}
                              profileImageSize={profileImageSize}
                              profileImageRadius={profileImageRadius}
                              profileImageObjectPosition={profileImageObjectPosition}
                              renderHtmlTextBlock={renderHtmlTextBlock}
                              renderPositionableElement={renderPositionableElement}
                            />
                          ) : section.type === 'founders' ? (
                            <ProfileCardsSection
                              section={section}
                              sectionKey={sectionKey}
                              sectionIndex={index}
                              profiles={founderCards}
                              columns={founderColumns}
                              profileKind="Founder"
                              emptyMessage="Add founder rows in editor to show profiles."
                              profileImageAlign={profileImageAlign}
                              profileImageSize={profileImageSize}
                              profileImageRadius={profileImageRadius}
                              profileImageObjectPosition={profileImageObjectPosition}
                              renderHtmlTextBlock={renderHtmlTextBlock}
                              renderPositionableElement={renderPositionableElement}
                            />
                          ) : section.type === 'image_grid' ? (
                            <Stack spacing={1} sx={{ mt: 1 }}>
                              {renderInlineEditableField({
                                index,
                                field: 'body',
                                value: section.body || '',
                                mode: 'rich',
                                placeholder: 'Gallery intro',
                                display: section.body
                                  ? renderHtmlTextBlock(section.body, {
                                      color: 'text.secondary',
                                      lineHeight: 1.65,
                                      fontSize: { xs: '0.8rem', md: '0.86rem' },
                                    })
                                  : <Typography sx={{ color: 'text.secondary', fontSize: '0.84rem' }}>Double-click to add gallery intro</Typography>,
                              })}
                              {galleryImages.length ? (
                                <Box
                                  sx={{
                                    display: 'grid',
                                    gap: 1,
                                    gridTemplateColumns: {
                                      xs: 'repeat(2, minmax(0, 1fr))',
                                      md: `repeat(${galleryColumns}, minmax(0, 1fr))`,
                                    },
                                  }}
                                >
                                  {galleryImages.map((imageUrl, imageIndex) => (
                                    <Box
                                      key={`${sectionKey}-gallery-${imageIndex}`}
                                      component="img"
                                      src={imageUrl}
                                      alt={`${section.title || 'Gallery'} ${imageIndex + 1}`}
                                      sx={{
                                        width: '100%',
                                        height: { xs: 120, md: 150 },
                                        objectFit: 'cover',
                                        borderRadius: 1.2,
                                        border: '1px solid rgba(15,23,42,0.08)',
                                        display: 'block',
                                      }}
                                    />
                                  ))}
                                </Box>
                              ) : (
                                <Typography color="text.secondary" sx={{ fontSize: '0.82rem' }}>
                                  Add image URLs in editor to build your gallery.
                                </Typography>
                              )}
                            </Stack>
                          ) : section.type === 'map' ? (
                            <Stack spacing={1} sx={{ mt: 1 }}>
                              {renderInlineEditableField({
                                index,
                                field: 'body',
                                value: section.body || '',
                                mode: 'rich',
                                placeholder: 'Map description',
                                display: section.body
                                  ? renderHtmlTextBlock(section.body, {
                                      color: 'text.secondary',
                                      lineHeight: 1.65,
                                      fontSize: { xs: '0.8rem', md: '0.86rem' },
                                    })
                                  : <Typography sx={{ color: 'text.secondary', fontSize: '0.84rem' }}>Double-click to add map description</Typography>,
                              })}
                              <Box
                                component="iframe"
                                src={
                                  section.mapEmbedUrl || 'https://www.google.com/maps?q=Skhool%20Demo%20School%20Bengaluru&output=embed'
                                }
                                title={`${section.title || 'Map'} Location`}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                sx={{
                                  width: '100%',
                                  height: { xs: 260, md: 320 },
                                  border: `1px solid ${DEMO_THEME.border}`,
                                  borderRadius: 1.2,
                                }}
                              />
                              {section.mapAddress ? (
                                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                  {section.mapAddress}
                                </Typography>
                              ) : null}
                            </Stack>
                          ) : section.type === 'contact_form' || section.type === 'admissions_form' ? (
                            <Stack spacing={1.1} sx={{ mt: 1 }}>
                              {renderInlineEditableField({
                                index,
                                field: 'body',
                                value: section.body || '',
                                mode: 'rich',
                                placeholder: 'Form introduction',
                                display: section.body
                                  ? renderHtmlTextBlock(section.body, {
                                      color: 'text.secondary',
                                      lineHeight: 1.65,
                                      fontSize: { xs: '0.8rem', md: '0.86rem' },
                                    })
                                  : <Typography sx={{ color: 'text.secondary', fontSize: '0.84rem' }}>Double-click to add form introduction</Typography>,
                              })}
                              <Box
                                component="form"
                                onSubmit={(event) => {
                                  event.preventDefault();
                                  showSnackbar(
                                    section.formSuccessMessage || 'Form submitted successfully.',
                                    'success'
                                  );
                                  event.currentTarget.reset();
                                }}
                              >
                                <Grid container spacing={1}>
                                  {(section.type === 'admissions_form'
                                    ? [
                                        { name: 'studentName', label: 'Student Name', type: 'text' },
                                        { name: 'grade', label: 'Class Applying For', type: 'text' },
                                        { name: 'parentName', label: 'Parent Name', type: 'text' },
                                        { name: 'phone', label: 'Phone', type: 'tel' },
                                        { name: 'email', label: 'Email', type: 'email' },
                                      ]
                                    : [
                                        { name: 'name', label: 'Name', type: 'text' },
                                        { name: 'phone', label: 'Phone', type: 'tel' },
                                        { name: 'email', label: 'Email', type: 'email' },
                                      ]
                                  ).map((field) => (
                                    <Grid item xs={12} md={6} key={`${sectionKey}-field-${field.name}`}>
                                      <TextField
                                        size="small"
                                        name={field.name}
                                        label={field.label}
                                        type={field.type}
                                        fullWidth
                                        required
                                      />
                                    </Grid>
                                  ))}
                                  <Grid item xs={12}>
                                    <TextField
                                      size="small"
                                      name="message"
                                      label="Message"
                                      fullWidth
                                      required
                                      multiline
                                      minRows={4}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Button type="submit" variant="contained" size="small">
                                      {section.formSubmitLabel || 'Submit'}
                                    </Button>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Stack>
                          ) : section.type === 'footer' ? (
                            <Stack spacing={1} sx={{ mt: 0.4 }}>
                              {renderInlineEditableField({
                                index,
                                field: 'title',
                                value: section.title || '',
                                mode: 'text',
                                placeholder: 'Footer title',
                                display: <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{section.title || 'Footer'}</Typography>,
                              })}
                              {renderInlineEditableField({
                                index,
                                field: 'body',
                                value: section.body || '',
                                mode: 'rich',
                                placeholder: 'Footer description',
                                display: section.body ? (
                                  renderHtmlTextBlock(section.body, {
                                    color: 'text.secondary',
                                    fontSize: '0.82rem',
                                    lineHeight: 1.65,
                                    '& p': { mt: 0, mb: 0.4 },
                                  })
                                ) : <Typography sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>Double-click to add footer description</Typography>,
                              })}
                              <Grid container spacing={1}>
                                <Grid item xs={12} md={5}>
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>Address</Typography>
                                  <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                    {section.footerAddress || 'Add address in editor'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>Contact</Typography>
                                  <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                    {section.footerPhone || '-'}
                                  </Typography>
                                  <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                    {section.footerEmail || '-'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>Quick Links</Typography>
                                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                    {footerLinks.length ? (
                                      footerLinks.map((path, linkIndex) => (
                                        <Button
                                          key={`${sectionKey}-footer-link-${linkIndex}`}
                                          size="small"
                                          variant="text"
                                          sx={{ justifyContent: 'flex-start', px: 0 }}
                                          onClick={() => openActionPath(path)}
                                        >
                                          {path}
                                        </Button>
                                      ))
                                    ) : (
                                      <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                        Add links in editor
                                      </Typography>
                                    )}
                                  </Stack>
                                </Grid>
                              </Grid>
                              {section.footerCopyright ? (
                                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', pt: 0.6 }}>
                                  {section.footerCopyright}
                                </Typography>
                              ) : null}
                            </Stack>
                          ) : section.type === 'custom_combo' ? (
                            <Stack spacing={1} sx={{ mt: 1 }}>
                              {comboShowImage && section.image ? (
                                <Box
                                  component="img"
                                  src={section.image}
                                  alt={section.title || 'Custom Combo'}
                                  sx={{
                                    width: '100%',
                                    height: { xs: `${Math.max(160, mediaHeight - 40)}px`, md: `${mediaHeight}px` },
                                    objectFit: 'cover',
                                    borderRadius: 1.2,
                                    border: '1px solid rgba(15,23,42,0.08)',
                                  }}
                                />
                              ) : null}
                              {renderInlineEditableField({
                                index,
                                field: 'body',
                                value: section.body || '',
                                mode: 'rich',
                                placeholder: 'Section content',
                                display: section.body
                                  ? renderHtmlTextBlock(section.body, {
                                      color: 'text.secondary',
                                      lineHeight: 1.65,
                                      fontSize: { xs: '0.8rem', md: '0.86rem' },
                                    })
                                  : <Typography sx={{ color: 'text.secondary', fontSize: '0.84rem' }}>Double-click to add section content</Typography>,
                              })}
                              {comboShowTable ? (
                                <Paper
                                  elevation={0}
                                  sx={{
                                    borderRadius: 1.2,
                                    border: '1px solid rgba(15, 23, 42, 0.12)',
                                    background: '#FFFFFF',
                                  }}
                                >
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        {tableColumns.map((column) => (
                                          <TableCell key={`${sectionKey}-combo-column-${column}`} sx={{ fontWeight: 700 }}>
                                            {column}
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {tableRows.map((row, rowIndex) => (
                                        <TableRow key={`${sectionKey}-combo-row-${rowIndex}`}>
                                          {row.map((cell, cellIndex) => (
                                            <TableCell key={`${sectionKey}-combo-${rowIndex}-${cellIndex}`}>
                                              {cell}
                                            </TableCell>
                                          ))}
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Paper>
                              ) : null}
                              {comboShowButton ? (
                                <Button
                                  size="small"
                                  variant={buttonVariant}
                                  sx={{
                                    width: 'fit-content',
                                    ...(buttonVariant === 'contained'
                                      ? {
                                          backgroundColor: buttonBgColor,
                                          color: buttonTextColor,
                                          '&:hover': { backgroundColor: buttonBgColor, opacity: 0.9 },
                                        }
                                      : {}),
                                    ...(buttonVariant === 'outlined'
                                      ? {
                                          borderColor: buttonBgColor,
                                          color: buttonTextColor,
                                        }
                                      : {}),
                                    ...(buttonVariant === 'text' ? { color: buttonTextColor } : {}),
                                  }}
                                  disabled={!buttonEnabled || !section.buttonPath}
                                  onClick={() => {
                                    if (!(isAdmin && editMode) && buttonEnabled) {
                                      openActionPath(section.buttonPath);
                                    }
                                  }}
                                >
                                  {section.buttonLabel || 'Open'}
                                </Button>
                              ) : null}
                            </Stack>
                          ) : section.type === 'image_read_more' ? (
                            <Stack spacing={0.8} sx={{ mt: 1 }}>
                              {renderInlineEditableField({
                                index,
                                field: 'body',
                                value: section.body || '',
                                mode: 'rich',
                                placeholder: 'Read more content',
                                display: readMoreDisplay
                                  ? renderHtmlTextBlock(readMoreDisplay, {
                                      color: 'text.secondary',
                                      lineHeight: 1.7,
                                      fontSize: { xs: '0.8rem', md: '0.86rem' },
                                    })
                                  : <Typography sx={{ color: 'text.secondary', fontSize: '0.84rem' }}>Double-click to add content</Typography>,
                              })}
                              {readMoreCanCollapse ? (
                                <Button
                                  size="small"
                                  variant="text"
                                  sx={{ width: 'fit-content', px: 0 }}
                                  onClick={() =>
                                    setExpandedReadMoreBySection((prev) => ({
                                      ...prev,
                                      [sectionKey]: !Boolean(prev[sectionKey]),
                                    }))
                                  }
                                >
                                  {readMoreExpanded ? readLessLabel : readMoreLabel}
                                </Button>
                              ) : null}
                            </Stack>
                          ) : section.type === 'dropdown' ? (
                            <Stack spacing={1.1} sx={{ mt: 1 }}>
                              {renderInlineEditableField({
                                index,
                                field: 'body',
                                value: section.body || '',
                                mode: 'rich',
                                placeholder: 'Dropdown help text',
                                display: section.body
                                  ? renderHtmlTextBlock(section.body, {
                                      color: 'text.secondary',
                                      lineHeight: 1.7,
                                      fontSize: { xs: '0.8rem', md: '0.86rem' },
                                    })
                                  : <Typography sx={{ color: 'text.secondary', fontSize: '0.84rem' }}>Double-click to add dropdown help text</Typography>,
                              })}
                              <FormControl size="small" fullWidth>
                                <InputLabel>{dropdownPlaceholder}</InputLabel>
                                <Select
                                  label={dropdownPlaceholder}
                                  value={selectedDropdown}
                                  onChange={(event) =>
                                    setActiveDropdownBySection((prev) => ({
                                      ...prev,
                                      [sectionKey]: event.target.value,
                                    }))
                                  }
                                >
                                  {dropdownOptions.map((optionItem, optionIndex) => (
                                    <MenuItem key={`${sectionKey}-option-${optionIndex}`} value={optionItem}>
                                      {optionItem}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Stack>
                          ) : section.type === 'list' && !(isAdmin && editMode) && !looksLikeHtml(section.body || '') ? (
                            <Stack spacing={0.85} sx={{ mt: 1 }}>
                              {listItems.map((item, itemIndex) => (
                                <Box key={`${sectionKey}-list-${itemIndex}`} sx={{ display: 'flex', gap: 1 }}>
                                  <Typography sx={{ fontWeight: 700, color: 'primary.main', minWidth: 18 }}>
                                    {itemIndex + 1}.
                                  </Typography>
                                  <Typography sx={{ color: 'text.secondary', lineHeight: 1.65 }}>{item}</Typography>
                                </Box>
                              ))}
                            </Stack>
                          ) : section.type !== 'tabs' ? (
                            renderInlineEditableField({
                              index,
                              field: 'body',
                              value: section.body || '',
                              mode: 'rich',
                              placeholder: 'Section content',
                              display: section.body
                                ? renderPositionableElement({
                                    section,
                                    index,
                                    elementKey: `section-body-${sectionKey}`,
                                    showHandle: true,
                                    children: renderHtmlTextBlock(section.body, {
                                      color: section.type === 'cta' ? 'text.primary' : 'text.secondary',
                                      lineHeight: 1.7,
                                      fontSize: { xs: '0.8rem', md: '0.86rem' },
                                      fontStyle: section.type === 'quote' ? 'italic' : 'normal',
                                    }),
                                  })
                                : (
                                  <Typography sx={{ color: 'text.secondary', fontSize: '0.84rem' }}>
                                    Double-click to add section content
                                  </Typography>
                                ),
                            })
                          ) : null}

                          {section.type === 'tabs' && (
                            <Box sx={{ mt: 1.2 }}>
                              <Tabs
                                value={activeTabIndex}
                                onChange={(_event, value) =>
                                  setActiveTabsBySection((prev) => ({ ...prev, [sectionKey]: value }))
                                }
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, textTransform: 'none' } }}
                              >
                                {sectionTabs.map((tabItem, tabIndex) => (
                                  <Tab key={`${sectionKey}-tab-${tabIndex}`} label={tabItem.title} />
                                ))}
                              </Tabs>
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 1.4,
                                  mt: 0.9,
                                  borderRadius: 2,
                                  border: 'none',
                                  background: 'rgba(255,255,255,0.74)',
                                  boxShadow: 'none',
                                }}
                              >
                                {looksLikeHtml(sectionTabs[activeTabIndex]?.content || '') ? (
                                  <Box
                                    sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                                    dangerouslySetInnerHTML={{
                                      __html: sanitizeUserHtml(sectionTabs[activeTabIndex]?.content || ''),
                                    }}
                                  />
                                ) : (
                                  <Typography sx={{ whiteSpace: 'pre-line', color: 'text.secondary', lineHeight: 1.7 }}>
                                    {sectionTabs[activeTabIndex]?.content || ''}
                                  </Typography>
                                )}
                              </Paper>
                            </Box>
                          )}

                          {section.type !== 'custom_combo' && (buttonEnabled || (isAdmin && editMode)) && (
                            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.1 }}>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                  size="small"
                                  variant={buttonVariant}
                                  sx={{
                                    ...(buttonVariant === 'contained'
                                      ? {
                                          backgroundColor: buttonBgColor,
                                          color: buttonTextColor,
                                          '&:hover': { backgroundColor: buttonBgColor, opacity: 0.9 },
                                        }
                                      : {}),
                                    ...(buttonVariant === 'outlined'
                                      ? {
                                          borderColor: buttonBgColor,
                                          color: buttonTextColor,
                                        }
                                      : {}),
                                    ...(buttonVariant === 'text' ? { color: buttonTextColor } : {}),
                                  }}
                                  onClick={() => {
                                    if (!(isAdmin && editMode) && buttonEnabled) {
                                      const resolvedPath =
                                        section.type === 'dropdown'
                                          ? String(section.buttonPath || '').replace(
                                              /\{value\}/g,
                                              encodeURIComponent(selectedDropdown)
                                            )
                                          : section.buttonPath;
                                      openActionPath(resolvedPath);
                                    }
                                  }}
                                  disabled={
                                    !buttonEnabled ||
                                    !section.buttonPath ||
                                    (section.type === 'dropdown' &&
                                      String(section.buttonPath || '').includes('{value}') &&
                                      !selectedDropdown)
                                  }
                                >
                                  {section.buttonLabel || 'Open'}
                                </Button>
                              </Box>
                            </Box>
                          )}
                        </Grid>
                      )}

                      {hasMedia && (
                        <Grid
                          item
                          xs={12}
                          md={isMediaOnly ? 12 : imageSpan}
                          sx={{ order: { xs: 2, md: imageOnLeft && !isMediaOnly ? 1 : 2 } }}
                        >
                          <Box
                            sx={{
                              position: 'relative',
                              width: '100%',
                              overflow: 'hidden',
                              borderRadius:
                                imageFrameShape === 'rounded'
                                  ? `${imageCornerRadius}px`
                                  : imageFrameShape === 'rectangle'
                                    ? 0
                                    : undefined,
                              boxShadow: 'none',
                              border:
                                mediaBorderWidth > 0
                                  ? `${mediaBorderWidth}px solid ${
                                      section.imageBorderColor || 'rgba(15, 23, 42, 0.2)'
                                    }`
                                  : 'none',
                              boxSizing: 'border-box',
                            }}
                          >
                            {sectionVideo ? (
                              isSectionVideoDirect ? (
                                <Box
                                  component="video"
                                  src={sectionVideo}
                                  controls
                                  sx={{
                                    width: '100%',
                                    height: { xs: `${Math.max(160, mediaHeight - 40)}px`, md: `${mediaHeight}px` },
                                    objectFit: 'cover',
                                    objectPosition: section.imageObjectPosition || 'center center',
                                    display: 'block',
                                    background: '#000',
                                  }}
                                />
                              ) : (
                                <Box
                                  component="iframe"
                                  src={sectionVideo}
                                  title={`${section.title || 'Video'} ${index + 1}`}
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  sx={{
                                    width: '100%',
                                    height: { xs: `${Math.max(160, mediaHeight - 40)}px`, md: `${mediaHeight}px` },
                                    border: 0,
                                    display: 'block',
                                  }}
                                />
                              )
                            ) : (
                              <Box
                                component="img"
                                src={section.image}
                                alt={section.title}
                                sx={{
                                  width: '100%',
                                  height: { xs: `${Math.max(160, mediaHeight - 40)}px`, md: `${mediaHeight}px` },
                                  objectFit: 'cover',
                                  objectPosition: section.imageObjectPosition || 'center center',
                                  display: 'block',
                                }}
                              />
                            )}
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Paper>
              </Box>
            </Container>
          </Box>
        );
        })}
      </Box>

      {isAdmin && portalReady && createPortal(
        <>
          {editMode && editModeNoticeVisible && (
            <Box
              sx={{
                position: 'fixed',
                top: { xs: 82, md: 96 },
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1500,
                pointerEvents: 'none',
                animation: 'editModeBannerIn 320ms ease, editModeBannerOut 320ms ease 2.05s forwards',
                '@keyframes editModeBannerIn': {
                  '0%': { opacity: 0, transform: 'translateX(-50%) translateY(-14px)' },
                  '100%': { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
                },
                '@keyframes editModeBannerOut': {
                  '0%': { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
                  '100%': { opacity: 0, transform: 'translateX(-50%) translateY(-10px)' },
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  px: 1.5,
                  py: 1.1,
                  borderRadius: 0,
                  border: '1px solid rgba(15, 23, 42, 0.12)',
                  background: 'rgba(255,255,255,0.98)',
                  boxShadow: '0 20px 44px rgba(15, 23, 42, 0.18)',
                  minWidth: { xs: 260, md: 320 },
                }}
              >
                <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.3 }}>
                  Edit Mode Enabled
                </Typography>
                <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: 'text.primary' }}>
                  You can edit this page now.
                </Typography>
              </Paper>
            </Box>
          )}

          {!editMode && (
            <Box
              sx={{
                position: 'fixed',
                right: { xs: 10, md: 18 },
                bottom: { xs: 12, md: 18 },
                zIndex: 1300,
                maxWidth: 'calc(100% - 20px)',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  px: 1.1,
                  py: 1,
                  borderRadius: 0,
                  border: '1px solid rgba(15, 23, 42, 0.12)',
                  background: 'rgba(255,255,255,0.97)',
                  backdropFilter: 'blur(12px)',
                  boxShadow: '0 18px 40px rgba(15, 23, 42, 0.18)',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'text.secondary' }}>
                      Website Builder
                    </Typography>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: 'text.primary' }}>
                      Customize this page
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => setEditMode(true)}
                    size="medium"
                    sx={{
                      px: 1.7,
                      py: 0.9,
                      borderRadius: 0,
                      fontWeight: 780,
                      boxShadow: 'none',
                    }}
                  >
                    Edit
                  </Button>
                </Stack>
              </Paper>
            </Box>
          )}

          {editMode && (
            <>
              {!toolsMinimized ? (
                <Paper
                  elevation={0}
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: 336,
                    minHeight: '100vh',
                    zIndex: 1400,
                    flexDirection: 'column',
                    borderRadius: 0,
                    borderLeft: '1px solid rgba(15, 23, 42, 0.12)',
                    borderTop: 'none',
                    borderRight: 'none',
                    borderBottom: 'none',
                    background: 'linear-gradient(180deg, #edf2f7 0%, #f8fafc 100%)',
                    backdropFilter: 'none',
                    boxShadow: '-18px 0 38px rgba(15, 23, 42, 0.09)',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      px: 1.35,
                      py: 1.25,
                      minHeight: 94,
                      borderBottom: '1px solid rgba(15, 23, 42, 0.1)',
                      background: 'linear-gradient(180deg, #ffffff 0%, #f6f8fb 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.35 }}>
                        Editor Workspace
                      </Typography>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: 'text.primary' }}>
                        Custom Tools
                      </Typography>
                      <Typography sx={{ fontSize: '0.84rem', color: 'text.secondary', mt: 0.2 }}>
                        {(pageData?.sections || []).length} sections on this page
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setToolsMinimized(true)} sx={{ border: '1px solid rgba(15,23,42,0.12)', borderRadius: 0 }}>
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Stack spacing={1.1} sx={{ p: 1.25, overflowY: 'auto', flex: 1 }}>
                    <Button size="small" variant="contained" startIcon={<PaletteIcon />} onClick={() => setThemeDialogOpen(true)} fullWidth sx={{ borderRadius: 0, justifyContent: 'flex-start', py: 1 }}>
                      Open Theme Customizer
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setComponentDialogOpen(true)} fullWidth sx={{ borderRadius: 0, justifyContent: 'flex-start', py: 1 }}>
                      Open Component Library
                    </Button>

                    <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 0, background: '#FFFFFF' }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.7 }}>
                        Editing
                      </Typography>
                      <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
                        <Button size="small" variant="outlined" startIcon={<UndoIcon />} onClick={undoLastChange} disabled={!undoStack.length} sx={{ borderRadius: 0 }}>
                          Undo
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<RedoIcon />} onClick={redoLastChange} disabled={!redoStack.length} sx={{ borderRadius: 0 }}>
                          Redo
                        </Button>
                        <Button size="small" variant="outlined" color="secondary" startIcon={<RestartAltIcon />} onClick={resetToDefaultDesign} sx={{ borderRadius: 0 }}>
                          Reset
                        </Button>
                        <Button size="small" variant="outlined" color="warning" startIcon={<RestoreIcon />} onClick={rollbackUnsavedChanges} disabled={!hasUnsavedChanges} sx={{ borderRadius: 0 }}>
                          Roll Back
                        </Button>
                      </Stack>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 0, background: '#FFFFFF' }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.45 }}>
                        Status
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: hasUnsavedChanges ? '#9A3412' : 'text.secondary',
                          fontWeight: 700,
                          mb: 0.9,
                        }}
                      >
                        {hasUnsavedChanges ? 'Unsaved changes pending' : 'No pending changes'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.1 }}>
                        Ctrl/Cmd+Z for undo. Open the section editor from any block on the page.
                      </Typography>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={savePage}
                        disabled={saving || !hasUnsavedChanges}
                        fullWidth
                        sx={{ borderRadius: 0 }}
                      >
                        {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Page Changes'}
                      </Button>
                    </Paper>
                  </Stack>
                </Paper>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    position: 'fixed',
                    top: 96,
                    right: 12,
                    zIndex: 1400,
                    alignItems: 'center',
                    gap: 0.8,
                    px: 1,
                    py: 0.8,
                    borderRadius: 0,
                    border: '1px solid rgba(15, 23, 42, 0.12)',
                    background: 'rgba(255,255,255,0.96)',
                    boxShadow: '0 16px 34px rgba(15, 23, 42, 0.18)',
                  }}
                >
                  <IconButton size="small" onClick={() => setToolsMinimized(false)} sx={{ border: '1px solid rgba(15,23,42,0.12)', borderRadius: 0 }}>
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary' }}>
                    Custom Tools
                  </Typography>
                </Paper>
              )}

              <Box
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  position: 'fixed',
                  right: 10,
                  bottom: 12,
                  zIndex: 1300,
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 0.9,
                  maxWidth: 'calc(100% - 20px)',
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    width: 'min(100%, 320px)',
                    px: 1.15,
                    py: 1.05,
                    borderRadius: 0,
                    border: '1px solid rgba(15, 23, 42, 0.12)',
                    background: 'rgba(255,255,255,0.97)',
                    backdropFilter: 'blur(12px)',
                    boxShadow: '0 22px 44px rgba(15, 23, 42, 0.18)',
                  }}
                >
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Box>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'text.secondary' }}>
                          Page Customizer
                        </Typography>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: 'text.primary' }}>
                          {(pageData?.sections || []).length} sections on this page
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={0.8}>
                      <Button size="small" variant="contained" startIcon={<PaletteIcon />} onClick={() => setThemeDialogOpen(true)} sx={{ flex: 1, borderRadius: 0 }}>
                        Theme
                      </Button>
                      <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setComponentDialogOpen(true)} sx={{ flex: 1, borderRadius: 0 }}>
                        Components
                      </Button>
                    </Stack>

                    <Stack direction="row" spacing={0.7} flexWrap="wrap">
                      <Button size="small" variant="outlined" startIcon={<UndoIcon />} onClick={undoLastChange} disabled={!undoStack.length} sx={{ borderRadius: 0 }}>
                        Undo
                      </Button>
                      <Button size="small" variant="outlined" startIcon={<RedoIcon />} onClick={redoLastChange} disabled={!redoStack.length} sx={{ borderRadius: 0 }}>
                        Redo
                      </Button>
                      <Button size="small" variant="outlined" color="secondary" startIcon={<RestartAltIcon />} onClick={resetToDefaultDesign} sx={{ borderRadius: 0 }}>
                        Reset
                      </Button>
                      <Button size="small" variant="outlined" color="warning" startIcon={<RestoreIcon />} onClick={rollbackUnsavedChanges} disabled={!hasUnsavedChanges} sx={{ borderRadius: 0 }}>
                        Roll Back
                      </Button>
                    </Stack>

                    <Stack direction="row" spacing={0.8} alignItems="center" justifyContent="space-between">
                      <Typography
                        variant="caption"
                        sx={{
                          color: hasUnsavedChanges ? '#9A3412' : 'text.secondary',
                          fontWeight: 700,
                        }}
                      >
                        {(hasUnsavedChanges ? 'Unsaved changes pending' : 'No pending changes') + ' | Ctrl/Cmd+Z undo'}
                      </Typography>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={savePage}
                        disabled={saving || !hasUnsavedChanges}
                        sx={{ borderRadius: 0 }}
                      >
                        {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Changes'}
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              </Box>
            </>
          )}
        </>
      , document.body)}

      <ImageSelectorDialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        images={images}
        loading={loadingImages}
        onSelect={handleSelectImage}
        onUploadComplete={async () => {
          if (!effectiveSchoolId) {
            return;
          }
          const resp = await axiosPrivate.get('/api/images/all', { headers: { schoolId: effectiveSchoolId } });
          setImages(resp.data || []);
        }}
        subdomain={tenantKey || ''}
      />

      <CareerApplyDialog
        open={careerApplyOpen}
        onClose={closeCareerApplyDialog}
        onSubmit={handleCareerApplySubmit}
        submitting={careerApplySubmitting}
        selectedRole={selectedCareerRole}
        formData={careerApplyForm}
        onFieldChange={handleCareerApplyFieldChange}
      />

      <SectionEditorDialog
        open={sectionEditor.open}
        onClose={closeSectionEditor}
        onSave={saveSectionEditor}
        saving={saving}
        sectionDraft={sectionDraft}
        sectionDraftTabs={sectionDraftTabs}
        setSectionEditor={setSectionEditor}
        showSnackbar={showSnackbar}
        availablePageLinks={availablePageLinks}
        openImageDialogFor={openImageDialogFor}
        sectionLibrary={SECTION_LIBRARY}
        getSectionTemplate={getSectionTemplate}
        getSectionTypeOptions={getSectionTypeOptions}
        normalizeSection={normalizeSection}
        clampNumber={clampNumber}
        normalizeHexColor={normalizeHexColor}
        normalizeStringList={normalizeStringList}
        normalizeDropdownOptions={normalizeDropdownOptions}
        normalizeTableColumns={normalizeTableColumns}
        normalizeTableRows={normalizeTableRows}
        parseTableColumnsInput={parseTableColumnsInput}
        parseTableRowsInput={parseTableRowsInput}
        serializeTableRows={serializeTableRows}
        serializeIconCards={serializeIconCards}
        parseIconCardsInput={parseIconCardsInput}
        serializeCareerCards={serializeCareerCards}
        parseCareerCardsInput={parseCareerCardsInput}
        serializeSocialLinks={serializeSocialLinks}
        parseSocialLinksInput={parseSocialLinksInput}
        serializeResultCards={serializeResultCards}
        parseResultCardsInput={parseResultCardsInput}
        serializeProfileCards={serializeProfileCards}
        parseFacultyCardsInput={parseFacultyCardsInput}
        parseFounderCardsInput={parseFounderCardsInput}
        parseProfileCardsInput={parseProfileCardsInput}
        serializeStatsItems={serializeStatsItems}
        parseStatsItemsInput={parseStatsItemsInput}
        serializeSidebarItems={serializeSidebarItems}
        parseSidebarItemsInput={parseSidebarItemsInput}
        constants={sectionEditorDialogConstants}
      />

      <ThemeCustomizationDialog
        open={themeDialogOpen}
        onClose={() => setThemeDialogOpen(false)}
        presets={PAGE_THEME_PRESETS}
        onApplyTheme={applyThemePreset}
      />

      <WebsiteSectionsDialog
        open={componentDialogOpen}
        onClose={() => setComponentDialogOpen(false)}
        componentSearch={componentSearch}
        onSearchChange={setComponentSearch}
        componentLibrary={componentLibrary}
        onAddSection={(sectionType) => {
          addSection(sectionType);
          setComponentDialogOpen(false);
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CustomPageRenderer;
